import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import './SportsScheduleTable.css';
import './EventCancelled.css';
import EventModal from './EventModal';
import { objectsAPI, eventsAPI, holidaysAPI, usersAPI, scheduleAPI, referencesAPI } from '../services/api';
import { debugLog } from '../utils/debugLog';

const daysOfWeek = [
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
  'Воскресенье',
];
const daysOfWeekShort = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const monthsOfYear = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

// Релиз: вкладка «Расписание» только при REACT_APP_SHOW_SCHEDULE=true (для продолжения разработки)
const SHOW_SCHEDULE = process.env.REACT_APP_SHOW_SCHEDULE === 'true';

// Даты с бэкенда хранятся и отдаются в UTC. Строка без Z парсится в JS как локальное время — приводим к UTC ISO.
function ensureUTCISO(val) {
  if (val == null || typeof val !== 'string') return val;
  const s = val.trim();
  if (!s || s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s)) return s;
  return s + (s.includes('.') ? 'Z' : '.000Z');
}

// Преобразование данных из API (snake_case) в формат компонента (camelCase)
const normalizeEvent = (event) => {
  const start = ensureUTCISO(event.start_date || event.startDate);
  const end = ensureUTCISO(event.end_date || event.endDate);
  let objectDates = event.objectDates && typeof event.objectDates === 'object' ? event.objectDates : {};
  if (Object.keys(objectDates).length > 0) {
    objectDates = Object.fromEntries(
      Object.entries(objectDates).map(([oid, d]) => [
        oid,
        { startDate: ensureUTCISO(d?.startDate), endDate: ensureUTCISO(d?.endDate) }
      ])
    );
  }
  return {
    id: event.id,
    name: event.name,
    startDate: start,
    endDate: end,
    description: event.description || '',
    cloudUrl: event.cloud_url ?? event.cloudUrl ?? null,
    status: event.status || (event.approved === true ? 'approved' : 'not_approved'),
    objectIds: Array.isArray(event.objectIds) ? event.objectIds : [],
    objectDates,
    organizationId: event.organization_id ?? event.organizationId ?? null,
    sportTypeId: event.sport_type_id ?? event.sportTypeId ?? null,
    organizationName: event.organization_name ?? event.organizationName ?? null,
    sportTypeName: event.sport_type_name ?? event.sportTypeName ?? null
  };
};

// Преобразование данных компонента в формат API
const denormalizeEvent = (event) => ({
  name: event.name,
  startDate: event.startDate,
  endDate: event.endDate,
  description: event.description || '',
  cloudUrl: event.cloudUrl != null && String(event.cloudUrl).trim() !== '' ? String(event.cloudUrl).trim() : '',
  status: (event.status === 'approved' || event.status === 'cancelled') ? event.status : 'not_approved',
  objectIds: Array.isArray(event.objectIds) ? event.objectIds : [],
  objectDates: event.objectDates && typeof event.objectDates === 'object' ? event.objectDates : {},
  ...(event.organizationId != null && event.organizationId !== '' && { organizationId: event.organizationId }),
  ...(event.sportTypeId != null && event.sportTypeId !== '' && { sportTypeId: event.sportTypeId })
});

// Для отображения на объекте: даты из objectDates[objectId] или основные даты мероприятия
function getEventStartEndForObject(event, objectId) {
  const od = event.objectDates && event.objectDates[objectId];
  if (od && (od.startDate || od.endDate)) {
    return {
      startDate: od.startDate || event.startDate,
      endDate: od.endDate || event.endDate
    };
  }
  return { startDate: event.startDate, endDate: event.endDate };
}

// Функция для генерации цвета по id или имени мероприятия. Неутверждённые — серый.
function getEventColor(eventId, eventName = '', status = '') {
  if (status === 'not_approved') {
    return '#9e9e9e'; // серый по умолчанию для неутверждённых
  }
  // Улучшенная палитра с более контрастными и разнообразными цветами
  // Избегаем желтых и светло-голубых (по предпочтениям пользователя)
  const palette = [
    '#4CAF50', // Зеленый
    '#2196F3', // Синий
    '#FF5722', // Глубокий оранжевый
    '#9C27B0', // Фиолетовый
    '#F44336', // Красный
    '#00BCD4', // Циан
    '#FF9800', // Оранжевый
    '#673AB7', // Глубокий фиолетовый
    '#E91E63', // Розовый
    '#009688', // Бирюзовый
    '#3F51B5', // Индиго
    '#795548', // Коричневый
    '#607D8B', // Сине-серый
    '#CDDC39', // Лайм
    '#FFC107', // Янтарный
    '#8BC34A', // Светло-зеленый
    '#FF6F00', // Темно-оранжевый
    '#7C4DFF', // Глубокий фиолетовый
    '#00E676', // Зелено-циан
    '#FF1744', // Ярко-красный
    '#1DE9B6', // Бирюзово-зеленый
    '#651FFF', // Фиолетово-синий
    '#00B8D4', // Циан
    '#C6FF00', // Желто-зеленый
    '#FF3D00', // Красно-оранжевый
    '#2962FF', // Синий
    '#AA00FF', // Фиолетовый
    '#00C853', // Зеленый
    '#FF6D00', // Оранжевый
    '#304FFE'  // Индиго
  ];
  
  // Используем имя события для генерации цвета (если есть), иначе ID
  const seed = eventName || eventId.toString();
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

// Генерация дополнительных классов события по статусу
const getEventStatusClasses = (event) => {
  const classes = [];
  if (event.status === 'not_approved') classes.push('event-not-approved');
  if (event.status === 'cancelled') classes.push('event-cancelled');
  return classes.join(' ');
};

const SportsScheduleTable = ({ focusedEventId, onFocusCleared }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const userRole = localStorage.getItem('userRole') || 'user';
  const [userProfile, setUserProfile] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModalMode, setEventModalMode] = useState('create');
  const [currentObjectIdxForEvent, setCurrentObjectIdxForEvent] = useState(null);
  const [defaultEventDate, setDefaultEventDate] = useState(null);
  const [highlightEventId, setHighlightEventId] = useState(null);
  
  // Вычисляем, может ли пользователь менять статус события
  const userOrgId = userProfile?.organizationId ? Number(userProfile.organizationId) : null;
  const eventOrgId = selectedEvent?.organizationId ? Number(selectedEvent.organizationId) : null;
  const canEditStatus = userRole === 'superuser' || 
    (userRole === 'admin' && userOrgId && userOrgId === eventOrgId) ||
    userRole === 'manager';
  
  // Загружаем профиль пользователя для получения organizationId
  useEffect(() => {
    if (userRole === 'admin') {
      usersAPI.getMe().then(setUserProfile).catch(() => setUserProfile(null));
    }
  }, [userRole]);

  // Обработка focusedEventId — переход из журнала мероприятий в месяц с подсветкой
  useEffect(() => {
    if (focusedEventId == null || data.length === 0) return;
    setCalendarMode('month');
    setHighlightEventId(focusedEventId);
    // Найти месяц мероприятия и переключиться на него
    for (const obj of data) {
      const found = (obj.events || []).find(e => e.id === focusedEventId);
      if (found) {
        const d = new Date(found.startDate);
        setMonthStartDate(new Date(d.getFullYear(), d.getMonth(), 1));
        break;
      }
    }
    if (onFocusCleared) onFocusCleared();
  }, [focusedEventId, data]);

  // Скролл к подсвеченному мероприятию после рендера month view
  useEffect(() => {
    if (highlightEventId == null) return;
    const timerId = setTimeout(() => {
      const el = document.querySelector(`[data-event-id="${highlightEventId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 100);
    const clearTimerId = setTimeout(() => {
      setHighlightEventId(null);
    }, 8000);
    return () => {
      clearTimeout(timerId);
      clearTimeout(clearTimerId);
    };
  }, [highlightEventId]);

  // Состояния для drag and drop
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragArmedEventId, setDragArmedEventId] = useState(null);
  const dragArmTimerRef = useRef(null);
  
  // Состояния для touch drag & drop (мобильные устройства)
  const [touchDragState, setTouchDragState] = useState(null);
  const touchStartPosRef = useRef(null);
  
  // Состояние для tooltip события
  const [eventTooltip, setEventTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const managerDefaultOverviewRef = useRef(false);
  const [overviewViewMode, setOverviewViewMode] = useState('week'); // 'week' | 'month' | 'year'

  // Состояния для сворачивания/разворачивания
  // eslint-disable-next-line no-unused-vars
  const [collapsedObjects] = useState(new Set());

  // Текущая дата для недели
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };
  
  const [weekStartDate, setWeekStartDate] = useState(getMonday(new Date()));
  const [monthStartDate, setMonthStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [yearStartDate, setYearStartDate] = useState(new Date(new Date().getFullYear(), 0, 1));

  const [activeTab, setActiveTab] = useState('events');
  const [calendarMode, setCalendarMode] = useState('week');
  const [eventDayDate, setEventDayDate] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
  const [selectedCell, setSelectedCell] = useState(null);

  // Расписание секций: объекты с помещениями, секции, слоты
  const [scheduleData, setScheduleData] = useState({ objects: [], sections: [], slots: [] });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleWeekStart, setScheduleWeekStart] = useState(getMonday(new Date()));
  const [scheduleViewMode, setScheduleViewMode] = useState('week'); // 'week' | 'hours' | 'month'
  const [scheduleDay, setScheduleDay] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
  const [scheduleMonthStart, setScheduleMonthStart] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [collapsedScheduleObjects, setCollapsedScheduleObjects] = useState(new Set()); // id объекта -> свёрнут
  // Режим «Шаблон недели»: календарь слотов vs шаблон (паттерн по дням недели)
  const [scheduleScheduleMode, setScheduleScheduleMode] = useState('calendar'); // 'calendar' | 'template'
  const [templateObjectId, setTemplateObjectId] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null); // { id, object_id, valid_from, valid_to }
  const [templateSlots, setTemplateSlots] = useState([]);
  const [templateList, setTemplateList] = useState([]); // шаблоны выбранного объекта
  const [templateOrgId, setTemplateOrgId] = useState(null);
  const [templateSportTypeId, setTemplateSportTypeId] = useState(null);
  const [templateCoachId, setTemplateCoachId] = useState(null); // тренер шаблона (для каждого тренера свой шаблон)
  const [templateCoachesFull, setTemplateCoachesFull] = useState([]); // все тренеры с орг/вид спорта для выбора
  const [templateOrganizations, setTemplateOrganizations] = useState([]);
  const [templateSportTypes, setTemplateSportTypes] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templatePeriod, setTemplatePeriod] = useState({ valid_from: '', valid_to: '' });
  const [showTemplateSlotModal, setShowTemplateSlotModal] = useState(false);
  const [templateSlotForm, setTemplateSlotForm] = useState({ sectionId: '', premiseId: '', day_of_week: 1, time_start: '08:00', time_end: '09:00', organizationId: '', sportTypeId: '', coachId: '', daysOfWeek: [false, false, false, false, false, false, false] });
  const [lastTemplateSlotRefs, setLastTemplateSlotRefs] = useState({ organizationId: '', sportTypeId: '', coachId: '' }); // последний выбор при добавлении слота шаблона
  const [editingTemplateSlot, setEditingTemplateSlot] = useState(null);
  const [editTemplateSlotForm, setEditTemplateSlotForm] = useState({ day_of_week: 1, time_start: '', time_end: '' });
  const logAction = (action, detail) => {
    const ts = new Date().toISOString();
    const detailStr = detail !== undefined && detail !== null ? (typeof detail === 'object' ? JSON.stringify(detail) : String(detail)) : '';
    console.log('%c[Действие]', 'color: #2196F3; font-weight: bold', ts, action, detailStr || '');
    debugLog.push({ type: 'user_action', action, detail: detail ?? '' });
  };
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [sectionForm, setSectionForm] = useState({ name: '', coach: '', institution: '' });
  const [slotForm, setSlotForm] = useState({ sectionId: '', premiseId: '', startDate: '', endDate: '', organizationId: '', sportTypeId: '', coachId: '' });
  // Данные для выбора в модалке слота (организация → вид спорта → тренер)
  const [slotModalOrgs, setSlotModalOrgs] = useState([]);
  const [slotModalSportTypes, setSlotModalSportTypes] = useState([]);
  const [slotModalCoaches, setSlotModalCoaches] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null); // { id, section_name, ..., start_date, end_date } для модалки редактирования
  const [editSlotForm, setEditSlotForm] = useState({ startDate: '', endDate: '' });
  const [copiedSlot, setCopiedSlot] = useState(null); // слот для вставки (копировать в другое место)
  const [scheduleDragOver, setScheduleDragOver] = useState(null); // { premId, day, hour? } при перетаскивании слота
  const [templateSlotDragOver, setTemplateSlotDragOver] = useState(null); // { premId, dayOfWeek } при перетаскивании слота шаблона

  // Справочники: организации → виды спорта → тренеры
  const [refOrganizations, setRefOrganizations] = useState([]);
  const [refSportTypes, setRefSportTypes] = useState([]);
  const [refCoaches, setRefCoaches] = useState([]);

  // Экспорт в Excel
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRefOrgId, setSelectedRefOrgId] = useState(null);
  const [selectedRefSportTypeId, setSelectedRefSportTypeId] = useState(null);
  const [refLoading, setRefLoading] = useState(false);
  const [refModal, setRefModal] = useState(null); // { type: 'org'|'sport'|'coach', editId?: number, name: string }
  
  // Состояния для праздничных и выходных дней
  const [holidays, setHolidays] = useState(new Set());
  const [weekendDays, setWeekendDays] = useState(new Set());

  // Загрузка данных с сервера
  useEffect(() => {
    loadData();
  }, []);

  // Загрузка профиля пользователя для автозаполнения организации
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profile = await usersAPI.getMe();
        setUserProfile(profile);
      } catch (err) {
        console.error('Ошибка загрузки профиля:', err);
      }
    };
    loadUserProfile();
  }, []);

  // Загрузка данных расписания при переключении на вкладку «Расписание»
  const loadScheduleData = async () => {
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const result = await scheduleAPI.getData();
      setScheduleData(result);
    } catch (err) {
      setScheduleError(err.response?.data?.error || 'Ошибка загрузки расписания');
      console.error(err);
    } finally {
      setScheduleLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'schedule' || activeTab === 'overview') loadScheduleData();
    if (activeTab === 'references') loadRefOrganizations();
  }, [activeTab]);

  // Загрузка организаций и тренеров при входе в режим «Шаблон недели»
  useEffect(() => {
    if (activeTab !== 'schedule' || scheduleScheduleMode !== 'template') return;
    referencesAPI.getOrganizations().then(setTemplateOrganizations).catch(() => setTemplateOrganizations([]));
    referencesAPI.getCoachesFull().then(setTemplateCoachesFull).catch(() => setTemplateCoachesFull([]));
  }, [activeTab, scheduleScheduleMode]);

  // Загрузка видов спорта при выборе организации в шаблоне
  useEffect(() => {
    if (!templateOrgId) { setTemplateSportTypes([]); return; }
    referencesAPI.getSportTypes(templateOrgId).then(setTemplateSportTypes).catch(() => setTemplateSportTypes([]));
  }, [templateOrgId]);

  // Загрузка списка шаблонов при выборе объекта — показываем все шаблоны объекта (всех тренеров)
  useEffect(() => {
    if (activeTab !== 'schedule' || scheduleScheduleMode !== 'template' || !templateObjectId) return;
    setTemplateLoading(true);
    scheduleAPI.getScheduleTemplates(templateObjectId, null)
      .then((list) => { setTemplateList(list || []); setCurrentTemplate(null); setTemplateSlots([]); })
      .catch(() => setTemplateList([]))
      .finally(() => setTemplateLoading(false));
  }, [activeTab, scheduleScheduleMode, templateObjectId]);

  // При загрузке шаблона подставить организацию и вид спорта по тренеру шаблона
  useEffect(() => {
    if (activeTab !== 'schedule' || scheduleScheduleMode !== 'template' || !currentTemplate?.coach_id) return;
    const coaches = templateCoachesFull || [];
    if (coaches.length === 0) return;
    const coach = coaches.find((c) => Number(c.id) === Number(currentTemplate.coach_id));
    if (coach) {
      setTemplateOrgId((prev) => (prev != null && prev !== '' ? prev : (coach.organization_id != null ? Number(coach.organization_id) : null)));
      setTemplateSportTypeId((prev) => (prev != null && prev !== '' ? prev : (coach.sport_type_id != null ? Number(coach.sport_type_id) : null)));
    }
  }, [activeTab, scheduleScheduleMode, currentTemplate?.id, currentTemplate?.coach_id, templateCoachesFull]);

  // Автоопределение тренера по событиям шаблона: если в шаблоне есть слоты и тренер не выбран — подставить тренера из первого слота (организация + вид спорта + тренер)
  useEffect(() => {
    if (activeTab !== 'schedule' || scheduleScheduleMode !== 'template') return;
    if (templateSlots.length === 0 || (templateCoachId != null && templateCoachId !== '')) return;
    const coaches = templateCoachesFull || [];
    if (coaches.length === 0) return;
    const sections = scheduleData.sections || [];
    const first = templateSlots[0];
    const sectionId = first?.section_id != null ? Number(first.section_id) : null;
    const section = sectionId != null ? sections.find((s) => Number(s.id) === sectionId) : null;
    if (!section) return;
    const inst = (section.institution || '').trim();
    const sportName = (section.name || '').trim();
    const coachName = (section.coach || '').trim();
    const match = coaches.find(
      (c) =>
        (c.organization_name || '').trim() === inst &&
        (c.sport_type_name || '').trim() === sportName &&
        (c.name || '').trim() === coachName
    );
    if (match) setTemplateCoachId(match.id);
  }, [activeTab, scheduleScheduleMode, templateSlots, templateCoachId, templateCoachesFull, scheduleData.sections]);

  // Загрузка шаблона и слотов при выборе шаблона из списка — шаблон появляется в сетке для редактирования слотов
  const loadTemplateAndSlots = (templateId) => {
    if (!templateId) { setCurrentTemplate(null); setTemplateSlots([]); return Promise.resolve(); }
    setTemplateLoading(true);
    return Promise.all([
      scheduleAPI.getScheduleTemplate(templateId),
      scheduleAPI.getScheduleTemplateSlots(templateId)
    ])
      .then(([t, slots]) => {
        setCurrentTemplate(t);
        if (t.object_id != null) setTemplateObjectId(t.object_id);
        if (t.coach_id != null) setTemplateCoachId(t.coach_id);
        setTemplatePeriod({ valid_from: t.valid_from || '', valid_to: t.valid_to || '' });
        setTemplateSlots(slots || []);
      })
      .catch(() => { setCurrentTemplate(null); setTemplateSlots([]); })
      .finally(() => setTemplateLoading(false));
  };

  const handleCreateScheduleTemplate = () => {
    const { valid_from, valid_to } = templatePeriod;
    if (!valid_from || !valid_to || !templateObjectId) return;
    if (templateCoachId == null || templateCoachId === '') {
      alert('Выберите тренера. Для каждого тренера создаётся свой недельный шаблон.');
      return;
    }
    setTemplateLoading(true);
    scheduleAPI.createScheduleTemplate(templateObjectId, valid_from, valid_to, templateCoachId)
      .then((t) => {
        setTemplateList((prev) => [...prev, t].sort((a, b) => (a.valid_from || '').localeCompare(b.valid_from || '')));
        setCurrentTemplate(t);
        setTemplateSlots([]);
        setTemplatePeriod({ valid_from: t.valid_from, valid_to: t.valid_to });
      })
      .finally(() => setTemplateLoading(false));
  };

  const handleApproveTemplate = () => {
    if (!currentTemplate?.id) return;
    setTemplateLoading(true);
    scheduleAPI.approveScheduleTemplate(currentTemplate.id)
      .then(() => {
        logAction('template_approve');
        setCurrentTemplate((prev) => (prev ? { ...prev, status: 'approved' } : null));
        loadScheduleData();
        if (templateObjectId) scheduleAPI.getScheduleTemplates(templateObjectId, null).then((list) => setTemplateList(list || [])).catch(() => {});
      })
      .finally(() => setTemplateLoading(false));
  };

  const handleEditTemplate = () => {
    if (!currentTemplate?.id) return;
    const { valid_from, valid_to } = templatePeriod;
    if (!valid_from || !valid_to) {
      alert('Укажите срок действия шаблона (даты «С» и «По»).');
      return;
    }
    setTemplateLoading(true);
    scheduleAPI.updateScheduleTemplate(currentTemplate.id, valid_from, valid_to, templateCoachId ?? currentTemplate.coach_id ?? undefined)
      .then(() => {
        setCurrentTemplate((prev) => prev ? { ...prev, valid_from, valid_to } : null);
        return scheduleAPI.saveScheduleTemplateChanges(currentTemplate.id);
      })
      .then(() => { logAction('template_edit'); loadScheduleData(); })
      .catch((err) => { const msg = err.response?.data?.error || err.message; window.alert(msg || 'Не удалось обновить шаблон'); })
      .finally(() => setTemplateLoading(false));
  };

  const handleDeleteTemplate = () => {
    if (!currentTemplate?.id) return;
    if (!window.confirm('Удалить шаблон расписания? Все слоты в календаре, созданные из этого шаблона, также будут удалены.')) return;
    setTemplateLoading(true);
    scheduleAPI.deleteScheduleTemplate(currentTemplate.id)
      .then(() => {
        logAction('template_delete');
        setCurrentTemplate(null);
        setTemplateSlots([]);
        setTemplatePeriod({ valid_from: '', valid_to: '' });
        if (templateObjectId) {
          scheduleAPI.getScheduleTemplates(templateObjectId, null)
            .then((list) => setTemplateList(list || []))
            .catch(() => setTemplateList([]));
        } else setTemplateList([]);
        loadScheduleData();
      })
      .catch((err) => { const msg = err.response?.data?.error || err.message; window.alert(msg || 'Не удалось удалить шаблон'); })
      .finally(() => setTemplateLoading(false));
  };

  const handleSaveTemplateChanges = () => {
    if (!currentTemplate?.id) return;
    setTemplateLoading(true);
    scheduleAPI.saveScheduleTemplateChanges(currentTemplate.id)
      .then(() => { logAction('template_save_changes'); loadScheduleData(); })
      .finally(() => setTemplateLoading(false));
  };

  const openTemplateSlotModalForCell = (premiseId, dayOfWeek, timeStart = '08:00', timeEnd = '09:00') => {
    logAction('template_slot_modal_open', { premiseId, dayOfWeek, timeStart, timeEnd });
    let organizationId = lastTemplateSlotRefs.organizationId || '';
    let sportTypeId = lastTemplateSlotRefs.sportTypeId || '';
    let coachId = lastTemplateSlotRefs.coachId || '';
    if (currentTemplate?.coach_id != null && (templateCoachesFull || []).length > 0) {
      const coach = templateCoachesFull.find((c) => Number(c.id) === Number(currentTemplate.coach_id));
      if (coach) {
        organizationId = String(coach.organization_id ?? '');
        sportTypeId = String(coach.sport_type_id ?? '');
        coachId = String(coach.id ?? '');
      }
    }
    const daysOfWeek = [false, false, false, false, false, false, false];
    if (dayOfWeek >= 1 && dayOfWeek <= 7) daysOfWeek[dayOfWeek - 1] = true;
    setTemplateSlotForm({
      sectionId: '',
      premiseId: premiseId,
      day_of_week: dayOfWeek,
      time_start: timeStart,
      time_end: timeEnd,
      organizationId,
      sportTypeId,
      coachId,
      daysOfWeek
    });
    if (organizationId) {
      referencesAPI.getSportTypes(organizationId).then((sportTypes) => {
        setSlotModalSportTypes(sportTypes);
        if (sportTypeId) {
          referencesAPI.getCoaches(sportTypeId).then(setSlotModalCoaches).catch(() => setSlotModalCoaches([]));
        } else setSlotModalCoaches([]);
      }).catch(() => { setSlotModalSportTypes([]); setSlotModalCoaches([]); });
    } else {
      setSlotModalSportTypes([]);
      setSlotModalCoaches([]);
    }
    setShowTemplateSlotModal(true);
    setEditingTemplateSlot(null);
  };

  const logTemplateSlotSave = (kind, payload) => {
    logAction('template_slot_save', { kind, ...payload });
  };

  // Загрузка справочников при открытии модалки слота шаблона
  useEffect(() => {
    if (!showTemplateSlotModal) return;
    if (slotModalOrgs.length === 0) referencesAPI.getOrganizations().then(setSlotModalOrgs).catch(() => setSlotModalOrgs([]));
  }, [showTemplateSlotModal]);

  // При редактировании слота шаблона подставляем Организация / Вид спорта / Тренер по секции
  useEffect(() => {
    if (!showTemplateSlotModal || !editingTemplateSlot || !scheduleData.sections?.length || !slotModalOrgs.length) return;
    const section = scheduleData.sections.find((s) => Number(s.id) === Number(editingTemplateSlot.section_id));
    if (!section) return;
    const inst = (section.institution || '').trim();
    const sectionName = (section.name || '').trim();
    const coachName = (section.coach || '').trim();
    const org = slotModalOrgs.find((o) => (o.name || '').trim() === inst);
    if (!org) return;
    setTemplateSlotForm((f) => ({ ...f, organizationId: String(org.id) }));
    referencesAPI.getSportTypes(org.id).then((sportTypes) => {
      setSlotModalSportTypes(sportTypes);
      const sport = sportTypes.find((s) => (s.name || '').trim() === sectionName);
      if (sport) {
        setTemplateSlotForm((f) => ({ ...f, sportTypeId: String(sport.id) }));
        referencesAPI.getCoaches(sport.id).then((coaches) => {
          setSlotModalCoaches(coaches);
          const coach = coaches.find((c) => (c.name || '').trim() === coachName);
          if (coach) setTemplateSlotForm((f) => ({ ...f, coachId: String(coach.id) }));
        }).catch(() => setSlotModalCoaches([]));
      }
    }).catch(() => setSlotModalSportTypes([]));
  }, [showTemplateSlotModal, editingTemplateSlot?.id, scheduleData.sections, slotModalOrgs]);

  const saveTemplateSlot = async () => {
    const { sectionId, premiseId, day_of_week, time_start, time_end, organizationId, sportTypeId, coachId, daysOfWeek } = templateSlotForm;
    if (!premiseId) return;
    const useRefs = organizationId && sportTypeId;
    if (!useRefs && !sectionId) {
      alert('Выберите организацию и вид спорта');
      return;
    }
    const ts = (time_start || '08:00').toString().trim().slice(0, 8);
    const te = (time_end || '09:00').toString().trim().slice(0, 8);
    let resolvedSectionId = sectionId;
    let sectionName = '';
    if (useRefs) {
      const org = slotModalOrgs.find((o) => String(o.id) === String(organizationId));
      const sport = slotModalSportTypes.find((s) => String(s.id) === String(sportTypeId));
      if (!org || !sport) {
        alert('Выберите организацию и вид спорта');
        return;
      }
      const institution = org.name;
      const sportName = sport.name;
      const coach = coachId ? slotModalCoaches.find((c) => String(c.id) === String(coachId)) : null;
      const coachName = coach ? coach.name : '';
      const existing = (scheduleData.sections || []).find(
        (s) => (s.institution || '') === institution && (s.name || '') === sportName && (s.coach || '') === coachName
      );
      if (existing) {
        resolvedSectionId = String(existing.id);
      } else {
        try {
          const created = await scheduleAPI.createSection({ name: sportName, coach: coachName, institution });
          resolvedSectionId = String(created.id);
          setScheduleData((prev) => ({ ...prev, sections: [...(prev.sections || []), { id: created.id, name: sportName, coach: coachName, institution }] }));
        } catch (e) {
          alert(e.response?.data?.error || 'Не удалось создать секцию');
          return;
        }
      }
      sectionName = [institution, coachName, sportName].filter(Boolean).join(' ');
    } else {
      const section = (scheduleData.sections || []).find((s) => Number(s.id) === Number(sectionId));
      sectionName = section ? [section.institution, section.coach, section.name].filter(Boolean).join(' ') : '';
    }

    if (useRefs) setLastTemplateSlotRefs({ organizationId: organizationId || '', sportTypeId: sportTypeId || '', coachId: coachId || '' });

    const selectedDays = (daysOfWeek || Array(7).fill(false)).map((v, i) => (v ? i + 1 : 0)).filter(Boolean);
    const dayForEdit = selectedDays.length > 0 ? selectedDays[0] : (day_of_week || 1);
    if (!editingTemplateSlot && selectedDays.length === 0) {
      alert('Выберите хотя бы один день недели (Пн–Вс).');
      return;
    }

    if (currentTemplate?.id) {
      try {
        if (editingTemplateSlot) {
          logTemplateSlotSave(editingTemplateSlot.id?.toString().startsWith('draft-') ? 'edit_draft' : 'edit_api', { slotId: editingTemplateSlot.id, day_of_week: dayForEdit });
          if (typeof editingTemplateSlot.id === 'string' && editingTemplateSlot.id.startsWith('draft-')) {
            setTemplateSlots((prev) => prev.map((s) => (s.id === editingTemplateSlot.id ? { ...s, section_id: resolvedSectionId, premise_id: premiseId, day_of_week: dayForEdit, time_start: ts, time_end: te, section_name: sectionName } : s)));
          } else {
            await scheduleAPI.updateScheduleTemplateSlot(editingTemplateSlot.id, { section_id: resolvedSectionId, premise_id: premiseId, day_of_week: dayForEdit, time_start: ts, time_end: te });
            setTemplateSlots((prev) => prev.map((s) => (s.id === editingTemplateSlot.id ? { ...s, section_id: resolvedSectionId, day_of_week: dayForEdit, time_start: ts, time_end: te, section_name: sectionName } : s)));
          }
        } else {
          const toCreate = selectedDays.length > 0 ? selectedDays : [day_of_week || 1];
          logTemplateSlotSave('add_multi', { days: toCreate, premiseId, timeStart: ts, timeEnd: te });
          for (const dow of toCreate) {
            await scheduleAPI.createScheduleTemplateSlot(currentTemplate.id, { section_id: resolvedSectionId, premise_id: premiseId, day_of_week: dow, time_start: ts, time_end: te });
          }
          await loadTemplateAndSlots(currentTemplate.id);
        }
        setShowTemplateSlotModal(false);
        setEditingTemplateSlot(null);
      } catch (e) {
        console.error(e);
      }
      return;
    }

    // Черновик: без сохранённого шаблона — храним слоты только в state
    if (editingTemplateSlot) {
      logTemplateSlotSave('edit_draft', { day_of_week: dayForEdit });
      setTemplateSlots((prev) => prev.map((s) => (s.id === editingTemplateSlot.id ? { ...s, section_id: resolvedSectionId, premise_id: premiseId, day_of_week: dayForEdit, time_start: ts, time_end: te, section_name: sectionName } : s)));
    } else {
      const toAdd = selectedDays.length > 0 ? selectedDays : [day_of_week || 1];
      logTemplateSlotSave('add_draft', { days: toAdd, premiseId, timeStart: ts, timeEnd: te });
      const premiseName = (selectedScheduleObject?.premises || []).find((p) => p.id === premiseId)?.name || '';
      setTemplateSlots((prev) => [...prev, ...toAdd.map((dow, idx) => ({
        id: 'draft-' + Date.now() + '-' + idx,
        section_id: resolvedSectionId,
        premise_id: premiseId,
        day_of_week: dow,
        time_start: ts,
        time_end: te,
        section_name: sectionName,
        premise_name: premiseName
      }))]);
    }
    setShowTemplateSlotModal(false);
    setEditingTemplateSlot(null);
  };

  const deleteTemplateSlot = async (slot) => {
    if (typeof slot.id === 'string' && slot.id.startsWith('draft-')) {
      setTemplateSlots((prev) => prev.filter((s) => s.id !== slot.id));
      return;
    }
    try {
      await scheduleAPI.deleteScheduleTemplateSlot(slot.id);
      setTemplateSlots((prev) => prev.filter((s) => s.id !== slot.id));
    } catch (e) {
      console.error(e);
    }
  };

  // Сохранить шаблон и слоты без утверждения. Черновик остаётся черновиком; утверждённый — обновляет слоты в календаре.
  const handleSaveTemplate = async () => {
    const { valid_from, valid_to } = templatePeriod;
    if (!valid_from || !valid_to) {
      alert('Укажите срок действия шаблона (даты «С» и «По»).');
      return;
    }
    if (!templateObjectId) return;
    if (!currentTemplate?.id && (templateCoachId == null || templateCoachId === '')) {
      alert('Выберите тренера. Для каждого тренера создаётся свой недельный шаблон.');
      return;
    }
    setTemplateLoading(true);
    try {
      if (!currentTemplate?.id) {
        // Новый шаблон: создаём и сохраняем слоты, оставляем черновиком (без утверждения)
        const t = await scheduleAPI.createScheduleTemplate(templateObjectId, valid_from, valid_to, templateCoachId);
        setCurrentTemplate(t);
        setTemplateList((prev) => [...prev, t].sort((a, b) => (a.valid_from || '').localeCompare(b.valid_from || '')));
        for (const slot of templateSlots) {
          await scheduleAPI.createScheduleTemplateSlot(t.id, {
            section_id: slot.section_id,
            premise_id: slot.premise_id,
            day_of_week: slot.day_of_week,
            time_start: (slot.time_start || '08:00').toString().trim().slice(0, 8),
            time_end: (slot.time_end || '09:00').toString().trim().slice(0, 8)
          });
        }
        await loadTemplateAndSlots(t.id);
        // Не вызываем approve — шаблон остаётся черновиком; утвердить можно кнопкой «Утвердить»
      } else {
        // Существующий шаблон: обновляем срок/тренера и слоты
        await scheduleAPI.updateScheduleTemplate(currentTemplate.id, valid_from, valid_to, templateCoachId ?? currentTemplate.coach_id ?? undefined);
        setCurrentTemplate((prev) => prev ? { ...prev, valid_from, valid_to } : null);
        const existing = await scheduleAPI.getScheduleTemplateSlots(currentTemplate.id);
        for (const s of existing) await scheduleAPI.deleteScheduleTemplateSlot(s.id);
        for (const slot of templateSlots) {
          await scheduleAPI.createScheduleTemplateSlot(currentTemplate.id, {
            section_id: slot.section_id,
            premise_id: slot.premise_id,
            day_of_week: slot.day_of_week,
            time_start: (slot.time_start || '08:00').toString().trim().slice(0, 8),
            time_end: (slot.time_end || '09:00').toString().trim().slice(0, 8)
          });
        }
        await loadTemplateAndSlots(currentTemplate.id);
        // Если шаблон уже утверждён — пересчитываем слоты в календаре; если черновик — только сохраняем
        if (currentTemplate.status === 'approved') {
          await scheduleAPI.saveScheduleTemplateChanges(currentTemplate.id);
          await loadScheduleData();
        }
      }
      logAction('template_save');
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Ошибка сохранения шаблона');
    } finally {
      setTemplateLoading(false);
    }
  };

  // Обзор доступен manager и admin (при SHOW_SCHEDULE)
  const isManagerWithSchedule = userRole === 'manager' && SHOW_SCHEDULE;
  const isAdminWithSchedule = userRole === 'admin' && SHOW_SCHEDULE;
  const showOverviewTab = isManagerWithSchedule || isAdminWithSchedule;

  // Расписание видят admin и manager (если SHOW_SCHEDULE); Справочники — только admin. Для manager по умолчанию — Обзор.
  useEffect(() => {
    if (activeTab === 'references' && userRole !== 'superuser') {
      setActiveTab(userRole === 'manager' && SHOW_SCHEDULE ? 'overview' : 'events');
    } else if (activeTab === 'schedule' && (userRole === 'user' || !SHOW_SCHEDULE)) {
      setActiveTab('events');
    } else if (activeTab === 'overview' && !showOverviewTab) {
      setActiveTab('events');
    }
  }, [userRole, activeTab, showOverviewTab]);
  // При первом входе руководителя показывать Обзор по умолчанию
  useEffect(() => {
    if (isManagerWithSchedule && !managerDefaultOverviewRef.current) {
      managerDefaultOverviewRef.current = true;
      setActiveTab('overview');
    }
  }, [isManagerWithSchedule]);

  useEffect(() => {
    if (activeTab !== 'references') return;
    if (selectedRefOrgId) {
      referencesAPI.getSportTypes(selectedRefOrgId).then(setRefSportTypes).catch(() => setRefSportTypes([]));
      setSelectedRefSportTypeId(null);
      setRefCoaches([]);
    } else {
      setRefSportTypes([]);
      setSelectedRefSportTypeId(null);
      setRefCoaches([]);
    }
  }, [activeTab, selectedRefOrgId]);

  useEffect(() => {
    if (activeTab !== 'references') return;
    if (selectedRefSportTypeId) {
      referencesAPI.getCoaches(selectedRefSportTypeId).then(setRefCoaches).catch(() => setRefCoaches([]));
    } else {
      setRefCoaches([]);
    }
  }, [activeTab, selectedRefSportTypeId]);

  const loadRefOrganizations = async () => {
    setRefLoading(true);
    try {
      const list = await referencesAPI.getOrganizations();
      setRefOrganizations(list);
    } catch (e) {
      console.error(e);
      setRefOrganizations([]);
    } finally {
      setRefLoading(false);
    }
  };

  const getExportDateRange = () => {
    const formatDate = (d) => {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    let startDate, endDate, filename;

    if (calendarMode === 'day') {
      startDate = formatDate(eventDayDate);
      endDate = formatDate(eventDayDate);
      filename = `мероприятия_${startDate}`;
    } else if (calendarMode === 'week') {
      const weekDates = getWeekDates(weekStartDate);
      startDate = formatDate(weekDates[0]);
      endDate = formatDate(weekDates[6]);
      filename = `мероприятия_неделя_${startDate}`;
    } else if (calendarMode === 'month') {
      const year = monthStartDate.getFullYear();
      const month = monthStartDate.getMonth();
      startDate = formatDate(new Date(year, month, 1));
      const lastDay = new Date(year, month + 1, 0).getDate();
      endDate = formatDate(new Date(year, month, lastDay));
      filename = `мероприятия_${monthsOfYear[month]}_${year}`;
    } else if (calendarMode === 'year') {
      const year = yearStartDate.getFullYear();
      startDate = formatDate(new Date(year, 0, 1));
      endDate = formatDate(new Date(year, 11, 31));
      filename = `мероприятия_${year}`;
    }

    return { startDate, endDate, filename };
  };

  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const { startDate, endDate, filename } = getExportDateRange();
      const data = await eventsAPI.exportToExcel(startDate, endDate);
      if (!data || data.length === 0) {
        alert('Нет данных для экспорта');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Мероприятия');
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } catch (e) {
      console.error(e);
      alert('Ошибка экспорта: ' + (e.response?.data?.error || e.message));
    } finally {
      setExportLoading(false);
    }
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!sectionForm.name.trim()) return;
    try {
      await scheduleAPI.createSection({
        name: sectionForm.name.trim(),
        coach: sectionForm.coach.trim() || undefined,
        institution: sectionForm.institution.trim() || undefined
      });
      setSectionForm({ name: '', coach: '', institution: '' });
      logAction('section_modal_close', 'save');
      setShowSectionModal(false);
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка создания секции');
    }
  };

  const handleAddPremise = async (objectId) => {
    const name = window.prompt('Название помещения (зал, бассейн, корт):');
    if (!name || !name.trim()) return;
    try {
      await scheduleAPI.createPremise(objectId, name.trim());
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка создания помещения');
    }
  };

  const handleDeletePremise = async (premiseId) => {
    if (!window.confirm('Удалить помещение? Слоты расписания в нём тоже удалятся.')) return;
    try {
      await scheduleAPI.deletePremise(premiseId);
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка удаления помещения');
    }
  };

  const toggleScheduleObjectCollapsed = (objectId) => {
    setCollapsedScheduleObjects((prev) => {
      const next = new Set(prev);
      if (next.has(objectId)) next.delete(objectId);
      else next.add(objectId);
      return next;
    });
  };

  // Формат даты для input datetime-local: YYYY-MM-DDTHH:mm
  const toDatetimeLocal = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // Парсим значение datetime-local как локальное время (избегаем разной трактовки в браузерах)
  const parseDatetimeLocal = (str) => {
    if (!str || typeof str !== 'string') return null;
    const [datePart, timePart] = str.split('T');
    if (!datePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh = 0, mm = 0] = (timePart || '0:0').split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  };

  // Открыть модалку редактирования слота (клик по слоту)
  const openSlotEditModal = (slot) => {
    logAction('slot_edit_open', { slotId: slot.id, section: slot.section_name });
    const start = new Date(slot.start_date);
    const end = new Date(slot.end_date);
    setEditingSlot(slot);
    setEditSlotForm({
      startDate: toDatetimeLocal(start),
      endDate: toDatetimeLocal(end)
    });
  };

  const handleUpdateSlot = async (e) => {
    e.preventDefault();
    if (!editingSlot || !editSlotForm.startDate || !editSlotForm.endDate) return;
    logAction('slot_edit_save', { slotId: editingSlot.id });
    try {
      const start = parseDatetimeLocal(editSlotForm.startDate);
      const end = parseDatetimeLocal(editSlotForm.endDate);
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        alert('Некорректные дата или время');
        return;
      }
      await scheduleAPI.updateSectionSchedule(editingSlot.id, {
        startDate: start.toISOString().slice(0, 19),
        endDate: end.toISOString().slice(0, 19)
      });
      logAction('slot_edit_modal_close', 'save');
      setEditingSlot(null);
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка сохранения');
    }
  };

  const handleDeleteSlot = async () => {
    if (!editingSlot || !window.confirm('Удалить этот слот из расписания?')) return;
    logAction('slot_delete', { slotId: editingSlot.id });
    try {
      await scheduleAPI.deleteSectionSchedule(editingSlot.id);
      logAction('slot_edit_modal_close', 'delete');
      setEditingSlot(null);
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка удаления');
    }
  };

  // Открыть модалку «Добавить слот» с подстановкой помещения и времени ячейки (при copiedSlot — подставить секцию)
  const openSlotModalForCell = (premiseId, day, hour) => {
    logAction('slot_add_open', { premiseId, day: day?.toLocaleDateString?.(), hour });
    const start = hour == null
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0)
      : new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0);
    const end = hour == null
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 10, 0, 0, 0)
      : new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0, 0, 0);
    const fromCopy = copiedSlot != null;
    setSlotForm((f) => ({
      ...f,
      premiseId: String(premiseId),
      startDate: toDatetimeLocal(start),
      endDate: toDatetimeLocal(end),
      ...(fromCopy ? { sectionId: String(copiedSlot.section_id) } : { organizationId: '', sportTypeId: '', coachId: '', sectionId: '' })
    }));
    setSlotModalSportTypes([]);
    setSlotModalCoaches([]);
    setShowSlotModal(true);
  };

  // При открытии модалки слота загружаем организации для выбора
  useEffect(() => {
    if (!showSlotModal) return;
    referencesAPI.getOrganizations().then(setSlotModalOrgs).catch(() => setSlotModalOrgs([]));
    setSlotModalSportTypes([]);
    setSlotModalCoaches([]);
  }, [showSlotModal]);

  // При вставке из скопированного слота подставить организацию, вид спорта и тренера по именам из секции
  useEffect(() => {
    if (!showSlotModal || !copiedSlot || !slotModalOrgs.length) return;
    if (slotForm.sectionId !== String(copiedSlot.section_id) || slotForm.organizationId) return;
    const inst = (copiedSlot.section_institution || '').trim();
    const org = slotModalOrgs.find((o) => (o.name || '').trim() === inst);
    if (!org) return;
    setSlotForm((prev) => ({ ...prev, organizationId: String(org.id) }));
    referencesAPI.getSportTypes(org.id).then((sportTypes) => {
      setSlotModalSportTypes(sportTypes);
      const sectionName = (copiedSlot.section_name || '').trim();
      const sport = sportTypes.find((s) => (s.name || '').trim() === sectionName);
      if (sport) {
        setSlotForm((prev) => ({ ...prev, sportTypeId: String(sport.id) }));
        referencesAPI.getCoaches(sport.id).then((coaches) => {
          setSlotModalCoaches(coaches);
          const coachName = (copiedSlot.section_coach || '').trim();
          const coach = coaches.find((c) => (c.name || '').trim() === coachName);
          if (coach) setSlotForm((prev) => ({ ...prev, coachId: String(coach.id) }));
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [showSlotModal, copiedSlot, slotModalOrgs, slotForm.sectionId, slotForm.organizationId]);

  const handleAddSlot = async (e) => {
    e.preventDefault();
    const useRefs = slotForm.organizationId && slotForm.sportTypeId;
    if (!useRefs && !slotForm.sectionId) {
      alert('Выберите организацию и вид спорта или секцию');
      return;
    }
    if (!slotForm.premiseId || !slotForm.startDate || !slotForm.endDate) {
      alert('Заполните помещение и даты');
      return;
    }
    try {
      let sectionId = slotForm.sectionId ? parseInt(slotForm.sectionId, 10) : null;
      if (useRefs) {
        const org = slotModalOrgs.find((o) => String(o.id) === String(slotForm.organizationId));
        const sport = slotModalSportTypes.find((s) => String(s.id) === String(slotForm.sportTypeId));
        if (!org || !sport) {
          alert('Выберите организацию и вид спорта');
          return;
        }
        const institution = org.name;
        const sectionName = sport.name;
        const coach = slotForm.coachId ? slotModalCoaches.find((c) => String(c.id) === String(slotForm.coachId)) : null;
        const coachName = coach ? coach.name : '';
        const existing = (scheduleData.sections || []).find(
          (s) => (s.institution || '') === institution && (s.name || '') === sectionName && (s.coach || '') === coachName
        );
        if (existing) {
          sectionId = existing.id;
        } else {
          const created = await scheduleAPI.createSection({ name: sectionName, coach: coachName, institution });
          sectionId = created.id;
        }
      }
      const start = parseDatetimeLocal(slotForm.startDate);
      const end = parseDatetimeLocal(slotForm.endDate);
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        alert('Некорректные дата или время');
        return;
      }
      await scheduleAPI.createSectionSchedule({
        sectionId,
        premiseId: parseInt(slotForm.premiseId, 10),
        startDate: start.toISOString().slice(0, 19),
        endDate: end.toISOString().slice(0, 19)
      });
      setSlotForm({ sectionId: '', premiseId: '', startDate: '', endDate: '', organizationId: '', sportTypeId: '', coachId: '' });
      logAction('slot_modal_close', 'save');
      setShowSlotModal(false);
      if (slotForm.sectionId) setCopiedSlot(null); // сброс копирования после вставки
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка создания слота');
    }
  };

  // Drag-and-drop слотов: перенос в другую ячейку (та же помещение — перемещение PUT, другое — копия POST)
  const SCHEDULE_SLOT_MIME = 'application/x-schedule-slot';
  const handleScheduleSlotDragStart = (e, slot) => {
    e.dataTransfer.setData(SCHEDULE_SLOT_MIME, JSON.stringify({
      id: slot.id,
      section_id: slot.section_id,
      premise_id: slot.premise_id,
      start_date: slot.start_date,
      end_date: slot.end_date
    }));
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleScheduleSlotDragOver = (e, premId, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setScheduleDragOver((prev) => {
      const key = `${premId}-${day.getTime()}`;
      if (prev && prev.premId === premId && prev.day.getTime() === day.getTime()) return prev;
      return { premId, day };
    });
  };
  const handleScheduleSlotDragLeave = (e, premId, day) => {
    const related = e.relatedTarget;
    if (!related || !e.currentTarget.contains(related)) {
      setScheduleDragOver((prev) => prev && prev.premId === premId && prev.day.getTime() === day.getTime() ? null : prev);
    }
  };
  const handleScheduleSlotDrop = async (e, targetPremId, targetDay) => {
    e.preventDefault();
    setScheduleDragOver(null);
    const raw = e.dataTransfer.getData(SCHEDULE_SLOT_MIME);
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const slotStart = new Date(payload.start_date);
    const slotEnd = new Date(payload.end_date);
    const newStart = new Date(targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(), slotStart.getHours(), slotStart.getMinutes(), slotStart.getSeconds(), 0);
    const newEnd = new Date(targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(), slotEnd.getHours(), slotEnd.getMinutes(), slotEnd.getSeconds(), 0);
    if (Number(newEnd) <= Number(newStart)) return;
    const samePremise = Number(targetPremId) === Number(payload.premise_id);
    try {
      if (samePremise) {
        await scheduleAPI.updateSectionSchedule(payload.id, { startDate: newStart.toISOString().slice(0, 19), endDate: newEnd.toISOString().slice(0, 19) });
        logAction('schedule_slot_move', { slotId: payload.id, toDay: targetDay.toLocaleDateString() });
      } else {
        await scheduleAPI.createSectionSchedule({
          sectionId: payload.section_id,
          premiseId: Number(targetPremId),
          startDate: newStart.toISOString().slice(0, 19),
          endDate: newEnd.toISOString().slice(0, 19)
        });
        logAction('schedule_slot_copy_drop', { fromSlotId: payload.id, toPremId: targetPremId, toDay: targetDay.toLocaleDateString() });
      }
      await loadScheduleData();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка при переносе слота');
    }
  };
  const handleScheduleSlotDragEnd = () => {
    setScheduleDragOver(null);
  };

  // Drag-and-drop слотов шаблона: то же помещение — PUT (день/время), другое помещение — POST (копия)
  const TEMPLATE_SLOT_MIME = 'application/x-template-slot';
  const handleTemplateSlotDragStart = (e, slot) => {
    if (typeof slot.id === 'string' && slot.id.startsWith('draft-')) return;
    e.dataTransfer.setData(TEMPLATE_SLOT_MIME, JSON.stringify({
      id: slot.id,
      section_id: slot.section_id,
      premise_id: slot.premise_id,
      day_of_week: slot.day_of_week,
      time_start: (slot.time_start || '08:00').toString().trim().slice(0, 8),
      time_end: (slot.time_end || '09:00').toString().trim().slice(0, 8)
    }));
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleTemplateSlotDragOver = (e, premId, dayOfWeek) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setTemplateSlotDragOver((prev) => {
      if (prev && prev.premId === premId && prev.dayOfWeek === dayOfWeek) return prev;
      return { premId, dayOfWeek };
    });
  };
  const handleTemplateSlotDragLeave = (e, premId, dayOfWeek) => {
    const related = e.relatedTarget;
    if (!related || !e.currentTarget.contains(related)) {
      setTemplateSlotDragOver((prev) => prev && prev.premId === premId && prev.dayOfWeek === dayOfWeek ? null : prev);
    }
  };
  const handleTemplateSlotDrop = async (e, targetPremId, dayOfWeek) => {
    e.preventDefault();
    setTemplateSlotDragOver(null);
    const raw = e.dataTransfer.getData(TEMPLATE_SLOT_MIME);
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.width > 0 ? e.clientX - rect.left : 0;
    const hour = Math.floor((x / rect.width) * 17) + 7;
    const h = Math.max(7, Math.min(22, hour));
    const timeStart = `${String(h).padStart(2, '0')}:00`;
    const timeEnd = `${String(h + 1).padStart(2, '0')}:00`;
    if (!currentTemplate?.id) return;
    const samePremise = Number(targetPremId) === Number(payload.premise_id);
    try {
      if (samePremise) {
        await scheduleAPI.updateScheduleTemplateSlot(payload.id, { day_of_week: dayOfWeek, time_start: timeStart, time_end: timeEnd });
        logAction('template_slot_move', { slotId: payload.id, dayOfWeek, timeStart, timeEnd });
      } else {
        await scheduleAPI.createScheduleTemplateSlot(currentTemplate.id, {
          section_id: payload.section_id,
          premise_id: Number(targetPremId),
          day_of_week: dayOfWeek,
          time_start: timeStart,
          time_end: timeEnd
        });
        logAction('template_slot_copy_drop', { fromSlotId: payload.id, toPremId: targetPremId, dayOfWeek });
      }
      await loadTemplateAndSlots(currentTemplate.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка при переносе слота шаблона');
    }
  };
  const handleTemplateSlotDragEnd = () => {
    setTemplateSlotDragOver(null);
  };

  // Загружаем праздничные дни для текущего года
  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const data = await holidaysAPI.getHolidays(currentYear);
        setHolidays(new Set(data.holidays || []));
        setWeekendDays(new Set(data.weekendDays || []));
      } catch (error) {
        console.error('Error loading holidays:', error);
      }
    };
    loadHolidays();
  }, []);
  
  // Загружаем праздничные дни при изменении года
  useEffect(() => {
    const loadHolidaysForYear = async () => {
      try {
        const year = yearStartDate.getFullYear();
        const data = await holidaysAPI.getHolidays(year);
        setHolidays(new Set(data.holidays || []));
        setWeekendDays(new Set(data.weekendDays || []));
      } catch (error) {
        console.error('Error loading holidays:', error);
      }
    };
    loadHolidaysForYear();
  }, [yearStartDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const objects = await objectsAPI.getAll();
      // Преобразуем события из API формата в формат компонента
      const normalizedObjects = objects.map(obj => ({
        ...obj,
        events: (obj.events || []).map(normalizeEvent)
      }));
      setData(normalizedObjects);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки данных');
      console.error('Ошибка загрузки данных:', err);
    } finally {
      setLoading(false);
    }
  };

  // Получить массив дат для недели
  const getWeekDates = (startDate) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };
  // Сетка месяца: 6 недель по 7 дней (пн–вс), каждая ячейка — Date (для «другого» месяца cell.getMonth() !== month)
  const getMonthGrid = (firstOfMonth) => {
    const year = firstOfMonth.getFullYear();
    const month = firstOfMonth.getMonth();
    const first = new Date(year, month, 1);
    const dayOfWeek = first.getDay();
    const toMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(first);
    start.setDate(first.getDate() + toMonday);
    const grid = [];
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const cell = new Date(start);
        cell.setDate(start.getDate() + w * 7 + d);
        week.push(cell);
      }
      grid.push(week);
    }
    return grid;
  };
  // Все слоты за день (любое помещение) для режима месяц
  const getSlotsForDay = (day, slotsOverride) => {
    if (!day || !(day instanceof Date)) return [];
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    const slots = slotsOverride !== undefined ? slotsOverride : (scheduleData.slots || []);
    return slots.filter((slot) => {
      const start = new Date(slot.start_date);
      const end = new Date(slot.end_date);
      return start <= dayEnd && end >= dayStart;
    });
  };
  // Виртуальные слоты для режима «Шаблон недели»: day_of_week + time -> start_date/end_date на отображаемой неделе/дне/месяце
  const templateSlotsVirtual = React.useMemo(() => {
    if (scheduleScheduleMode !== 'template' || !templateSlots.length) return [];
    const toDateStr = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const slotToVirtual = (s, d) => {
      const dateStr = toDateStr(d);
      const tStart = (s.time_start || '08:00').toString().trim().slice(0, 8);
      const tEnd = (s.time_end || '09:00').toString().trim().slice(0, 8);
      const startDate = dateStr + 'T' + (tStart.length <= 5 ? tStart + ':00' : tStart);
      const endDate = dateStr + 'T' + (tEnd.length <= 5 ? tEnd + ':00' : tEnd);
      return { ...s, start_date: startDate, end_date: endDate };
    };
    if (scheduleViewMode === 'month') {
      const year = scheduleMonthStart.getFullYear();
      const month = scheduleMonthStart.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const result = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i);
        const dayOfWeek = d.getDay() === 0 ? 7 : d.getDay();
        templateSlots.filter((s) => s.day_of_week === dayOfWeek).forEach((s) => result.push(slotToVirtual(s, d)));
      }
      return result;
    }
    const refDate = scheduleViewMode === 'week' ? scheduleWeekStart : scheduleDay;
    const monday = getMonday(refDate);
    const dayOfWeekFilter = scheduleViewMode === 'hours' ? (scheduleDay.getDay() === 0 ? 7 : scheduleDay.getDay()) : null;
    return templateSlots
      .filter((s) => dayOfWeekFilter == null || s.day_of_week === dayOfWeekFilter)
      .map((s) => {
        const dayOffset = s.day_of_week - 1;
        const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
        d.setDate(d.getDate() + dayOffset);
        return slotToVirtual(s, d);
      });
  }, [scheduleScheduleMode, templateSlots, scheduleWeekStart, scheduleDay, scheduleViewMode, scheduleMonthStart]);
  // В режиме шаблона показываем один объект и виртуальные слоты
  const effectiveScheduleSlots = scheduleScheduleMode === 'template' ? templateSlotsVirtual : (scheduleData.slots || []);
  const effectiveScheduleObjects = scheduleScheduleMode === 'template' && templateObjectId
    ? (scheduleData.objects || []).filter((o) => o.id === templateObjectId)
    : (scheduleData.objects || []);
  const selectedScheduleObject = scheduleData.objects?.find((o) => o.id === templateObjectId) || null;
  // Рабочие часы для дневного вида: 07:00–23:00
  const getScheduleHours = () => Array.from({ length: 17 }, (_, i) => 7 + i);
  // Получить слоты помещения за выбранный день (slotsOverride: в режиме шаблона передаём виртуальные слоты)
  const getSlotsForPremiseDay = (premId, day, slotsOverride) => {
    if (!day || !(day instanceof Date) || Number.isNaN(day.getTime())) return [];
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    const premIdNum = Number(premId);
    const slots = slotsOverride !== undefined ? slotsOverride : (scheduleData.slots || []);
    return slots.filter((slot) => {
      if (Number(slot.premise_id) !== premIdNum) return false;
      const start = new Date(slot.start_date);
      const end = new Date(slot.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      return start <= dayEnd && end >= dayStart;
    });
  };
  // Получить слоты помещения за выбранный день и час — из слотов дня отфильтровать по пересечению с часом
  const getSlotsForPremiseHour = (premId, day, hour, slotsOverride) => {
    const daySlots = getSlotsForPremiseDay(premId, day, slotsOverride);
    if (!daySlots.length) return [];
    const dayDate = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const hourStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), hour, 0, 0, 0);
    const hourEnd = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), hour + 1, 0, 0, 0);
    return daySlots.filter((slot) => {
      const start = new Date(slot.start_date);
      const end = new Date(slot.end_date);
      return start < hourEnd && end > hourStart;
    });
  };
  // Слоты дня с позицией для дневного вида (рабочие часы 7–23): один слот = один блок
  const getSlotsForPremiseDayWithPosition = (premId, day, minHour = 7, maxHour = 23, slotsOverride) => {
    const daySlots = getSlotsForPremiseDay(premId, day, slotsOverride);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    return daySlots.map((slot) => {
      const start = new Date(slot.start_date);
      const end = new Date(slot.end_date);
      let startHour = start < dayStart ? 0 : start.getHours();
      let endHour = end > dayEnd ? 24 : end.getHours();
      if (endHour <= startHour) endHour = startHour + 1;
      const displayStart = Math.max(minHour, Math.min(maxHour, startHour));
      const displayEnd = Math.min(maxHour + 1, Math.max(minHour, endHour));
      if (displayEnd <= displayStart) return null;
      const span = displayEnd - displayStart;
      return { slot, startHour: displayStart, span };
    }).filter(Boolean).sort((a, b) => a.startHour - b.startHour);
  };
  // Доля часа (0–1), которую слот занимает в данной часовой ячейке — для пропорциональной отрисовки
  const getSlotSegmentInHour = (slot, day, hour) => {
    const start = new Date(slot.start_date);
    const end = new Date(slot.end_date);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const hourStart = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate(), hour, 0, 0, 0);
    const hourEnd = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate(), hour + 1, 0, 0, 0);
    if (start >= hourEnd || end <= hourStart) return null;
    const msPerHour = 60 * 60 * 1000;
    const segStart = start <= hourStart ? 0 : (start.getTime() - hourStart.getTime()) / msPerHour;
    const segEnd = end >= hourEnd ? 1 : (end.getTime() - hourStart.getTime()) / msPerHour;
    return { startFrac: segStart, endFrac: segEnd };
  };
  // Позиция слота в дне 7:00–23:00 как один блок: left% и width% от всей полосы
  const getSlotPositionInDay = (slot, day, minHour = 7, maxHour = 23) => {
    const start = new Date(slot.start_date);
    const end = new Date(slot.end_date);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), minHour, 0, 0, 0);
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), maxHour + 1, 0, 0, 0);
    const segStart = start < dayStart ? dayStart : start;
    const segEnd = end > dayEnd ? dayEnd : end;
    if (segEnd.getTime() <= segStart.getTime()) return null;
    const totalMs = (maxHour - minHour + 1) * 60 * 60 * 1000;
    const leftPct = ((segStart.getTime() - dayStart.getTime()) / totalMs) * 100;
    const widthPct = ((segEnd.getTime() - segStart.getTime()) / totalMs) * 100;
    return { leftPct, widthPct };
  };
  // Назначить пересекающимся слотам разные «дорожки» (lane 0, 1, 2, …)
  const getSlotsWithLanes = (premId, day, slotsOverride) => {
    const daySlots = getSlotsForPremiseDay(premId, day, slotsOverride);
    const sorted = [...daySlots].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const laneEnds = [];
    sorted.forEach((slot) => {
      const start = new Date(slot.start_date).getTime();
      const end = new Date(slot.end_date).getTime();
      let lane = 0;
      while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
      slot._lane = lane;
      laneEnds[lane] = end;
    });
    return sorted;
  };
  // Для обзора админа: секции расписания, по времени пересекающиеся с мероприятием в пределах недели
  const getOverlappingSectionsForEvent = (event, objectId, weekDates, startIdx, endIdx, slotsOverride, objectsOverride) => {
    const objects = objectsOverride || (scheduleData.objects || []);
    const scheduleObj = objects.find((o) => o.id === objectId);
    const premises = scheduleObj?.premises || [];
    if (!premises.length) return [];
    const { startDate, endDate } = getEventStartEndForObject(event, objectId);
    const eventStart = new Date(startDate);
    const eventEnd = new Date(endDate);
    const sectionNames = new Set();
    for (let dayIdx = startIdx; dayIdx <= endIdx; dayIdx++) {
      const dayNorm = weekDates[dayIdx];
      if (!dayNorm) continue;
      const dayStart = new Date(dayNorm.getFullYear(), dayNorm.getMonth(), dayNorm.getDate());
      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);
      const eventDayStart = eventStart < dayStart ? dayStart : eventStart;
      const eventDayEnd = eventEnd > dayEnd ? dayEnd : eventEnd;
      if (eventDayStart.getTime() >= eventDayEnd.getTime()) continue;
      premises.forEach((prem) => {
        getSlotsForPremiseDay(prem.id, dayNorm, slotsOverride).forEach((slot) => {
          const slotStart = new Date(slot.start_date);
          const slotEnd = new Date(slot.end_date);
          if (slotStart.getTime() < eventDayEnd.getTime() && slotEnd.getTime() > eventDayStart.getTime()) {
            const label = [slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' / ') || 'Секция';
            sectionNames.add(label);
          }
        });
      });
    }
    return Array.from(sectionNames);
  };
  const LANE_HEIGHT = 28;
  // Текст подсказки при наведении на слот: секция + время
  const getSlotTooltipText = (slot, forEditor = false) => {
    const parts = [];
    const sectionLine = [slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' / ');
    if (sectionLine) parts.push(sectionLine);
    const start = new Date(slot.start_date);
    const end = new Date(slot.end_date);
    const timeStr = `${start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    parts.push(timeStr);
    if (forEditor) parts.push('Двойной клик — редактирование');
    return parts.join('\n');
  };
  // Получить массив месяцев для года
  const getYearMonths = () => monthsOfYear;
  // Доля месяца, которую занимает событие (для годового вида — полоска по длительности)
  const getEventSegmentInMonth = (eventStart, eventEnd, year, monthIdx) => {
    const monthStart = new Date(year, monthIdx, 1);
    const monthEnd = new Date(year, monthIdx + 1, 0, 23, 59, 59, 999);
    const segStart = eventStart < monthStart ? monthStart : eventStart;
    const segEnd = eventEnd > monthEnd ? monthEnd : eventEnd;
    if (segEnd.getTime() <= segStart.getTime()) return null;
    const totalMs = monthEnd.getTime() - monthStart.getTime() + 1;
    const leftPct = ((segStart.getTime() - monthStart.getTime()) / totalMs) * 100;
    const widthPct = ((segEnd.getTime() - segStart.getTime()) / totalMs) * 100;
    return { leftPct, widthPct };
  };
  // Позиция мероприятия в дне (7–23) для режима «День»: left% и width% от полосы
  const getEventPositionInDay = (event, objectId, day, minHour = 7, maxHour = 23) => {
    const { startDate, endDate } = getEventStartEndForObject(event, objectId);
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), minHour, 0, 0, 0);
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), maxHour + 1, 0, 0, 0);
    const segStart = start < dayStart ? dayStart : start;
    const segEnd = end > dayEnd ? dayEnd : end;
    if (segEnd.getTime() <= segStart.getTime()) return null;
    const totalMs = (maxHour - minHour + 1) * 60 * 60 * 1000;
    const leftPct = ((segStart.getTime() - dayStart.getTime()) / totalMs) * 100;
    const widthPct = ((segEnd.getTime() - segStart.getTime()) / totalMs) * 100;
    return { leftPct, widthPct };
  };
  // События объекта в один день с позицией и дорожкой (lane) для отрисовки в режиме «День»
  const getEventsWithLanesForDay = (obj, day, minHour = 7, maxHour = 23) => {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    const eventsOnDay = (obj.events || []).filter((event) => {
      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return !(end < dayStart || start > dayEnd);
    });
    const withPos = eventsOnDay
      .map((event) => {
        const pos = getEventPositionInDay(event, obj.id, day, minHour, maxHour);
        if (!pos || pos.widthPct <= 0) return null;
        return { event, ...pos };
      })
      .filter(Boolean);
    const sorted = [...withPos].sort((a, b) => a.leftPct - b.leftPct);
    const laneEnds = [];
    sorted.forEach((item) => {
      const start = item.leftPct;
      const end = item.leftPct + item.widthPct;
      let lane = 0;
      while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
      item._lane = lane;
      laneEnds[lane] = end;
    });
    return sorted;
  };
  // По каждому дню недели — сегменты событий с позицией по времени (7–23), для отрисовки в ячейке дня
  const getWeekEventSegmentsByDay = (obj, weekDates, minHour = 7, maxHour = 23) => {
    const weekStart = new Date(weekDates[0].getFullYear(), weekDates[0].getMonth(), weekDates[0].getDate());
    const weekEnd = new Date(weekDates[6].getFullYear(), weekDates[6].getMonth(), weekDates[6].getDate());
    weekEnd.setHours(23, 59, 59, 999);
    const filtered = (obj.events || []).filter((ev) => {
      const { startDate, endDate } = getEventStartEndForObject(ev, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      return !(eventEnd < weekStart || eventStart > weekEnd);
    });
    const uniqueEvents = Array.from(new Map(filtered.map((e) => [e.id, e])).values());
    const segmentsByDay = weekDates.map(() => []);
    uniqueEvents.forEach((event) => {
      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      weekDates.forEach((dayDate, dayIdx) => {
        const dayOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        if (endDateOnly < dayOnly || startDateOnly > dayOnly) return;
        const pos = getEventPositionInDay(event, obj.id, dayDate, minHour, maxHour);
        if (!pos || pos.widthPct <= 0) return;
        segmentsByDay[dayIdx].push({ event, leftPct: pos.leftPct, widthPct: pos.widthPct });
      });
    });
    const laneEndsByDay = weekDates.map(() => []);
    segmentsByDay.forEach((segments, dayIdx) => {
      const sorted = [...segments].sort((a, b) => a.leftPct - b.leftPct);
      sorted.forEach((seg) => {
        const start = seg.leftPct;
        const end = seg.leftPct + seg.widthPct;
        let lane = 0;
        while (lane < laneEndsByDay[dayIdx].length && start < laneEndsByDay[dayIdx][lane]) lane++;
        seg._lane = lane;
        laneEndsByDay[dayIdx][lane] = end;
      });
    });
    const maxLanes = Math.max(1, ...segmentsByDay.map((s) => s.length ? Math.max(...s.map((x) => (x._lane ?? 0))) + 1 : 0));
    return { segmentsByDay, maxLanes };
  };
  // Неделя: одно событие = один непрерывный бар на полосе (Пн 7:00 – Вс 23:00), left/width в %
  const getWeekEventSegmentsUnified = (obj, weekDates, minHour = 7, maxHour = 23) => {
    const weekStart = new Date(weekDates[0].getFullYear(), weekDates[0].getMonth(), weekDates[0].getDate(), minHour, 0, 0, 0);
    const weekEnd = new Date(weekDates[6].getFullYear(), weekDates[6].getMonth(), weekDates[6].getDate(), maxHour, 0, 0, 0);
    const totalMs = weekEnd.getTime() - weekStart.getTime();
    if (totalMs <= 0) return { segments: [], maxLanes: 1 };
    const filtered = (obj.events || []).filter((ev) => {
      const { startDate, endDate } = getEventStartEndForObject(ev, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return !(end < weekStart || start > weekEnd);
    });
    const uniqueEvents = Array.from(new Map(filtered.map((e) => [e.id, e])).values());
    const segments = uniqueEvents.map((event) => {
      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const clipStart = start < weekStart ? weekStart : start;
      const clipEnd = end > weekEnd ? weekEnd : end;
      if (clipEnd.getTime() <= clipStart.getTime()) return null;
      const leftPct = ((clipStart.getTime() - weekStart.getTime()) / totalMs) * 100;
      const widthPct = ((clipEnd.getTime() - clipStart.getTime()) / totalMs) * 100;
      return { event, leftPct, widthPct };
    }).filter(Boolean);
    const sorted = [...segments].sort((a, b) => a.leftPct - b.leftPct);
    const laneEnds = [];
    sorted.forEach((seg) => {
      const start = seg.leftPct;
      const end = seg.leftPct + seg.widthPct;
      let lane = 0;
      while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
      seg._lane = lane;
      laneEnds[lane] = end;
    });
    const maxLanes = Math.max(1, segments.length ? Math.max(...segments.map((s) => (s._lane ?? 0))) + 1 : 0);
    return { segments, maxLanes };
  };
  // Месяц: одно событие = один непрерывный бар на полосе (1-е число 00:00 – последнее число 23:59), left/width в %
  const getMonthEventSegmentsUnified = (obj, monthStartDate) => {
    const year = monthStartDate.getFullYear();
    const month = monthStartDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    if (!daysInMonth || daysInMonth <= 0) return { segments: [], maxLanes: 1 };
    const monthStart = new Date(year, month, 1, 0, 0, 0, 0);
    const monthEnd = new Date(year, month, daysInMonth, 23, 59, 59, 999);
    const filtered = (obj.events || []).filter((ev) => {
      const { startDate, endDate } = getEventStartEndForObject(ev, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      return !(eventEnd < monthStart || eventStart > monthEnd);
    });
    const uniqueEvents = Array.from(new Map(filtered.map((e) => [e.id, e])).values());
    const segments = uniqueEvents.map((event) => {
      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Проверяем вхождение в месяц
      if (end < monthStart || start > monthEnd) return null;

      // Обрезаем границы события до начала/конца месяца
      const clipStart = start < monthStart ? monthStart : start;
      const clipEnd = end > monthEnd ? monthEnd : end;

      const msPerDay = 24 * 60 * 60 * 1000;
      const totalMonthMs = daysInMonth * msPerDay;

      // Время начала относительно начала месяца
      const elapsedMs = clipStart.getTime() - monthStart.getTime();
      const leftPct = (elapsedMs / totalMonthMs) * 100;

      // Длительность события
      const durationMs = clipEnd.getTime() - clipStart.getTime();
      const widthPct = (durationMs / totalMonthMs) * 100;

      // Минимум ширина 1% (~7 часов в месячном виде) для кликабельности,
      // если событие очень короткое
      const finalWidthPct = Math.max(widthPct, 0.5);

      return { event, leftPct, widthPct: finalWidthPct };
    }).filter(Boolean);
    const sorted = [...segments].sort((a, b) => a.leftPct - b.leftPct);
    const laneEnds = [];
    sorted.forEach((seg) => {
      const start = seg.leftPct;
      const end = seg.leftPct + seg.widthPct;
      let lane = 0;
      while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
      seg._lane = lane;
      laneEnds[lane] = end;
    });
    const maxLanes = Math.max(1, segments.length ? Math.max(...segments.map((s) => (s._lane ?? 0))) + 1 : 0);
    return { segments, maxLanes };
  };
  const EVENT_DAY_LANE_HEIGHT = 28;
  // Получить массив дней для месяца
  const getMonthDates = (startDate) => {
    const dates = [];
    const year = startDate.getFullYear();
    const month = startDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      dates.push(new Date(year, month, i));
    }
    return dates;
  };

  // Логика для мероприятий
  const handleAddEvent = (objIdx, date = null) => {
    setSelectedEvent(null);
    setEventModalMode('create');
    setCurrentObjectIdxForEvent(objIdx);
    setDefaultEventDate(date);
    setIsEventModalOpen(true);
  };

  const handleEditEvent = (objIdx, event) => {
    // Admin может редактировать только события своей организации
    if (userRole === 'admin' && event.organizationId && userOrgId && Number(event.organizationId) !== userOrgId) {
      alert('Вы можете редактировать только мероприятия своей организации');
      return;
    }
    setSelectedEvent(event);
    setEventModalMode('edit');
    setCurrentObjectIdxForEvent(objIdx);
    setIsEventModalOpen(true);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      console.log('[DEBUG FRONT] handleSaveEvent received eventData:', eventData);
      const obj = data[currentObjectIdxForEvent];
      const payload = denormalizeEvent(eventData);
      console.log('[DEBUG FRONT] handleSaveEvent payload after denormalize:', payload);
      if (!payload.objectIds || payload.objectIds.length === 0) {
        payload.objectIds = [obj.id];
      }

      if (eventModalMode === 'create') {
        const newEvent = await eventsAPI.create(obj.id, payload);
        const normalizedEvent = normalizeEvent({ ...newEvent, objectIds: newEvent.objectIds || payload.objectIds });
        const objectIds = newEvent.objectIds || payload.objectIds;
        setData(prev => {
          const updated = prev.map((o, idx) => {
            if (!objectIds.includes(o.id)) return o;
            return {
              ...o,
              events: [...(o.events || []), normalizedEvent]
            };
          });
          return updated;
        });
      } else if (eventModalMode === 'edit') {
        console.log('[DEBUG FRONT] handleSaveEvent: calling update with payload:', payload);
        const updatedFromServer = await eventsAPI.update(selectedEvent.id, payload);
        console.log('[DEBUG FRONT] handleSaveEvent: updatedFromServer:', updatedFromServer);
        const newObjectIds = updatedFromServer.objectIds || payload.objectIds || [];
        const normalizedUpdated = normalizeEvent(updatedFromServer);
        setData(prev => prev.map(o => ({
          ...o,
          events: newObjectIds.includes(o.id)
            ? (o.events || []).some(e => e.id === selectedEvent.id)
              ? (o.events || []).map(e => e.id === selectedEvent.id ? normalizedUpdated : e)
              : [...(o.events || []), normalizedUpdated]
            : (o.events || []).filter(e => e.id !== selectedEvent.id)
        })));
      }
      setIsEventModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка сохранения мероприятия');
      console.error('Ошибка сохранения мероприятия:', err);
    }
  };

  const handleAddObject = async () => {
    const name = prompt("Введите название нового объекта:");
    if (name && name.trim()) {
      // Для admin можно назначить руководителей (несколько)
      let managerIds = [];
      if (userRole === 'admin') {
        try {
          const users = await usersAPI.getAll();
          const managers = users.filter(u => u.role === 'manager');
          if (managers.length > 0) {
            const managerList = managers.map(m => `${m.id}: ${m.username}`).join('\n');
            const managerInput = prompt(`Введите ID руководителей через запятую (или оставьте пустым):\n\nДоступные руководители:\n${managerList}`);
            if (managerInput && managerInput.trim()) {
              const ids = managerInput.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
              const validIds = ids.filter(id => managers.find(m => m.id === id));
              if (validIds.length !== ids.length) {
                alert('Некоторые ID руководителей неверны');
                return;
              }
              managerIds = validIds;
            }
          }
        } catch (err) {
          console.error('Ошибка загрузки пользователей:', err);
        }
      }
      try {
        const newObject = await objectsAPI.create(name.trim(), managerIds.length > 0 ? managerIds : null);
        setData(prev => [...prev, { ...newObject, events: [] }]);
      } catch (err) {
        alert(err.response?.data?.error || 'Ошибка создания объекта');
        console.error('Ошибка создания объекта:', err);
      }
    }
  };

  const handleEditObject = async (objIdx) => {
    const name = prompt("Введите новое название объекта:", data[objIdx].name);
    if (name && name.trim()) {
      // Для редактирования руководителей используйте панель "Управление пользователями"
      // Здесь обновляем только название
      try {
        await objectsAPI.update(data[objIdx].id, name.trim(), null); // null = не менять руководителей
        setData(prev => {
          const updated = [...prev];
          updated[objIdx].name = name.trim();
          return updated;
        });
      } catch (err) {
        alert(err.response?.data?.error || 'Ошибка обновления объекта');
        console.error('Ошибка обновления объекта:', err);
      }
    }
  };

  const handleDeleteObject = async (objIdx) => {
    if (window.confirm("Удалить объект и все его мероприятия?")) {
      try {
        await objectsAPI.delete(data[objIdx].id);
        setData(prev => prev.filter((_, idx) => idx !== objIdx));
      } catch (err) {
        alert(err.response?.data?.error || 'Ошибка удаления объекта');
        console.error('Ошибка удаления объекта:', err);
      }
    }
  };

  const handleDeleteEvent = async (objIdx, eventId) => {
    // Admin может удалять только события своей организации
    const event = data[objIdx]?.events?.find(e => e.id === eventId);
    if (userRole === 'admin' && event?.organizationId && userOrgId && Number(event.organizationId) !== userOrgId) {
      alert('Вы можете удалять только мероприятия своей организации');
      return;
    }
    if (window.confirm("Удалить мероприятие?")) {
      try {
        await eventsAPI.delete(eventId);
        setData(prev => prev.map(o => ({
          ...o,
          events: (o.events || []).filter(e => e.id !== eventId)
        })));
      } catch (err) {
        alert(err.response?.data?.error || 'Ошибка удаления мероприятия');
        console.error('Ошибка удаления мероприятия:', err);
      }
    }
  };

  // Обработчики drag and drop
  const handleDragStart = (e, event, objIdx) => {
    // Drag разрешаем только после удержания ЛКМ (arming)
    if (dragArmedEventId !== event.id) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Admin может перетаскивать только события своей организации
    if (userRole === 'admin' && event.organizationId && userOrgId && Number(event.organizationId) !== userOrgId) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    console.log('🟢 Drag Start:', { eventName: event.name, eventId: event.id, objIdx });
    setIsDragging(true);
    setDraggedEvent({ event, objIdx });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Для Firefox
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    const wasDragged = isDragging;
    console.log('🔴 Drag End', { wasDragged, hadDragOver: !!dragOverCell });
    e.currentTarget.style.opacity = '1';
    // Небольшая задержка перед сбросом флага, чтобы onClick не сработал
    setTimeout(() => {
      setIsDragging(false);
      setDraggedEvent(null);
      setDragOverCell(null);
      setDragArmedEventId(null);
    }, 300);
    
    // Если был реальный drag, предотвращаем onClick
    if (wasDragged) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const armDrag = (eventId) => {
    if (dragArmTimerRef.current) clearTimeout(dragArmTimerRef.current);
    dragArmTimerRef.current = setTimeout(() => {
      setDragArmedEventId(eventId);
    }, 200);
  };

  const disarmDrag = () => {
    if (dragArmTimerRef.current) {
      clearTimeout(dragArmTimerRef.current);
      dragArmTimerRef.current = null;
    }
    // Если реально тянем, сбросим после dragEnd (чтобы не ронять drag)
    if (!isDragging) setDragArmedEventId(null);
  };

  const handleDragOver = (e, targetDate, objIdx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dateStr = targetDate.toLocaleDateString();
    if (!dragOverCell || dragOverCell.date.getTime() !== targetDate.getTime()) {
      console.log('🟡 Drag Over:', { date: dateStr, objIdx });
    }
    setDragOverCell({ date: targetDate, objIdx });
  };

  const handleDragLeave = () => {
    console.log('🟠 Drag Leave');
    setDragOverCell(null);
  };

  // Touch события для мобильных устройств
  const handleTouchStart = (e, event, objIdx) => {
    if (e.touches.length !== 1) return; // Только одно касание
    // Admin может перетаскивать только события своей организации
    if (userRole === 'admin' && event.organizationId && userOrgId && Number(event.organizationId) !== userOrgId) {
      return;
    }
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    
    // Армируем drag через таймер (как для мыши)
    armDrag(event.id);
    
    // Сохраняем состояние для touch drag
    setTouchDragState({
      event,
      objIdx,
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now()
    });
    
    e.preventDefault(); // Предотвращаем скролл
  };

  const handleTouchMove = (e) => {
    if (!touchDragState || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);
    
    // Если движение достаточно большое (больше 10px), начинаем drag
    if ((deltaX > 10 || deltaY > 10) && dragArmedEventId === touchDragState.event.id) {
      if (!isDragging) {
        setIsDragging(true);
        setDraggedEvent({ event: touchDragState.event, objIdx: touchDragState.objIdx });
        console.log('📱 Touch Drag Start:', { eventName: touchDragState.event.name });
      }
      
      // Находим элемент под пальцем
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      if (elementBelow) {
        // Ищем родительскую ячейку календаря
        const cell = elementBelow.closest('td.calendar-cell');
        if (cell) {
          // Получаем данные из data-атрибутов
          const dateStr = cell.getAttribute('data-date');
          const objIdxStr = cell.getAttribute('data-obj-idx');
          
          if (dateStr && objIdxStr !== null) {
            const targetDate = new Date(dateStr);
            const targetObjIdx = parseInt(objIdxStr, 10);
            
            // Обновляем dragOverCell для визуальной обратной связи
            if (!dragOverCell || dragOverCell.date.getTime() !== targetDate.getTime() || dragOverCell.objIdx !== targetObjIdx) {
              setDragOverCell({ date: targetDate, objIdx: targetObjIdx });
            }
          }
        }
      }
    }
    
    e.preventDefault();
  };

  const handleTouchEnd = async (e) => {
    if (!touchDragState) {
      disarmDrag();
      return;
    }
    
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);
    
    // Если было движение и drag активен, пытаемся выполнить drop
    if (isDragging && draggedEvent && (deltaX > 10 || deltaY > 10)) {
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (elementBelow) {
        const cell = elementBelow.closest('td.calendar-cell');
        if (cell) {
          const dateStr = cell.getAttribute('data-date');
          const objIdxStr = cell.getAttribute('data-obj-idx');
          
          if (dateStr && objIdxStr !== null) {
            const targetDate = new Date(dateStr);
            const targetObjIdx = parseInt(objIdxStr, 10);
            
            // Выполняем drop
            console.log('📱 Touch Drop:', { targetDate: targetDate.toLocaleDateString(), targetObjIdx });
            await handleDrop(e, targetDate, targetObjIdx);
          }
        }
      }
    } else if (deltaX < 5 && deltaY < 5) {
      // Если движение было минимальным, это клик - открываем модалку редактирования
      handleEditEvent(touchDragState.objIdx, touchDragState.event);
    }
    
    // Сбрасываем состояние
    setTouchDragState(null);
    touchStartPosRef.current = null;
    disarmDrag();
    setIsDragging(false);
    setDraggedEvent(null);
    setDragOverCell(null);
    
    e.preventDefault();
  };

  const handleDrop = async (e, targetDate, targetObjIdx) => {
    e.preventDefault();
    setDragOverCell(null);
    
    if (!draggedEvent) {
      console.log('❌ Drop: нет draggedEvent');
      return;
    }
    
    const { event, objIdx: sourceObjIdx } = draggedEvent;
    const sourceObject = data[sourceObjIdx];
    const targetObject = data[targetObjIdx];
    let objectIds = Array.isArray(event.objectIds) ? event.objectIds : [];
    if (objectIds.length === 0) {
      objectIds = [sourceObject.id];
    }
    const objectDates = event.objectDates && typeof event.objectDates === 'object' ? event.objectDates : {};

    const originalStart = new Date(event.startDate);
    const originalEnd = new Date(event.endDate);
    const duration = originalEnd.getTime() - originalStart.getTime();
    const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const originalStartOnly = new Date(originalStart.getFullYear(), originalStart.getMonth(), originalStart.getDate());
    const daysDiff = Math.round((targetDateOnly.getTime() - originalStartOnly.getTime()) / (1000 * 60 * 60 * 24));

    const newStart = new Date(originalStart);
    newStart.setDate(newStart.getDate() + daysDiff);
    const newEnd = new Date(originalStart.getTime() + duration);
    newEnd.setDate(newEnd.getDate() + daysDiff);
    const newStartISO = newStart.toISOString();
    const newEndISO = newEnd.toISOString();

    const getObjectDatesForOid = (oid) => {
      const d = objectDates[oid] || objectDates[String(oid)];
      return d && (d.startDate || d.endDate)
        ? { startDate: d.startDate || event.startDate, endDate: d.endDate || event.endDate }
        : { startDate: event.startDate, endDate: event.endDate };
    };

    let newObjectIds;
    let updatedObjectDates;

    if (sourceObjIdx === targetObjIdx) {
      newObjectIds = [...objectIds];
      updatedObjectDates = {};
      objectIds.forEach((oid) => {
        const key = typeof oid === 'number' ? oid : parseInt(oid, 10);
        if (Number(oid) === sourceObject.id) {
          updatedObjectDates[key] = { startDate: newStartISO, endDate: newEndISO };
        } else {
          const cur = getObjectDatesForOid(oid);
          updatedObjectDates[key] = { startDate: cur.startDate, endDate: cur.endDate };
        }
      });
    } else {
      const targetAlreadyHasEvent = objectIds.some((id) => Number(id) === targetObject.id);
      if (targetAlreadyHasEvent) {
        newObjectIds = [...objectIds];
        updatedObjectDates = {};
        objectIds.forEach((oid) => {
          const key = typeof oid === 'number' ? oid : parseInt(oid, 10);
          if (Number(oid) === targetObject.id) {
            updatedObjectDates[key] = { startDate: newStartISO, endDate: newEndISO };
          } else {
            const cur = getObjectDatesForOid(oid);
            updatedObjectDates[key] = { startDate: cur.startDate, endDate: cur.endDate };
          }
        });
      } else {
        newObjectIds = objectIds.filter((id) => Number(id) !== sourceObject.id);
        newObjectIds.push(targetObject.id);
        updatedObjectDates = {};
        newObjectIds.forEach((oid) => {
          const key = typeof oid === 'number' ? oid : parseInt(oid, 10);
          if (Number(oid) === targetObject.id) {
            updatedObjectDates[key] = { startDate: newStartISO, endDate: newEndISO };
          } else {
            const cur = getObjectDatesForOid(oid);
            updatedObjectDates[key] = { startDate: cur.startDate, endDate: cur.endDate };
          }
        });
      }
    }

    const updatedEvent = {
      ...event,
      startDate: newStartISO,
      endDate: newEndISO,
      objectIds: newObjectIds,
      objectDates: updatedObjectDates
    };

    const previousData = [...data];

    setData((prev) => {
      return prev.map((obj) => {
        const objId = obj.id;
        const hasEvent = (obj.events || []).some((ev) => ev.id === event.id);
        const shouldHaveEvent = newObjectIds.includes(objId);

        if (!hasEvent && !shouldHaveEvent) return obj;
        if (hasEvent && !shouldHaveEvent) {
          return { ...obj, events: (obj.events || []).filter((ev) => ev.id !== event.id) };
        }
        if (!hasEvent && shouldHaveEvent) {
          return {
            ...obj,
            events: [...(obj.events || []), normalizeEvent({ ...updatedEvent, id: event.id })]
          };
        }
        return {
          ...obj,
          events: (obj.events || []).map((ev) =>
            ev.id === event.id ? normalizeEvent({ ...updatedEvent, id: event.id }) : ev
          )
        };
      });
    });

    setDraggedEvent(null);

    try {
      const updatePayload = {
        name: event.name,
        startDate: newStartISO,
        endDate: newEndISO,
        description: event.description || '',
  status: (event.status === 'approved' || event.status === 'cancelled') ? event.status : 'not_approved',
        objectIds: newObjectIds,
        objectDates: updatedObjectDates
      };
      if (event.organizationId != null && event.organizationId !== '') updatePayload.organizationId = event.organizationId;
      if (event.sportTypeId != null && event.sportTypeId !== '') updatePayload.sportTypeId = event.sportTypeId;
      if (event.cloudUrl != null && event.cloudUrl !== '') updatePayload.cloudUrl = event.cloudUrl;
      await eventsAPI.update(event.id, updatePayload);
    } catch (err) {
      console.error('❌ API request failed:', err);
      setData(previousData);
      alert(err.response?.data?.error || 'Ошибка перемещения мероприятия');
    }
  };

  // Функция для перехода к месяцу из года
  const handleMonthClick = (monthIdx) => {
    setCalendarMode('month');
    setMonthStartDate(new Date(yearStartDate.getFullYear(), monthIdx, 1));
  };
  // Функция для перехода к неделе из месяца
  const handleDayClick = (date) => {
    // Найти понедельник недели, в которую входит date
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 - воскресенье
    const monday = new Date(date);
    monday.setDate(date.getDate() - dayOfWeek);
    setCalendarMode('week');
    setWeekStartDate(monday);
  };

  // Добавляю useEffect для сброса выделения при клике вне таблицы
  useEffect(() => {
    const handleClick = () => setSelectedCell(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  
  // Функция для определения выходных и праздничных дней
  function getDayClasses(date) {
    const classes = [];
    
    // Форматируем дату для проверки
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // Проверяем выходные дни из API
    if (weekendDays.has(dateStr)) {
      classes.push('weekend');
    }
    
    // Проверяем праздничные дни из API
    if (holidays.has(dateStr)) {
      classes.push('holiday');
    }
    
    return classes.join(' ');
  }

  if (loading) {
    return (
      <div className="sports-schedule-table-wrapper">
        <div style={{ padding: '2rem', textAlign: 'center' }}>Загрузка данных...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-schedule-table-wrapper">
        <div style={{ padding: '2rem', textAlign: 'center', color: '#d32f2f' }}>
          <p>Ошибка: {error}</p>
          <button onClick={loadData} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-schedule-table-wrapper">
      {/* Вкладки: Обзор — для manager и admin (при SHOW_SCHEDULE); далее Мероприятия, Расписание, Справочники (только admin) */}
      <div className="tabs">
        {showOverviewTab && (
          <button
            className={activeTab === 'overview' ? 'tab active' : 'tab'}
            onClick={() => { logAction('tab', 'overview'); setActiveTab('overview'); }}
          >
            Обзор
          </button>
        )}
        <button
          className={activeTab === 'events' ? 'tab active' : 'tab'}
          onClick={() => { logAction('tab', 'events'); setActiveTab('events'); }}
        >
          Мероприятия
        </button>
        {SHOW_SCHEDULE && (userRole === 'admin' || userRole === 'manager' || userRole === 'superuser') && (
          <button
            className={activeTab === 'schedule' ? 'tab active' : 'tab'}
            onClick={() => { logAction('tab', 'schedule'); setActiveTab('schedule'); }}
          >
            Расписание
          </button>
        )}
        {userRole === 'superuser' && (
          <button
            className={activeTab === 'references' ? 'tab active' : 'tab'}
            onClick={() => { logAction('tab', 'references'); setActiveTab('references'); }}
          >
            Справочники
          </button>
        )}
      </div>

      {/* Вкладка «Справочники»: организации → виды спорта → тренеры */}
      {activeTab === 'references' && (
        <div className="schedule-tab-content ref-tab-content">
          {refLoading && <p className="schedule-loading">Загрузка...</p>}
          {!refLoading && (
            <div className="ref-layout">
              <div className="ref-block">
                <h3 className="ref-block-title">Организации</h3>
                {userRole !== 'user' && (
                  <button type="button" className="add-btn small" onClick={() => setRefModal({ type: 'org', name: '' })}>+ Добавить</button>
                )}
                <ul className="ref-list">
                  {refOrganizations.map((org) => (
                    <li
                      key={org.id}
                      className={`ref-list-item ${selectedRefOrgId === org.id ? 'selected' : ''}`}
                      onClick={() => setSelectedRefOrgId(org.id)}
                    >
                      <span className="ref-list-name">{org.name}</span>
                      {userRole !== 'user' && (
                        <span className="ref-list-actions">
                          <button type="button" className="icon-btn ref-edit-btn" title="Изменить" onClick={(e) => { e.stopPropagation(); setRefModal({ type: 'org', editId: org.id, name: org.name }); }}>✎</button>
                          <button type="button" className="icon-btn ref-delete-btn" title="Удалить" onClick={(e) => { e.stopPropagation(); if (window.confirm('Удалить организацию? Виды спорта и тренеры тоже удалятся.')) referencesAPI.deleteOrganization(org.id).then(loadRefOrganizations).then(() => setSelectedRefOrgId(null)); }}>✕</button>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {refOrganizations.length === 0 && <p className="ref-empty">Нет организаций</p>}
              </div>
              <div className="ref-block">
                <h3 className="ref-block-title">Виды спорта</h3>
                {selectedRefOrgId ? (
                  <>
                    {userRole !== 'user' && (
                      <button type="button" className="add-btn small" onClick={() => setRefModal({ type: 'sport', name: '' })}>+ Добавить</button>
                    )}
                    <ul className="ref-list">
                      {refSportTypes.map((st) => (
                        <li
                          key={st.id}
                          className={`ref-list-item ${selectedRefSportTypeId === st.id ? 'selected' : ''}`}
                          onClick={() => setSelectedRefSportTypeId(st.id)}
                        >
                          <span className="ref-list-name">{st.name}</span>
                          {userRole !== 'user' && (
                            <span className="ref-list-actions">
                              <button type="button" className="icon-btn ref-edit-btn" title="Изменить" onClick={(e) => { e.stopPropagation(); setRefModal({ type: 'sport', editId: st.id, name: st.name }); }}>✎</button>
                              <button type="button" className="icon-btn ref-delete-btn" title="Удалить" onClick={(e) => { e.stopPropagation(); if (window.confirm('Удалить вид спорта? Тренеры тоже удалятся.')) referencesAPI.deleteSportType(st.id).then(() => { referencesAPI.getSportTypes(selectedRefOrgId).then(setRefSportTypes); setSelectedRefSportTypeId(null); }); }}>✕</button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {refSportTypes.length === 0 && <p className="ref-empty">Нет видов спорта</p>}
                  </>
                ) : (
                  <p className="ref-empty">Выберите организацию</p>
                )}
              </div>
              <div className="ref-block">
                <h3 className="ref-block-title">Тренеры</h3>
                {selectedRefSportTypeId ? (
                  <>
                    {userRole !== 'user' && (
                      <button type="button" className="add-btn small" onClick={() => setRefModal({ type: 'coach', name: '' })}>+ Добавить</button>
                    )}
                    <ul className="ref-list">
                      {refCoaches.map((c) => (
                        <li key={c.id} className="ref-list-item">
                          <span className="ref-list-name">{c.name}</span>
                          {userRole !== 'user' && (
                            <span className="ref-list-actions">
                              <button type="button" className="icon-btn ref-edit-btn" title="Изменить" onClick={() => setRefModal({ type: 'coach', editId: c.id, name: c.name })}>✎</button>
                              <button type="button" className="icon-btn ref-delete-btn" title="Удалить" onClick={() => { if (window.confirm('Удалить тренера?')) referencesAPI.deleteCoach(c.id).then(() => referencesAPI.getCoaches(selectedRefSportTypeId).then(setRefCoaches)); }}>✕</button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {refCoaches.length === 0 && <p className="ref-empty">Нет тренеров</p>}
                  </>
                ) : (
                  <p className="ref-empty">Выберите вид спорта</p>
                )}
              </div>
            </div>
          )}
          {/* Модалка добавления/редактирования справочника */}
          {refModal && (
            <div className="modal-overlay" onClick={() => setRefModal(null)}>
              <div className="modal-content schedule-modal ref-modal" onClick={(e) => e.stopPropagation()}>
                <h2>
                  {refModal.type === 'org' && (refModal.editId ? 'Изменить организацию' : 'Добавить организацию')}
                  {refModal.type === 'sport' && (refModal.editId ? 'Изменить вид спорта' : 'Добавить вид спорта')}
                  {refModal.type === 'coach' && (refModal.editId ? 'Изменить тренера' : 'Добавить тренера')}
                </h2>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const name = (e.target.querySelector('input[name="refName"]').value || '').trim();
                  if (!name) return;
                  try {
                    if (refModal.type === 'org') {
                      if (refModal.editId) await referencesAPI.updateOrganization(refModal.editId, name);
                      else await referencesAPI.createOrganization(name);
                      await loadRefOrganizations();
                    } else if (refModal.type === 'sport') {
                      if (refModal.editId) await referencesAPI.updateSportType(refModal.editId, name);
                      else await referencesAPI.createSportType(selectedRefOrgId, name);
                      const list = await referencesAPI.getSportTypes(selectedRefOrgId);
                      setRefSportTypes(list);
                    } else if (refModal.type === 'coach') {
                      if (refModal.editId) await referencesAPI.updateCoach(refModal.editId, name);
                      else await referencesAPI.createCoach(selectedRefSportTypeId, name);
                      const list = await referencesAPI.getCoaches(selectedRefSportTypeId);
                      setRefCoaches(list);
                    }
                    setRefModal(null);
                  } catch (err) {
                    alert(err.response?.data?.error || 'Ошибка сохранения');
                  }
                }}
                >
                  <div className="form-group">
                    <label>
                      {refModal.type === 'org' && 'Название организации'}
                      {refModal.type === 'sport' && 'Вид спорта'}
                      {refModal.type === 'coach' && 'ФИО тренера'}
                    </label>
                    <input name="refName" type="text" defaultValue={refModal.name} placeholder={refModal.type === 'org' ? 'СШОР' : refModal.type === 'sport' ? 'Бокс, Дзюдо' : 'Иванов И.И.'} required />
                  </div>
                  <div className="modal-buttons">
                    <button type="submit" className="save-button">Сохранить</button>
                    <button type="button" className="cancel-button" onClick={() => setRefModal(null)}>Отмена</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Обзор: для manager — два блока (мероприятия + расписание); для admin — вариант B: объект | ячейка (полоса мероприятий + полоса расписания) */}
      {activeTab === 'overview' && showOverviewTab && (
        <div className="overview-tab-content">
          {isManagerWithSchedule && (
          <>
          <div className="overview-modes" style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, marginRight: 4 }}>Период:</span>
            <button type="button" className={`mode-btn ${overviewViewMode === 'week' ? 'active' : ''}`} onClick={() => setOverviewViewMode('week')}>Неделя</button>
            <button type="button" className={`mode-btn ${overviewViewMode === 'month' ? 'active' : ''}`} onClick={() => setOverviewViewMode('month')}>Месяц</button>
            <button type="button" className={`mode-btn ${overviewViewMode === 'year' ? 'active' : ''}`} onClick={() => setOverviewViewMode('year')}>Год</button>
          </div>

          {overviewViewMode === 'week' && (
          <>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Мероприятия</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('events')}>
                Перейти к мероприятиям →
              </button>
            </div>
            <div className="overview-section-scroll">
              <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                <button className="nav-button" onClick={() => { const p = new Date(weekStartDate); p.setDate(p.getDate() - 7); setWeekStartDate(p); }}>← Неделя</button>
                <h3 className="calendar-navigation-title">{monthsOfYear[weekStartDate.getMonth()]} {weekStartDate.getFullYear()}</h3>
                <button className="nav-button" onClick={() => { const n = new Date(weekStartDate); n.setDate(n.getDate() + 7); setWeekStartDate(n); }}>Вперед →</button>
              </div>
              <table className="sports-schedule-table calendar-view week-view overview-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Объект</th>
                    {getWeekDates(weekStartDate).map((date, idx) => (
                      <th key={idx} className={getDayClasses(date)}>{daysOfWeek[idx]}<br/>{date.toLocaleDateString()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 5).map((obj, objIdx) => {
                    const weekDates = getWeekDates(weekStartDate);
                    const weekStart = new Date(weekDates[0].getFullYear(), weekDates[0].getMonth(), weekDates[0].getDate());
                    const weekEnd = new Date(weekDates[6].getFullYear(), weekDates[6].getMonth(), weekDates[6].getDate());
                    weekEnd.setHours(23, 59, 59, 999);
                    const filteredEvents = (obj.events || []).filter(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      return !(eventEnd < weekStart || eventStart > weekEnd);
                    });
                    const uniqueEventsMap = new Map();
                    filteredEvents.forEach(event => { if (!uniqueEventsMap.has(event.id)) uniqueEventsMap.set(event.id, event); });
                    const events = Array.from(uniqueEventsMap.values());
                    let tracks = [];
                    events.forEach(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const eventForCell = { ...event, startDate, endDate };
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      let startIdx = weekDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() === startDateOnly.getTime());
                      if (startIdx === -1) startIdx = startDateOnly < weekStart ? 0 : weekDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) >= startDateOnly);
                      if (startIdx === -1) return;
                      let endIdx = weekDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() === endDateOnly.getTime());
                      if (endIdx === -1) endIdx = endDateOnly > weekEnd ? weekDates.length - 1 : weekDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) > endDateOnly) - 1;
                      if (endIdx === -2 || endIdx < 0) endIdx = weekDates.length - 1;
                      if (endIdx < startIdx) endIdx = startIdx;
                      let placed = false;
                      for (let t = 0; t < tracks.length; t++) {
                        let busy = false;
                        for (let e of tracks[t]) {
                          if (!(endIdx < e.startIdx || startIdx > e.endIdx)) { busy = true; break; }
                        }
                        if (!busy) { tracks[t].push({ event: eventForCell, startIdx, endIdx }); placed = true; break; }
                      }
                      if (!placed) tracks.push([{ event: eventForCell, startIdx, endIdx }]);
                    });
                    if (tracks.length === 0) tracks = [[]];
                    const numTracks = Math.max(tracks.length, 1);
                    return tracks.map((track, trackIdx) => {
                      const uniqueEvents = [];
                      const seen = new Set();
                      track.forEach(e => { if (!seen.has(e.event.id)) { seen.add(e.event.id); uniqueEvents.push(e); } });
                      return (
                        <tr key={obj.id + '-track-' + trackIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                          {trackIdx === 0 ? (
                            <td rowSpan={numTracks} className="sticky-col">
                              <div className="object-name">{obj.name}</div>
                            </td>
                          ) : null}
                          {weekDates.map((date, dayIdx) => {
                            const cellEvent = uniqueEvents.find(e => e.startIdx === dayIdx);
                            if (cellEvent) {
                              const span = cellEvent.endIdx - cellEvent.startIdx + 1;
                              const isEditableByAdmin = userRole !== 'admin' || !cellEvent.event.organizationId || !userOrgId || Number(cellEvent.event.organizationId) === userOrgId;
                              return (
                                <td key={dayIdx} colSpan={span} className={`calendar-cell ${getDayClasses(date)}`}>
                                  <div className={`calendar-event ${!isEditableByAdmin ? 'event-readonly' : ''} ${getEventStatusClasses(cellEvent.event)}`} style={{
                                    background: getEventColor(cellEvent.event.id, cellEvent.event.name, cellEvent.event.status),
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 6px',
                                    minWidth: 0,
                                    minHeight: '24px',
                                    opacity: cellEvent.event.status === 'cancelled' ? 0.7 : (!isEditableByAdmin ? 0.6 : 1),
                                    textDecoration: cellEvent.event.status === 'cancelled' ? 'line-through' : 'none',
                                    color: cellEvent.event.status === 'cancelled' ? '#666' : undefined,
                                    cursor: !isEditableByAdmin ? 'not-allowed' : 'grab'
                                  }}>
                                    <div className="event-title" style={{ flex: 1, paddingRight: cellEvent.event.cloudUrl ? '24px' : 0 }}>{cellEvent.event.name}</div>
                                    {cellEvent.event.cloudUrl && (
                                      <a href={cellEvent.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" onClick={(e) => e.stopPropagation()}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                      </a>
                                    )}
                                  </div>
                                </td>
                              );
                            }
                            if (uniqueEvents.some(e => dayIdx > e.startIdx && dayIdx <= e.endIdx)) return null;
                            return <td key={dayIdx} className={`calendar-cell ${getDayClasses(date)}`} />;
                          })}
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </section>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Расписание</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('schedule')}>
                Перейти к расписанию →
              </button>
            </div>
            <div className="overview-section-scroll">
              {scheduleLoading ? (
                <p className="schedule-loading">Загрузка расписания...</p>
              ) : scheduleError ? (
                <p className="schedule-error" style={{ color: '#c62828' }}>{scheduleError}</p>
              ) : (
                <>
                  <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                    <button className="nav-button" onClick={() => { const p = new Date(scheduleWeekStart); p.setDate(p.getDate() - 7); setScheduleWeekStart(p); }}>← Неделя</button>
                    <h3 className="calendar-navigation-title">{monthsOfYear[scheduleWeekStart.getMonth()]} {scheduleWeekStart.getFullYear()}</h3>
                    <button className="nav-button" onClick={() => { const n = new Date(scheduleWeekStart); n.setDate(n.getDate() + 7); setScheduleWeekStart(n); }}>Неделя →</button>
                  </div>
                  <table className="sports-schedule-table calendar-view week-view schedule-view overview-table">
                    <thead>
                      <tr>
                        <th className="sticky-col">Объект / Помещение</th>
                        {getWeekDates(scheduleWeekStart).map((date, idx) => (
                          <th key={idx} className={getDayClasses(date)}>{daysOfWeek[idx]}<br />{date.toLocaleDateString()}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(scheduleData.objects || []).slice(0, 3).map((obj) => (
                        <React.Fragment key={obj.id}>
                          {(!obj.premises || obj.premises.length === 0) ? (
                            <tr><td className="sticky-col">{obj.name}</td><td colSpan={7} className="schedule-no-data">Нет помещений</td></tr>
                          ) : (
                            <>
                              <tr><td colSpan={8} className="sticky-col" style={{ background: '#e8f2f5', fontWeight: 600, fontSize: 12 }}>{obj.name}</td></tr>
                              {obj.premises.map((prem) => {
                              const daySlots = getSlotsForPremiseDay(prem.id, getWeekDates(scheduleWeekStart)[0], scheduleData.slots);
                              const hasSlots = daySlots.length > 0 || getWeekDates(scheduleWeekStart).some((d) => getSlotsForPremiseDay(prem.id, d, scheduleData.slots).length > 0);
                              return (
                                <tr key={prem.id}>
                                  <td className="sticky-col schedule-cell-premise"><span className="schedule-premise-name">{prem.name}</span></td>
                                  {getWeekDates(scheduleWeekStart).map((date) => {
                                    const slots = getSlotsWithLanes(prem.id, date, scheduleData.slots);
                                    const numLanes = slots.length ? Math.max(...slots.map((s) => (s._lane ?? 0))) + 1 : 1;
                                    const dayNorm = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                    return (
                                      <td key={date.getTime()} className="schedule-day-track-cell" style={{ padding: 0, verticalAlign: 'middle', minHeight: 8 + numLanes * 28 }}>
                                        <div style={{ position: 'relative', width: '100%', height: 8 + numLanes * 28, minHeight: 8 + numLanes * 28 }}>
                                          {slots.map((slot) => {
                                            const pos = getSlotPositionInDay(slot, dayNorm);
                                            if (!pos || pos.widthPct <= 0) return null;
                                            const lane = slot._lane ?? 0;
                                            return (
                                              <div key={slot.id} className="schedule-slot calendar-event schedule-slot-cross" style={{ position: 'absolute', left: `${pos.leftPct}%`, width: `${pos.widthPct}%`, top: 4 + lane * 28 + 4, height: 28 - 8, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '0 6px', fontSize: 11, background: '#e3f2fd', border: '1px solid #90caf9' }}>
                                                <span className="schedule-slot-text">{[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
          </>
          )}

          {overviewViewMode === 'month' && (
          <>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Мероприятия</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('events')}>Перейти к мероприятиям →</button>
            </div>
            <div className="overview-section-scroll">
              <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                <button className="nav-button" onClick={() => { const p = new Date(monthStartDate); p.setMonth(p.getMonth() - 1); setMonthStartDate(p); }}>← Назад</button>
                <h3 className="calendar-navigation-title">{monthsOfYear[monthStartDate.getMonth()]} {monthStartDate.getFullYear()}</h3>
                <button className="nav-button" onClick={() => { const n = new Date(monthStartDate); n.setMonth(n.getMonth() + 1); setMonthStartDate(n); }}>Вперед →</button>
              </div>
              <table className="sports-schedule-table calendar-view month-view overview-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Объект</th>
                    {getMonthDates(monthStartDate).map((date, idx) => {
                      const dayOfWeek = date.getDay();
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      const weekendLabel = dayOfWeek === 6 ? 'сб' : dayOfWeek === 0 ? 'вс' : '';
                      return (
                        <th key={idx} className={getDayClasses(date)}>
                          <div className="month-day-header">{date.getDate()}{isWeekend && <span className="weekend-label">{weekendLabel}</span>}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 5).map((obj, objIdx) => {
                    const monthDates = getMonthDates(monthStartDate);
                    const monthStart = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth(), 1);
                    const monthEnd = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth() + 1, 0);
                    monthEnd.setHours(23, 59, 59, 999);
                    const filteredEvents = (obj.events || []).filter(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      return !(eventEnd < monthStart || eventStart > monthEnd);
                    });
                    const uniqueEventsMap = new Map();
                    filteredEvents.forEach(event => { if (!uniqueEventsMap.has(event.id)) uniqueEventsMap.set(event.id, event); });
                    const events = Array.from(uniqueEventsMap.values());
                    events.sort((a, b) => {
                      const { startDate: sa, endDate: ea } = getEventStartEndForObject(a, obj.id);
                      const { startDate: sb, endDate: eb } = getEventStartEndForObject(b, obj.id);
                      if (new Date(sa).getTime() !== new Date(sb).getTime()) return new Date(sa).getTime() - new Date(sb).getTime();
                      return new Date(ea).getTime() - new Date(eb).getTime();
                    });
                    let tracks = [];
                    const lastDayIdx = monthDates.length - 1;
                    events.forEach(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const eventForCell = { ...event, startDate, endDate };
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      if (startDateOnly > monthEnd || endDateOnly < monthStart) return;
                      let startIdx = startDateOnly <= monthStart ? 0 : startDateOnly.getDate() - 1;
                      if (startIdx > lastDayIdx) return;
                      let endIdx = endDateOnly >= monthEnd ? lastDayIdx : endDateOnly.getDate() - 1;
                      if (endIdx < 0) return;
                      if (endIdx < startIdx) endIdx = startIdx;
                      let placed = false;
                      for (let t = 0; t < tracks.length; t++) {
                        let busy = false;
                        for (let e of tracks[t]) {
                          if (!(endIdx < e.startIdx || startIdx > e.endIdx)) { busy = true; break; }
                        }
                        if (!busy) { tracks[t].push({ event: eventForCell, startIdx, endIdx }); placed = true; break; }
                      }
                      if (!placed) tracks.push([{ event: eventForCell, startIdx, endIdx }]);
                    });
                    if (tracks.length === 0) tracks = [[]];
                    const numTracks = Math.max(tracks.length, 1);
                    return tracks.map((track, trackIdx) => {
                      const uniqueEvents = [];
                      const seen = new Set();
                      track.forEach(e => { if (!seen.has(e.event.id)) { seen.add(e.event.id); uniqueEvents.push(e); } });
                      return (
                        <tr key={obj.id + '-track-' + trackIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                          {trackIdx === 0 ? <td rowSpan={numTracks} className="sticky-col"><div className="object-name">{obj.name}</div></td> : null}
                          {monthDates.map((date, dayIdx) => {
                            const cellEvent = uniqueEvents.find(e => e.startIdx === dayIdx);
                            if (cellEvent) {
                              const span = cellEvent.endIdx - cellEvent.startIdx + 1;
                              const isEditableByAdmin = userRole !== 'admin' || !cellEvent.event.organizationId || !userOrgId || Number(cellEvent.event.organizationId) === userOrgId;
                              return (
                                <td key={dayIdx} colSpan={span} className={`calendar-cell ${getDayClasses(date)}`}>
                                  <div className={`calendar-event ${!isEditableByAdmin ? 'event-readonly' : ''} ${getEventStatusClasses(cellEvent.event)}`} style={{ background: getEventColor(cellEvent.event.id, cellEvent.event.name, cellEvent.event.status), display: 'flex', alignItems: 'center', padding: '0 6px', minWidth: 0, minHeight: '20px', opacity: !isEditableByAdmin ? 0.6 : 1, cursor: !isEditableByAdmin ? 'not-allowed' : 'grab' }}>
                                    <div className="event-title" style={{ flex: 1, paddingRight: cellEvent.event.cloudUrl ? '20px' : 0 }}>{cellEvent.event.name}</div>
                                    {cellEvent.event.cloudUrl && (
                                      <a href={cellEvent.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" style={{ width: 16, height: 16 }} onClick={(e) => e.stopPropagation()}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                      </a>
                                    )}
                                  </div>
                                </td>
                              );
                            }
                            if (uniqueEvents.some(e => dayIdx > e.startIdx && dayIdx <= e.endIdx)) return null;
                            return <td key={dayIdx} className={`calendar-cell ${getDayClasses(date)}`} />;
                          })}
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </section>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Расписание</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('schedule')}>Перейти к расписанию →</button>
            </div>
            <div className="overview-section-scroll">
              {scheduleLoading ? <p className="schedule-loading">Загрузка...</p> : scheduleError ? <p style={{ color: '#c62828' }}>{scheduleError}</p> : (
                <>
                  <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                    <button className="nav-button" onClick={() => { setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() - 1, 1)); }}>← Месяц</button>
                    <h3 className="calendar-navigation-title">{monthsOfYear[scheduleMonthStart.getMonth()]} {scheduleMonthStart.getFullYear()}</h3>
                    <button className="nav-button" onClick={() => { setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() + 1, 1)); }}>Месяц →</button>
                  </div>
                  <table className="sports-schedule-table calendar-view week-view month-view schedule-view overview-table" data-days={getMonthDates(scheduleMonthStart).length}>
                    <thead>
                      <tr>
                        <th className="sticky-col">Объект / Помещение</th>
                        {getMonthDates(scheduleMonthStart).map((date, idx) => (
                          <th key={idx} className={getDayClasses(date)}><div className="month-day-header">{date.getDate()}</div></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(scheduleData.objects || []).slice(0, 3).map((obj) => (
                        <React.Fragment key={obj.id}>
                          {(!obj.premises || obj.premises.length === 0) ? (
                            <tr><td className="sticky-col">{obj.name}</td><td colSpan={getMonthDates(scheduleMonthStart).length} className="schedule-no-data">Нет помещений</td></tr>
                          ) : (
                            <>
                              <tr><td colSpan={getMonthDates(scheduleMonthStart).length + 1} className="sticky-col" style={{ background: '#e8f2f5', fontWeight: 600, fontSize: 12 }}>{obj.name}</td></tr>
                              {obj.premises.map((prem) => (
                                <tr key={prem.id}>
                                  <td className="sticky-col schedule-cell-premise"><span className="schedule-premise-name">{prem.name}</span></td>
                                  {getMonthDates(scheduleMonthStart).map((date) => {
                                    const dayNorm = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                    const slots = getSlotsWithLanes(prem.id, dayNorm, scheduleData.slots);
                                    const numLanes = slots.length ? Math.max(...slots.map((s) => (s._lane ?? 0))) + 1 : 1;
                                    return (
                                      <td key={date.getTime()} className="schedule-day-track-cell" style={{ padding: 0, verticalAlign: 'middle', minHeight: 8 + numLanes * 28 }}>
                                        <div style={{ position: 'relative', width: '100%', height: 8 + numLanes * 28, minHeight: 8 + numLanes * 28 }}>
                                          {slots.map((slot) => {
                                            const pos = getSlotPositionInDay(slot, dayNorm);
                                            if (!pos || pos.widthPct <= 0) return null;
                                            const lane = slot._lane ?? 0;
                                            return (
                                              <div key={slot.id} className="schedule-slot calendar-event schedule-slot-cross" style={{ position: 'absolute', left: `${pos.leftPct}%`, width: `${pos.widthPct}%`, top: 4 + lane * 28 + 4, height: 20, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '0 4px', fontSize: 10, background: '#e3f2fd', border: '1px solid #90caf9' }}>
                                                <span className="schedule-slot-text">{[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
          </>
          )}

          {overviewViewMode === 'year' && (
          <>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Мероприятия</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('events')}>Перейти к мероприятиям →</button>
            </div>
            <div className="overview-section-scroll">
              <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                <button className="nav-button" onClick={() => setYearStartDate(new Date(yearStartDate.getFullYear() - 1, 0, 1))}>← Назад</button>
                <h3 className="calendar-navigation-title">{yearStartDate.getFullYear()}</h3>
                <button className="nav-button" onClick={() => setYearStartDate(new Date(yearStartDate.getFullYear() + 1, 0, 1))}>Вперед →</button>
              </div>
              <table className="sports-schedule-table calendar-view year-view overview-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Объект</th>
                    {getYearMonths().map((month, idx) => (
                      <th key={idx}>{month}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 5).map((obj, objIdx) => {
                    const yearStart = new Date(yearStartDate.getFullYear(), 0, 1);
                    const yearEnd = new Date(yearStartDate.getFullYear(), 11, 31);
                    yearEnd.setHours(23, 59, 59, 999);
                    const filteredEvents = (obj.events || []).filter(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      return !(eventEnd < yearStart || eventStart > yearEnd);
                    });
                    const uniqueEventsMap = new Map();
                    filteredEvents.forEach(event => { if (!uniqueEventsMap.has(event.id)) uniqueEventsMap.set(event.id, event); });
                    const events = Array.from(uniqueEventsMap.values());
                    events.sort((a, b) => {
                      const { startDate: sa, endDate: ea } = getEventStartEndForObject(a, obj.id);
                      const { startDate: sb, endDate: eb } = getEventStartEndForObject(b, obj.id);
                      if (new Date(sa).getTime() !== new Date(sb).getTime()) return new Date(sa).getTime() - new Date(sb).getTime();
                      return new Date(ea).getTime() - new Date(eb).getTime();
                    });
                    const yearMonthStarts = getYearMonths().map((_, idx) => new Date(yearStartDate.getFullYear(), idx, 1));
                    const yearMonthStart = yearMonthStarts[0];
                    const yearMonthEnd = new Date(yearStartDate.getFullYear(), 11, 31);
                    yearMonthEnd.setHours(23, 59, 59, 999);
                    const msPerDay = 24 * 60 * 60 * 1000;
                    const lastDayOfYear = Math.floor((yearMonthEnd.getTime() - yearMonthStart.getTime()) / msPerDay);
                    let tracks = [];
                    events.forEach(event => {
                      const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                      const eventForCell = { ...event, startDate, endDate };
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                      let startIdx = -1;
                      if (startDateOnly < yearMonthStart) startIdx = 0;
                      else if (startDateOnly > yearMonthEnd) return;
                      else startIdx = yearMonthStarts.findIndex((d, i) => {
                        const monthStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                        const nextStart = i < 11 ? new Date(yearStartDate.getFullYear(), i + 1, 1) : new Date(yearStartDate.getFullYear(), 11, 31, 23, 59, 59, 999);
                        return startDateOnly >= monthStart && startDateOnly < nextStart;
                      });
                      if (startIdx === -1) return;
                      let endIdx = -1;
                      if (endDateOnly > yearMonthEnd) endIdx = 11;
                      else if (endDateOnly < yearMonthStart) endIdx = startIdx;
                      else {
                        endIdx = yearMonthStarts.findIndex((d, i) => {
                          const monthStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                          const nextStart = i < 11 ? new Date(yearStartDate.getFullYear(), i + 1, 1) : new Date(yearStartDate.getFullYear(), 11, 31, 23, 59, 59, 999);
                          return endDateOnly >= monthStart && endDateOnly < nextStart;
                        });
                        if (endIdx === -1) endIdx = yearMonthStarts.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) > endDateOnly) - 1;
                        if (endIdx === -1) endIdx = 11;
                      }
                      if (endIdx < startIdx) endIdx = startIdx;
                      const startDayOfYear = Math.max(0, Math.min(lastDayOfYear, Math.floor((startDateOnly.getTime() - yearMonthStart.getTime()) / msPerDay)));
                      const endDayOfYear = Math.max(0, Math.min(lastDayOfYear, Math.floor((endDateOnly.getTime() - yearMonthStart.getTime()) / msPerDay)));
                      let placed = false;
                      for (let t = 0; t < tracks.length; t++) {
                        let busy = false;
                        for (let e of tracks[t]) {
                          if (!(endDayOfYear < e.startDayOfYear || startDayOfYear > e.endDayOfYear)) { busy = true; break; }
                        }
                        if (!busy) { tracks[t].push({ event: eventForCell, startIdx, endIdx, startDayOfYear, endDayOfYear }); placed = true; break; }
                      }
                      if (!placed) tracks.push([{ event: eventForCell, startIdx, endIdx, startDayOfYear, endDayOfYear }]);
                    });
                    if (tracks.length === 0) tracks = [[]];
                    const numTracks = Math.max(tracks.length, 1);
                    return tracks.map((track, trackIdx) => {
                      const uniqueEvents = [];
                      const seen = new Set();
                      track.forEach(e => { if (!seen.has(e.event.id)) { seen.add(e.event.id); uniqueEvents.push(e); } });
                      return (
                        <tr key={obj.id + '-track-' + trackIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                          {trackIdx === 0 ? <td rowSpan={numTracks} className="sticky-col"><div className="object-name">{obj.name}</div></td> : null}
                          {getYearMonths().map((_, monthIdx) => {
                            const cellEvent = uniqueEvents.find(e => e.startIdx <= monthIdx && e.endIdx >= monthIdx);
                            if (cellEvent) {
                              const { startDate: es, endDate: ee } = getEventStartEndForObject(cellEvent.event, obj.id);
                              const seg = getEventSegmentInMonth(new Date(es), new Date(ee), yearStartDate.getFullYear(), monthIdx);
                              if (!seg || seg.widthPct <= 0) return <td key={monthIdx} className="calendar-cell" />;
                              return (
                                <td key={monthIdx} className="calendar-cell">
                                  <div className="year-view-month-track" style={{ position: 'relative', minHeight: 28, height: 28 }}>
                                    <div className="calendar-event" style={{ position: 'absolute', left: `${seg.leftPct}%`, width: `${seg.widthPct}%`, top: 2, height: 24, background: getEventColor(cellEvent.event.id, cellEvent.event.name, cellEvent.event.status), boxSizing: 'border-box', overflow: 'hidden', fontSize: 10, display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                                      {cellEvent.event.name}
                                    </div>
                                  </div>
                                </td>
                              );
                            }
                            return <td key={monthIdx} className="calendar-cell" />;
                          })}
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </section>
          <section className="overview-section">
            <div className="overview-section-header">
              <h2 className="overview-section-title">Расписание</h2>
              <button type="button" className="nav-button" onClick={() => setActiveTab('schedule')}>Перейти к расписанию →</button>
            </div>
            <div className="overview-section-scroll">
              {scheduleLoading ? <p className="schedule-loading">Загрузка...</p> : scheduleError ? <p style={{ color: '#c62828' }}>{scheduleError}</p> : (
                <>
                  <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Расписание по месяцам — в полном режиме «Расписание».</p>
                  <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                    <button className="nav-button" onClick={() => { setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() - 1, 1)); }}>← Месяц</button>
                    <h3 className="calendar-navigation-title">{monthsOfYear[scheduleMonthStart.getMonth()]} {scheduleMonthStart.getFullYear()}</h3>
                    <button className="nav-button" onClick={() => { setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() + 1, 1)); }}>Месяц →</button>
                  </div>
                  <table className="sports-schedule-table calendar-view week-view month-view schedule-view overview-table" data-days={getMonthDates(scheduleMonthStart).length}>
                    <thead>
                      <tr>
                        <th className="sticky-col">Объект / Помещение</th>
                        {getMonthDates(scheduleMonthStart).map((date, idx) => (
                          <th key={idx} className={getDayClasses(date)}><div className="month-day-header">{date.getDate()}</div></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(scheduleData.objects || []).slice(0, 3).map((obj) => (
                        <React.Fragment key={obj.id}>
                          {(!obj.premises || obj.premises.length === 0) ? (
                            <tr><td className="sticky-col">{obj.name}</td><td colSpan={getMonthDates(scheduleMonthStart).length} className="schedule-no-data">Нет помещений</td></tr>
                          ) : (
                            <>
                              <tr><td colSpan={getMonthDates(scheduleMonthStart).length + 1} className="sticky-col" style={{ background: '#e8f2f5', fontWeight: 600, fontSize: 12 }}>{obj.name}</td></tr>
                              {obj.premises.map((prem) => (
                                <tr key={prem.id}>
                                  <td className="sticky-col schedule-cell-premise"><span className="schedule-premise-name">{prem.name}</span></td>
                                  {getMonthDates(scheduleMonthStart).map((date) => {
                                    const dayNorm = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                    const slots = getSlotsWithLanes(prem.id, dayNorm, scheduleData.slots);
                                    const numLanes = slots.length ? Math.max(...slots.map((s) => (s._lane ?? 0))) + 1 : 1;
                                    return (
                                      <td key={date.getTime()} className="schedule-day-track-cell" style={{ padding: 0, verticalAlign: 'middle', minHeight: 8 + numLanes * 28 }}>
                                        <div style={{ position: 'relative', width: '100%', height: 8 + numLanes * 28, minHeight: 8 + numLanes * 28 }}>
                                          {slots.map((slot) => {
                                            const pos = getSlotPositionInDay(slot, dayNorm);
                                            if (!pos || pos.widthPct <= 0) return null;
                                            const lane = slot._lane ?? 0;
                                            return (
                                              <div key={slot.id} className="schedule-slot calendar-event schedule-slot-cross" style={{ position: 'absolute', left: `${pos.leftPct}%`, width: `${pos.widthPct}%`, top: 4 + lane * 28 + 4, height: 20, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '0 4px', fontSize: 10, background: '#e3f2fd', border: '1px solid #90caf9' }}>
                                                <span className="schedule-slot-text">{[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
          </>
          )}
          </>
          )}

          {isAdminWithSchedule && (
            <>
              <div className="overview-modes" style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, marginRight: 4 }}>Период:</span>
                <button type="button" className={`mode-btn ${overviewViewMode === 'week' ? 'active' : ''}`} onClick={() => setOverviewViewMode('week')}>Неделя</button>
                <button type="button" className={`mode-btn ${overviewViewMode === 'month' ? 'active' : ''}`} onClick={() => setOverviewViewMode('month')}>Месяц</button>
                <button type="button" className={`mode-btn ${overviewViewMode === 'year' ? 'active' : ''}`} onClick={() => setOverviewViewMode('year')}>Год</button>
              </div>
              <div className="overview-section-scroll" style={{ maxHeight: 'none' }}>
                <div className="calendar-navigation" style={{ marginBottom: 8 }}>
                  {overviewViewMode === 'year' ? (
                    <>
                      <button className="nav-button" onClick={() => setYearStartDate(new Date(yearStartDate.getFullYear() - 1, 0, 1))}>← Назад</button>
                      <h3 className="calendar-navigation-title">{yearStartDate.getFullYear()}</h3>
                      <button className="nav-button" onClick={() => setYearStartDate(new Date(yearStartDate.getFullYear() + 1, 0, 1))}>Вперед →</button>
                    </>
                  ) : overviewViewMode === 'month' ? (
                    <>
                      <button className="nav-button" onClick={() => { const p = new Date(monthStartDate); p.setMonth(p.getMonth() - 1); setMonthStartDate(p); }}>← Месяц</button>
                      <h3 className="calendar-navigation-title">{monthsOfYear[monthStartDate.getMonth()]} {monthStartDate.getFullYear()}</h3>
                      <button className="nav-button" onClick={() => { const n = new Date(monthStartDate); n.setMonth(n.getMonth() + 1); setMonthStartDate(n); }}>Вперед →</button>
                    </>
                  ) : (
                    <>
                      <button className="nav-button" onClick={() => { const p = new Date(weekStartDate); p.setDate(p.getDate() - 7); setWeekStartDate(p); }}>← Неделя</button>
                      <h3 className="calendar-navigation-title">{monthsOfYear[weekStartDate.getMonth()]} {weekStartDate.getFullYear()}</h3>
                      <button className="nav-button" onClick={() => { const n = new Date(weekStartDate); n.setDate(n.getDate() + 7); setWeekStartDate(n); }}>Вперед →</button>
                    </>
                  )}
                </div>
                <table className="sports-schedule-table calendar-view overview-admin-table">
                  <thead>
                    <tr>
                      <th className="sticky-col overview-admin-obj-col">Объект</th>
                      <th className="overview-admin-cell-col">Мероприятия и расписание (одна шкала, два ряда)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((obj, objIdx) => {
                      if (overviewViewMode === 'year') {
                        const yearStart = new Date(yearStartDate.getFullYear(), 0, 1);
                        const yearEnd = new Date(yearStartDate.getFullYear(), 11, 31, 23, 59, 59, 999);
                        const months = getYearMonths();
                        const yearMonthStarts = months.map((_, idx) => new Date(yearStartDate.getFullYear(), idx, 1));

                        const filteredEvents = (obj.events || []).filter(event => {
                          const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                          return !(new Date(endDate) < yearStart || new Date(startDate) > yearEnd);
                        });

                        const uniqueEventsMap = new Map();
                        filteredEvents.forEach(e => { if (!uniqueEventsMap.has(e.id)) uniqueEventsMap.set(e.id, e); });
                        const events = Array.from(uniqueEventsMap.values());

                        let eventTracks = [];
                        events.forEach(event => {
                          const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                          const start = new Date(startDate);
                          const end = new Date(endDate);
                          const msPerDay = 24 * 60 * 60 * 1000;
                          const startDayOfYear = Math.max(0, Math.floor((start.getTime() - yearStart.getTime()) / msPerDay));
                          const endDayOfYear = Math.min(366, Math.floor((end.getTime() - yearStart.getTime()) / msPerDay));

                          let placed = false;
                          for (let t = 0; t < eventTracks.length; t++) {
                            let busy = false;
                            for (let e of eventTracks[t]) {
                              if (!(endDayOfYear < e.startDayOfYear || startDayOfYear > e.endDayOfYear)) { busy = true; break; }
                            }
                            if (!busy) { eventTracks[t].push({ event, startDayOfYear, endDayOfYear }); placed = true; break; }
                          }
                          if (!placed) eventTracks.push([{ event, startDayOfYear, endDayOfYear }]);
                        });
                        const numEventTracks = Math.max(eventTracks.length, 1);
                        const eventRowHeight = 8 + numEventTracks * 24;

                        // Сводное расписания по месяцам для года (упрощенно: если есть хоть один слот в месяце)
                        const scheduleByMonth = yearMonthStarts.map((monthStart, mIdx) => {
                          const monthEnd = new Date(yearStartDate.getFullYear(), mIdx + 1, 0, 23, 59, 59, 999);
                          const hasSlots = (scheduleData.slots || []).some(s => {
                            const isMyObj = (scheduleData.objects || []).find(o => o.id === obj.id)?.premises?.some(p => p.id === s.premise_id);
                            if (!isMyObj) return false;
                            const sStart = new Date(s.start_date);
                            const sEnd = new Date(s.end_date);
                            return !(sEnd < monthStart || sStart > monthEnd);
                          });
                          return hasSlots;
                        });

                        return (
                          <tr key={obj.id} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                            <td className="sticky-col overview-admin-obj-col">
                              <div className="object-name">{obj.name}</div>
                            </td>
                            <td className="overview-admin-cell-col" style={{ padding: 0, verticalAlign: 'top' }}>
                              <div className="overview-admin-two-strips">
                                <div className="overview-admin-scale" style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
                                  <div style={{ width: 88, flexShrink: 0, borderRight: '1px solid #e0e0e0' }} />
                                  <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                    {months.map((m, idx) => (
                                      <div key={idx} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 10, padding: '2px 0' }}>{m}</div>
                                    ))}
                                  </div>
                                </div>
                                <div style={{ display: 'flex' }}>
                                  <div className="overview-admin-strip-labels" style={{ width: 88, flexShrink: 0, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ height: eventRowHeight, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 10, color: '#666', background: '#fafafa' }}>Мероприятия</div>
                                    <div style={{ height: 32, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 10, color: '#666', background: '#fff' }}>Расписание</div>
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ height: eventRowHeight, position: 'relative', borderBottom: '1px solid #e8e8e8' }}>
                                      {eventTracks.map((track, tIdx) => (
                                        <div key={tIdx} style={{ position: 'absolute', left: 0, right: 0, top: 4 + tIdx * 24, height: 20 }}>
                                          {track.map((e, ei) => {
                                            const totalDays = (yearEnd.getTime() - yearStart.getTime()) > 365.1 * 24 * 3600 * 1000 ? 366 : 365;
                                            const startPct = (e.startDayOfYear / totalDays) * 100;
                                            const endPct = (e.endDayOfYear / totalDays) * 100;
                                            return (
                                              <div key={ei} className={`calendar-event ${getEventStatusClasses(e.event)}`} title={e.event.name} style={{ position: 'absolute', left: `${startPct}%`, width: `${Math.max(1, endPct - startPct)}%`, height: '100%', background: getEventColor(e.event.id, e.event.name, e.event.status), fontSize: 9, padding: '0 4px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', borderRadius: 2 }}>
                                                {e.event.name}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ))}
                                    </div>
                                    <div style={{ height: 32, display: 'flex' }}>
                                      {scheduleByMonth.map((has, mIdx) => (
                                        <div key={mIdx} style={{ flex: 1, borderRight: mIdx < 11 ? '1px solid #f0f0f0' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          {has && <div style={{ width: '80%', height: 12, background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 2 }} title="Есть занятые слоты в этом месяце" />}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      const currentDates = overviewViewMode === 'month' ? getMonthDates(monthStartDate) : getWeekDates(weekStartDate);
                      const displayStart = new Date(currentDates[0].getFullYear(), currentDates[0].getMonth(), currentDates[0].getDate());
                      const displayEnd = new Date(currentDates[currentDates.length - 1].getFullYear(), currentDates[currentDates.length - 1].getMonth(), currentDates[currentDates.length - 1].getDate());
                      displayEnd.setHours(23, 59, 59, 999);
                      const scheduleObj = (scheduleData.objects || []).find((o) => o.id === obj.id);
                      const premises = scheduleObj?.premises || [];

                      const filteredEvents = (obj.events || []).filter(event => {
                        const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                        const start = new Date(startDate);
                        const end = new Date(endDate);
                        const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                        const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                        return !(eventEnd < displayStart || eventStart > displayEnd);
                      });
                      const uniqueEventsMap = new Map();
                      filteredEvents.forEach(event => { if (!uniqueEventsMap.has(event.id)) uniqueEventsMap.set(event.id, event); });
                      const events = Array.from(uniqueEventsMap.values());
                      let eventTracks = [];
                      events.forEach(event => {
                        const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                        const eventForCell = { ...event, startDate, endDate };
                        const start = new Date(startDate);
                        const end = new Date(endDate);
                        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                        let startIdx = currentDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() === startDateOnly.getTime());
                        if (startIdx === -1) startIdx = startDateOnly < displayStart ? 0 : currentDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) >= startDateOnly);
                        if (startIdx === -1) return;
                        let endIdx = currentDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() === endDateOnly.getTime());
                        if (endIdx === -1) endIdx = endDateOnly > displayEnd ? currentDates.length - 1 : currentDates.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) > endDateOnly) - 1;
                        if (endIdx === -2 || endIdx < 0) endIdx = currentDates.length - 1;
                        if (endIdx < startIdx) endIdx = startIdx;
                        let placed = false;
                        for (let t = 0; t < eventTracks.length; t++) {
                          let busy = false;
                          for (let e of eventTracks[t]) {
                            if (!(endIdx < e.startIdx || startIdx > e.endIdx)) { busy = true; break; }
                          }
                          if (!busy) { eventTracks[t].push({ event: eventForCell, startIdx, endIdx }); placed = true; break; }
                        }
                        if (!placed) eventTracks.push([{ event: eventForCell, startIdx, endIdx }]);
                      });
                      if (eventTracks.length === 0) eventTracks = [[]];
                      const numEventTracks = Math.max(eventTracks.length, 1);
                      const eventRowHeight = 8 + numEventTracks * 24;

                      const scheduleByDay = currentDates.map((dayNorm) => {
                        const daySlots = [];
                        premises.forEach((prem) => {
                          getSlotsWithLanes(prem.id, dayNorm, scheduleData.slots).forEach((slot) => daySlots.push({ ...slot, _premId: prem.id }));
                        });
                        const sorted = [...daySlots].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
                        const laneEnds = [];
                        sorted.forEach((slot) => {
                          const start = new Date(slot.start_date).getTime();
                          const end = new Date(slot.end_date).getTime();
                          let lane = 0;
                          while (lane < laneEnds.length && start < laneEnds[lane]) lane++;
                          slot._lane = lane;
                          laneEnds[lane] = end;
                        });
                        return { dayNorm, slots: sorted };
                      });
                      const maxScheduleLanes = Math.max(1, ...scheduleByDay.map((d) => d.slots.length ? Math.max(...d.slots.map((s) => (s._lane ?? 0))) + 1 : 0));
                      const scheduleRowHeight = 8 + maxScheduleLanes * 24;

                      return (
                        <tr key={obj.id} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                          <td className="sticky-col overview-admin-obj-col">
                            <div className="object-name">{obj.name}</div>
                          </td>
                          <td className="overview-admin-cell-col" style={{ padding: 0, verticalAlign: 'top' }}>
                            <div className="overview-admin-two-strips">
                              <div className="overview-admin-scale" style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
                                <div style={{ width: 88, flexShrink: 0, borderRight: '1px solid #e0e0e0' }} />
                                <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                  {currentDates.map((date, idx) => (
                                    <div key={idx} className={getDayClasses(date)} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 10, padding: '2px 0' }}>
                                      {overviewViewMode === 'month' ? date.getDate() : `${daysOfWeek[idx]} ${date.getDate()}`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8' }}>
                                <div className="overview-admin-strip-labels" style={{ width: 88, flexShrink: 0, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ height: eventRowHeight, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 10, color: '#666', background: '#fafafa' }}>Мероприятия</div>
                                  <div style={{ height: scheduleRowHeight, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 10, color: '#666', background: '#fff' }}>Расписание</div>
                                </div>
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                              <div className="overview-admin-events-strip" style={{ height: eventRowHeight, minHeight: eventRowHeight, position: 'relative', borderBottom: '1px solid #e8e8e8' }}>
                                {eventTracks.map((track, trackIdx) => {
                                  const uniqueEvents = [];
                                  const seen = new Set();
                                  track.forEach(e => { if (!seen.has(e.event.id)) { seen.add(e.event.id); uniqueEvents.push(e); } });
                                  return (
                                    <div key={trackIdx} style={{ position: 'absolute', left: 0, right: 0, top: 4 + trackIdx * 24, height: 24 - 4, display: 'flex' }}>
                                      {currentDates.map((date, dayIdx) => {
                                        const cellEvent = uniqueEvents.find(e => e.startIdx === dayIdx);
                                        if (cellEvent) {
                                          const span = cellEvent.endIdx - cellEvent.startIdx + 1;
                                          const overlapping = getOverlappingSectionsForEvent(cellEvent.event, obj.id, currentDates, cellEvent.startIdx, cellEvent.endIdx, scheduleData.slots, scheduleData.objects);
                                          const tooltipText = overlapping.length
                                            ? `${cellEvent.event.name}\nПересекается с секциями: ${overlapping.join(', ')}`
                                            : cellEvent.event.name;
                                          const isEditableByAdmin = userRole !== 'admin' || !cellEvent.event.organizationId || !userOrgId || Number(cellEvent.event.organizationId) === userOrgId;
                                           return (
                                               <div key={dayIdx} style={{ flex: span, minWidth: 0, padding: '0 2px' }}>
                                                <div className={`calendar-event ${!isEditableByAdmin ? 'event-readonly' : ''} ${getEventStatusClasses(cellEvent.event)}`} title={tooltipText} style={{ background: getEventColor(cellEvent.event.id, cellEvent.event.name, cellEvent.event.status), borderRadius: 4, height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '0 6px', fontSize: 10, opacity: !isEditableByAdmin ? 0.6 : 1, cursor: !isEditableByAdmin ? 'not-allowed' : 'grab' }}>
                                                  {cellEvent.event.name}
                                                </div>
                                              </div>
                                           );
                                        }
                                        if (uniqueEvents.some(e => dayIdx > e.startIdx && dayIdx <= e.endIdx)) return null;
                                        return <div key={dayIdx} style={{ flex: 1, minWidth: 0 }} />;
                                      })}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="overview-admin-schedule-strip" style={{ height: scheduleRowHeight, minHeight: scheduleRowHeight, position: 'relative' }}>
                                <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                                  {currentDates.map((date, dayIdx) => {
                                    const { dayNorm, slots } = scheduleByDay[dayIdx] || { dayNorm: date, slots: [] };
                                    return (
                                      <div key={dayIdx} style={{ flex: 1, minWidth: 0, borderRight: dayIdx < currentDates.length - 1 ? '1px solid #e0e0e0' : undefined, position: 'relative' }}>
                                        {slots.map((slot) => {
                                          const pos = getSlotPositionInDay(slot, dayNorm);
                                          if (!pos || pos.widthPct <= 0) return null;
                                          const lane = slot._lane ?? 0;
                                          return (
                                            <div key={slot.id} className="schedule-slot calendar-event schedule-slot-cross" style={{ position: 'absolute', left: `${pos.leftPct}%`, width: `${pos.widthPct}%`, top: 4 + lane * 24, height: 20, boxSizing: 'border-box', overflow: 'hidden', display: 'flex', alignItems: 'center', padding: '0 4px', fontSize: 10, background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 4 }}>
                                              <span className="schedule-slot-text">{[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Вкладка «Расписание»: объекты → помещения, слоты секций по дням (скрыта в релизе, см. REACT_APP_SHOW_SCHEDULE) */}
      {SHOW_SCHEDULE && activeTab === 'schedule' && (
        <div className="schedule-tab-content">
          {scheduleLoading && <p className="schedule-loading">Загрузка расписания...</p>}
          {scheduleError && (
            <p className="schedule-error" style={{ color: '#c62828' }}>{scheduleError}</p>
          )}
          {!scheduleLoading && !scheduleError && (
            <>
              {copiedSlot && (
                <div className="schedule-copy-hint" style={{ marginBottom: 8, padding: '8px 12px', background: '#e3f2fd', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span>Слот скопирован. Двойной клик по ячейке — вставка (можно отредактировать время).</span>
                  <button type="button" className="cancel-button" style={{ padding: '4px 10px' }} onClick={() => { setCopiedSlot(null); logAction('slot_copy_clear'); }}>Отменить копирование</button>
                </div>
              )}
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>Режим:</span>
                <button
                  type="button"
                  className={`nav-button ${scheduleScheduleMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => { setScheduleScheduleMode('calendar'); logAction('schedule_mode', 'calendar'); loadScheduleData(); }}
                >
                  Календарь
                </button>
                <button
                  type="button"
                  className={`nav-button ${scheduleScheduleMode === 'template' ? 'active' : ''}`}
                  onClick={() => { setScheduleScheduleMode('template'); logAction('schedule_mode', 'template'); if (!templateObjectId && scheduleData.objects?.length) setTemplateObjectId(scheduleData.objects[0].id); setCurrentTemplate(null); setTemplateSlots([]); }}
                >
                  Шаблон недели
                </button>
                {scheduleScheduleMode === 'template' && (
                  <>
                    <span style={{ marginLeft: 8, color: 'var(--text-secondary, #666)', fontSize: '0.9em' }}>
                      {currentTemplate?.id ? 'Редактирование шаблона' : 'Добавление шаблона'}
                    </span>
                    {currentTemplate?.id && (templateObjectId != null || currentTemplate.object_id != null) && (() => {
                      const objName = selectedScheduleObject?.name || (scheduleData.objects || []).find((o) => o.id === (templateObjectId ?? currentTemplate.object_id))?.name || '—';
                      const from = templatePeriod.valid_from || currentTemplate.valid_from || '';
                      const to = templatePeriod.valid_to || currentTemplate.valid_to || '';
                      const fmt = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : d);
                      return (
                        <span style={{ padding: '4px 10px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6, fontSize: '0.95em' }} title="Загруженный шаблон">
                          Шаблон: {objName}, {fmt(from)} – {fmt(to)}
                        </span>
                      );
                    })()}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Объект:
                      <select
                        value={templateObjectId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setTemplateObjectId(v ? Number(v) : null);
                          setTemplateOrgId(null);
                          setTemplateSportTypeId(null);
                          setTemplateCoachId(null);
                          setCurrentTemplate(null);
                          setTemplateSlots([]);
                        }}
                        style={{ minWidth: 160 }}
                      >
                        <option value="">— выберите —</option>
                        {(scheduleData.objects || []).map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </label>
                    {templateObjectId && (
                      <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          Организация:
                          <select
                            value={templateOrgId ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setTemplateOrgId(v ? Number(v) : null);
                              setTemplateSportTypeId(null);
                              setTemplateCoachId(null);
                              setCurrentTemplate(null);
                              setTemplateSlots([]);
                            }}
                            style={{ minWidth: 140 }}
                          >
                            <option value="">— выберите —</option>
                            {(templateOrganizations || []).map((o) => (
                              <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          Вид спорта:
                          <select
                            value={templateSportTypeId ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setTemplateSportTypeId(v ? Number(v) : null);
                              setTemplateCoachId(null);
                              setCurrentTemplate(null);
                              setTemplateSlots([]);
                            }}
                            style={{ minWidth: 140 }}
                            disabled={!templateOrgId}
                          >
                            <option value="">— выберите —</option>
                            {(templateSportTypes || []).map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          Тренер:
                          <select
                            value={templateCoachId ?? ''}
                            onChange={(e) => { const v = e.target.value; setTemplateCoachId(v ? Number(v) : null); setCurrentTemplate(null); setTemplateSlots([]); }}
                            style={{ minWidth: 220 }}
                            title="Для каждого тренера свой недельный шаблон"
                            disabled={!templateSportTypeId}
                          >
                            <option value="">— выберите тренера —</option>
                            {(templateCoachesFull || [])
                              .filter((c) => !templateSportTypeId || Number(c.sport_type_id) === Number(templateSportTypeId))
                              .filter((c) => !templateOrgId || Number(c.organization_id) === Number(templateOrgId))
                              .map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}{c.sport_type_name || c.organization_name ? ` (${[c.sport_type_name, c.organization_name].filter(Boolean).join(', ')})` : ''}
                                </option>
                              ))}
                          </select>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          Срок действия: с
                          <input type="date" value={templatePeriod.valid_from} onChange={(e) => setTemplatePeriod((p) => ({ ...p, valid_from: e.target.value }))} />
                          по
                          <input type="date" value={templatePeriod.valid_to} onChange={(e) => setTemplatePeriod((p) => ({ ...p, valid_to: e.target.value }))} />
                        </label>
                        <button type="button" className="add-btn" onClick={handleSaveTemplate} disabled={templateLoading}>
                          {templateLoading ? 'Сохранение…' : (currentTemplate?.id ? 'Сохранить' : 'Сохранить черновик')}
                        </button>
                        {currentTemplate?.id && (
                          <>
                            <button type="button" className="nav-button" onClick={handleEditTemplate} disabled={templateLoading} title="Применить только срок и тренера (без перезаписи слотов)">Редактировать</button>
                            <button type="button" className="nav-button" onClick={handleDeleteTemplate} disabled={templateLoading} title="Удалить загруженный шаблон">Удалить</button>
                            <button type="button" className="nav-button" onClick={handleApproveTemplate} disabled={templateLoading} title="Пересчитать слоты в календаре по шаблону">Утвердить</button>
                          </>
                        )}
                        <button type="button" className="nav-button" onClick={() => { setCurrentTemplate(null); setTemplateSlots([]); logAction('template_mode', 'add_new'); }} title="Создать новый шаблон">
                          Добавить шаблон
                        </button>
                        {currentTemplate?.id && (
                          <>
                            <span style={{ marginLeft: 4, color: 'var(--text-secondary, #666)', fontSize: '0.9em' }} title={currentTemplate.status === 'approved' ? 'Шаблон утверждён, слоты есть в календаре' : 'Черновик: нажмите «Утвердить», чтобы создать слоты в календаре'}>
                              {currentTemplate.status === 'approved' ? '✓ Утверждён' : 'Черновик'}
                            </span>
                            {currentTemplate.status === 'approved' && (
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, cursor: 'pointer' }} title={currentTemplate.enabled !== false ? 'Слоты шаблона показываются в календаре' : 'Слоты шаблона скрыты в календаре'}>
                                <input
                                  type="checkbox"
                                  checked={currentTemplate.enabled !== false}
                                  onChange={async () => {
                                    const nextEnabled = currentTemplate.enabled === false;
                                    setTemplateLoading(true);
                                    try {
                                      await scheduleAPI.updateScheduleTemplate(
                                        currentTemplate.id,
                                        templatePeriod.valid_from || currentTemplate.valid_from,
                                        templatePeriod.valid_to || currentTemplate.valid_to,
                                        templateCoachId ?? currentTemplate.coach_id ?? undefined,
                                        nextEnabled
                                      );
                                      setCurrentTemplate((prev) => (prev ? { ...prev, enabled: nextEnabled } : null));
                                      loadScheduleData();
                                    } catch (e) {
                                      console.error(e);
                                      alert(e.response?.data?.error || 'Не удалось изменить состояние');
                                    } finally {
                                      setTemplateLoading(false);
                                    }
                                  }}
                                  disabled={templateLoading}
                                />
                                <span style={{ fontSize: '0.9em' }}>Включён</span>
                              </label>
                            )}
                          </>
                        )}
                        {templateList.length > 0 && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }} title="Только шаблоны выбранного объекта. Черновик — не утверждён, слоты в календаре не созданы.">
                            {currentTemplate?.id ? 'Загрузить другой:' : 'Загрузить существующий:'}
                            <select
                              value=""
                              onChange={(e) => { const v = e.target.value; if (v) loadTemplateAndSlots(Number(v)); e.target.value = ''; }}
                              style={{ minWidth: 420 }}
                              title="Только шаблоны выбранного объекта. Черновик / Утверждён."
                            >
                              <option value="">— выберите шаблон —</option>
                              {[...templateList]
                                .sort((a, b) => (a.status === 'draft' && b.status !== 'draft' ? -1 : a.status !== 'draft' && b.status === 'draft' ? 1 : 0))
                                .map((t) => {
                                  const fmt = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : d || '');
                                  const period = `${fmt(t.valid_from)} – ${fmt(t.valid_to)}`;
                                  const orgSportCoach = (t.display_name || '').trim() || '—';
                                  const statusLabel = t.status === 'approved' ? ' [Утверждён]' : ' [Черновик]';
                                  return (
                                    <option key={t.id} value={t.id}>
                                      {orgSportCoach} — Срок: {period}{statusLabel}
                                    </option>
                                  );
                                })}
                            </select>
                          </label>
                        )}
                      </>
                    )}
                    {templateLoading && <span className="schedule-loading">Загрузка…</span>}
                  </>
                )}
              </div>
              <div className="calendar-navigation" style={{ flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    className={`nav-button ${scheduleViewMode === 'hours' ? 'active' : ''}`}
                    onClick={() => { logAction('schedule_view', 'day'); setScheduleViewMode('hours'); }}
                  >
                    День
                  </button>
                  <button
                    type="button"
                    className={`nav-button ${scheduleViewMode === 'week' ? 'active' : ''}`}
                    onClick={() => { logAction('schedule_view', 'week'); setScheduleViewMode('week'); }}
                  >
                    Неделя
                  </button>
                  <button
                    type="button"
                    className={`nav-button ${scheduleViewMode === 'month' ? 'active' : ''}`}
                    onClick={() => { logAction('schedule_view', 'month'); setScheduleViewMode('month'); setScheduleMonthStart(new Date(scheduleWeekStart.getFullYear(), scheduleWeekStart.getMonth(), 1)); }}
                  >
                    Месяц
                  </button>
                </div>
                {scheduleViewMode === 'week' && (
                  <>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_week_nav', 'prev');
                      const prev = new Date(scheduleWeekStart);
                      prev.setDate(prev.getDate() - 7);
                      setScheduleWeekStart(prev);
                    }}>← Неделя</button>
                    <h3 className="calendar-navigation-title">
                      {monthsOfYear[scheduleWeekStart.getMonth()]} {scheduleWeekStart.getFullYear()}
                    </h3>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_week_nav', 'next');
                      const next = new Date(scheduleWeekStart);
                      next.setDate(next.getDate() + 7);
                      setScheduleWeekStart(next);
                    }}>Неделя →</button>
                  </>
                )}
                {scheduleViewMode === 'hours' && (
                  <>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_day_nav', 'prev');
                      const prev = new Date(scheduleDay);
                      prev.setDate(prev.getDate() - 1);
                      setScheduleDay(prev);
                    }}>← День</button>
                    <h3 className="calendar-navigation-title">
                      {scheduleDay.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_day_nav', 'next');
                      const next = new Date(scheduleDay);
                      next.setDate(next.getDate() + 1);
                      setScheduleDay(next);
                    }}>День →</button>
                  </>
                )}
                {scheduleViewMode === 'month' && (
                  <>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_month_nav', 'prev');
                      setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() - 1, 1));
                    }}>← Месяц</button>
                    <h3 className="calendar-navigation-title">
                      {monthsOfYear[scheduleMonthStart.getMonth()]} {scheduleMonthStart.getFullYear()}
                    </h3>
                    <button className="nav-button" type="button" onClick={() => {
                      logAction('schedule_month_nav', 'next');
                      setScheduleMonthStart(new Date(scheduleMonthStart.getFullYear(), scheduleMonthStart.getMonth() + 1, 1));
                    }}>Месяц →</button>
                  </>
                )}
              </div>
              {scheduleViewMode === 'week' && (
              <table className="sports-schedule-table calendar-view week-view schedule-view">
                <thead>
                  <tr className="schedule-header-time-row">
                    <th className="sticky-col schedule-th-time">Время</th>
                    {getWeekDates(scheduleWeekStart).map((date, idx) => (
                      <th key={idx} className={`${getDayClasses(date)} schedule-th-hours`}>
                        <div className="schedule-hours-scale">
                          {getScheduleHours().map((h) => (
                            <span key={h} className="schedule-hour-label">{String(h).padStart(2, '0')}</span>
                          ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th className="sticky-col">Объект / Помещение</th>
                    {getWeekDates(scheduleWeekStart).map((date, idx) => (
                      <th
                        key={idx}
                        className={`${getDayClasses(date)} schedule-day-header-clickable`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          logAction('schedule_open_day', date.toLocaleDateString());
                          setScheduleViewMode('hours');
                          setScheduleDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            logAction('schedule_open_day', date.toLocaleDateString());
                            setScheduleViewMode('hours');
                            setScheduleDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                          }
                        }}
                      >
                        {daysOfWeek[idx]}<br />{date.toLocaleDateString()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {effectiveScheduleObjects.map((obj) => {
                    const hasPremises = obj.premises && obj.premises.length > 0;
                    const isTemplateMode = scheduleScheduleMode === 'template';
                    return (
                      <React.Fragment key={obj.id}>
                        {/* Строка объекта: название + пиктограммы + (добавить/убрать помещение) */}
                        <tr className="schedule-row schedule-row-object">
                          <td className="sticky-col schedule-cell-premise schedule-cell-object">
                            {hasPremises && (
                              <button
                                type="button"
                                className="schedule-collapse-btn"
                                title={collapsedScheduleObjects.has(obj.id) ? 'Развернуть' : 'Свернуть'}
                                onClick={() => toggleScheduleObjectCollapsed(obj.id)}
                              >
                                <svg className={`schedule-collapse-icon ${collapsedScheduleObjects.has(obj.id) ? 'collapsed' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            <span className="schedule-object-name">{obj.name}</span>
                            {userRole !== 'user' && !isTemplateMode && (
                              <span className="schedule-object-icons">
                                <button
                                  type="button"
                                  className="icon-btn add-event-btn"
                                  title="Добавить помещение"
                                  onClick={() => handleAddPremise(obj.id)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/>
                                    <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                    <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                  </svg>
                                </button>
                                {hasPremises && (
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    title="Убрать помещение (последнее)"
                                    onClick={() => {
                                      const last = obj.premises[obj.premises.length - 1];
                                      if (last) handleDeletePremise(last.id);
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                      <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                          {hasPremises ? (
                            getWeekDates(scheduleWeekStart).map((day, dayIdx) => (
                              <td key={dayIdx} className="schedule-cell calendar-cell schedule-cell-empty" />
                            ))
                          ) : (
                            <td colSpan={7} className="schedule-cell-empty schedule-cell-object-as-premise">
                              <span className="schedule-no-premises">(сам объект как помещение)</span>
                            </td>
                          )}
                        </tr>
                        {/* Строки помещений объекта */}
                        {hasPremises && !collapsedScheduleObjects.has(obj.id) && obj.premises.map((prem) => (
                          <tr key={`${obj.id}-${prem.id}`} className="schedule-row schedule-row-premise">
                            <td className="sticky-col schedule-cell-premise">
                              <span className="schedule-premise-name">{prem.name}</span>
                              {userRole !== 'user' && !isTemplateMode && (
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Убрать помещение"
                                  onClick={() => handleDeletePremise(prem.id)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                    <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              )}
                            </td>
                            {getWeekDates(scheduleWeekStart).map((day, dayIdx) => {
                              const dayNorm = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                              const slotsWithLanes = getSlotsWithLanes(prem.id, dayNorm, effectiveScheduleSlots);
                              const numLanes = slotsWithLanes.length ? Math.max(...slotsWithLanes.map((s) => (s._lane ?? 0))) + 1 : 1;
                              const trackMinHeight = 8 + numLanes * LANE_HEIGHT;
                              const isDragOver = !isTemplateMode && scheduleDragOver && scheduleDragOver.premId === prem.id && scheduleDragOver.day.getTime() === dayNorm.getTime();
                              const isTemplateDragOver = isTemplateMode && templateSlotDragOver && templateSlotDragOver.premId === prem.id && templateSlotDragOver.dayOfWeek === dayIdx + 1;
                              return (
                                <td
                                  key={dayIdx}
                                  className={`schedule-cell calendar-cell schedule-day-track-cell ${userRole !== 'user' ? 'schedule-cell-clickable' : ''} ${isDragOver || isTemplateDragOver ? 'schedule-cell-drag-over' : ''}`}
                                  style={{ padding: 0, overflow: 'hidden', verticalAlign: 'middle', minHeight: trackMinHeight }}
                                  onDoubleClick={userRole !== 'user' ? (e) => {
                                    if (e.target.closest('.schedule-slot')) return;
                                    e.stopPropagation();
                                    if (isTemplateMode && templateObjectId) {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = e.clientX - rect.left;
                                      const hour = rect.width > 0 ? Math.floor((x / rect.width) * 17) + 7 : 8;
                                      const h = Math.max(7, Math.min(22, hour));
                                      openTemplateSlotModalForCell(prem.id, dayIdx + 1, `${String(h).padStart(2, '0')}:00`, `${String(h + 1).padStart(2, '0')}:00`);
                                    } else if (!isTemplateMode) openSlotModalForCell(prem.id, day, null);
                                  } : undefined}
                                  onDragOver={userRole !== 'user' ? (e) => { if (isTemplateMode) { e.preventDefault(); handleTemplateSlotDragOver(e, prem.id, dayIdx + 1); } else handleScheduleSlotDragOver(e, prem.id, dayNorm); } : undefined}
                                  onDragLeave={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragLeave(e, prem.id, dayIdx + 1); else handleScheduleSlotDragLeave(e, prem.id, dayNorm); } : undefined}
                                  onDrop={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDrop(e, prem.id, dayIdx + 1); else handleScheduleSlotDrop(e, prem.id, dayNorm); } : undefined}
                                >
                                  <div
                                    className="schedule-day-track-wrap schedule-week-day-track"
                                    style={{ position: 'relative', width: '100%', height: trackMinHeight, minHeight: trackMinHeight, margin: 0 }}
                                  >
                                    {slotsWithLanes.map((slot) => {
                                      const pos = getSlotPositionInDay(slot, dayNorm);
                                      if (!pos || pos.widthPct <= 0) return null;
                                      const lane = slot._lane ?? 0;
                                      return (
                                        <div
                                          key={slot.id}
                                          className={`schedule-slot calendar-event schedule-slot-cross ${userRole !== 'user' ? 'schedule-slot-clickable' : ''}`}
                                          title={getSlotTooltipText(slot, userRole !== 'user')}
                                          draggable={userRole !== 'user' && (isTemplateMode ? (typeof slot.id !== 'string' || !String(slot.id).startsWith('draft-')) : true)}
                                          onDragStart={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragStart(e, slot); else handleScheduleSlotDragStart(e, slot); } : undefined}
                                          onDragEnd={userRole !== 'user' ? () => { if (isTemplateMode) handleTemplateSlotDragEnd(); else handleScheduleSlotDragEnd(); } : undefined}
                                          onDoubleClick={userRole !== 'user' ? (e) => {
                                            e.stopPropagation();
                                            if (isTemplateMode && templateObjectId) {
                                              setEditingTemplateSlot(slot);
                                              const dow = slot.day_of_week || 1;
                                              const daysOfWeekEdit = [false, false, false, false, false, false, false];
                                              if (dow >= 1 && dow <= 7) daysOfWeekEdit[dow - 1] = true;
                                              setTemplateSlotForm({ sectionId: slot.section_id, premiseId: slot.premise_id, day_of_week: dow, time_start: (slot.time_start || '08:00').toString().slice(0, 8), time_end: (slot.time_end || '09:00').toString().slice(0, 8), organizationId: '', sportTypeId: '', coachId: '', daysOfWeek: daysOfWeekEdit });
                                              setShowTemplateSlotModal(true);
                                            } else openSlotEditModal(slot);
                                          } : undefined}
                                          role={userRole !== 'user' ? 'button' : undefined}
                                          style={{
                                            position: 'absolute',
                                            left: `${pos.leftPct}%`,
                                            width: `${pos.widthPct}%`,
                                            top: 4 + lane * LANE_HEIGHT + 4,
                                            height: LANE_HEIGHT - 8,
                                            boxSizing: 'border-box',
                                            pointerEvents: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            padding: '2px 6px',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <span className="schedule-slot-text">
                                            {[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {(!effectiveScheduleObjects || effectiveScheduleObjects.length === 0) && (
                    <tr>
                      <td colSpan={8} style={{ padding: '1rem', color: '#666' }}>
                        {scheduleScheduleMode === 'template' ? 'Выберите объект и шаблон выше.' : 'Нет объектов. Добавьте объекты во вкладке «Мероприятия» (управление объектами — для администратора).'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
              {scheduleViewMode === 'month' && (() => {
                const monthDates = getMonthDates(scheduleMonthStart);
                return (
              <table className="sports-schedule-table calendar-view week-view month-view schedule-view" data-days={monthDates.length}>
                <thead>
                  <tr>
                    <th className="sticky-col">Объект / Помещение</th>
                    {monthDates.map((date, idx) => (
                      <th
                        key={idx}
                        className={`${getDayClasses(date)} schedule-day-header-clickable`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setScheduleViewMode('hours');
                          setScheduleDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setScheduleViewMode('hours');
                            setScheduleDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                          }
                        }}
                      >
                        {daysOfWeekShort[date.getDay() === 0 ? 6 : date.getDay() - 1]} {date.getDate()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {effectiveScheduleObjects.map((obj) => {
                    const hasPremises = obj.premises && obj.premises.length > 0;
                    const isTemplateMode = scheduleScheduleMode === 'template';
                    return (
                      <React.Fragment key={obj.id}>
                        <tr className="schedule-row schedule-row-object">
                          <td className="sticky-col schedule-cell-premise schedule-cell-object">
                            {hasPremises && (
                              <button
                                type="button"
                                className="schedule-collapse-btn"
                                title={collapsedScheduleObjects.has(obj.id) ? 'Развернуть' : 'Свернуть'}
                                onClick={() => toggleScheduleObjectCollapsed(obj.id)}
                              >
                                <svg className={`schedule-collapse-icon ${collapsedScheduleObjects.has(obj.id) ? 'collapsed' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            <span className="schedule-object-name">{obj.name}</span>
                            {userRole !== 'user' && !isTemplateMode && (
                              <span className="schedule-object-icons">
                                <button type="button" className="icon-btn add-event-btn" title="Добавить помещение" onClick={() => handleAddPremise(obj.id)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/>
                                    <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                    <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                  </svg>
                                </button>
                                {hasPremises && (
                                  <button type="button" className="icon-btn" title="Убрать помещение (последнее)" onClick={() => { const last = obj.premises[obj.premises.length - 1]; if (last) handleDeletePremise(last.id); }}>
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                      <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                          {hasPremises ? monthDates.map((day) => (
                            <td key={day.getTime()} className="schedule-cell calendar-cell schedule-cell-empty" />
                          )) : (
                            <td colSpan={monthDates.length} className="schedule-cell-empty schedule-cell-object-as-premise">
                              <span className="schedule-no-premises">(сам объект как помещение)</span>
                            </td>
                          )}
                        </tr>
                        {hasPremises && !collapsedScheduleObjects.has(obj.id) && obj.premises.map((prem) => (
                          <tr key={`${obj.id}-${prem.id}`} className="schedule-row schedule-row-premise">
                            <td className="sticky-col schedule-cell-premise">
                              <span className="schedule-premise-name">{prem.name}</span>
                              {userRole !== 'user' && !isTemplateMode && (
                                <button type="button" className="icon-btn" title="Убрать помещение" onClick={() => handleDeletePremise(prem.id)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                    <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              )}
                            </td>
                            {monthDates.map((day, dayIdx) => {
                              const dayNorm = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                              const dayOfWeekMonth = dayNorm.getDay() === 0 ? 7 : dayNorm.getDay();
                              const slotsWithLanes = getSlotsWithLanes(prem.id, dayNorm, effectiveScheduleSlots);
                              const numLanes = slotsWithLanes.length ? Math.max(...slotsWithLanes.map((s) => (s._lane ?? 0))) + 1 : 1;
                              const trackMinHeight = 8 + numLanes * LANE_HEIGHT;
                              const isDragOver = !isTemplateMode && scheduleDragOver && scheduleDragOver.premId === prem.id && scheduleDragOver.day.getTime() === dayNorm.getTime();
                              const isTemplateDragOverMonth = isTemplateMode && templateSlotDragOver && templateSlotDragOver.premId === prem.id && templateSlotDragOver.dayOfWeek === dayOfWeekMonth;
                              return (
                                <td
                                  key={dayIdx}
                                  className={`schedule-cell calendar-cell schedule-day-track-cell ${userRole !== 'user' ? 'schedule-cell-clickable' : ''} ${isDragOver || isTemplateDragOverMonth ? 'schedule-cell-drag-over' : ''} ${getDayClasses(day)}`}
                                  style={{ padding: 0, overflow: 'hidden', verticalAlign: 'middle', minHeight: trackMinHeight }}
                                  onDoubleClick={userRole !== 'user' ? (e) => {
                                    if (e.target.closest('.schedule-slot')) return;
                                    e.stopPropagation();
                                    if (isTemplateMode && templateObjectId) {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = e.clientX - rect.left;
                                      const hour = rect.width > 0 ? Math.floor((x / rect.width) * 17) + 7 : 8;
                                      const h = Math.max(7, Math.min(22, hour));
                                      openTemplateSlotModalForCell(prem.id, dayOfWeekMonth, `${String(h).padStart(2, '0')}:00`, `${String(h + 1).padStart(2, '0')}:00`);
                                    } else if (!isTemplateMode) openSlotModalForCell(prem.id, dayNorm, null);
                                  } : undefined}
                                  onDragOver={userRole !== 'user' ? (e) => { if (isTemplateMode) { e.preventDefault(); handleTemplateSlotDragOver(e, prem.id, dayOfWeekMonth); } else handleScheduleSlotDragOver(e, prem.id, dayNorm); } : undefined}
                                  onDragLeave={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragLeave(e, prem.id, dayOfWeekMonth); else handleScheduleSlotDragLeave(e, prem.id, dayNorm); } : undefined}
                                  onDrop={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDrop(e, prem.id, dayOfWeekMonth); else handleScheduleSlotDrop(e, prem.id, dayNorm); } : undefined}
                                >
                                  <div className="schedule-day-track-wrap schedule-week-day-track" style={{ position: 'relative', width: '100%', height: trackMinHeight, minHeight: trackMinHeight, margin: 0 }}>
                                    {slotsWithLanes.map((slot) => {
                                      const pos = getSlotPositionInDay(slot, dayNorm);
                                      if (!pos || pos.widthPct <= 0) return null;
                                      const lane = slot._lane ?? 0;
                                      return (
                                        <div
                                          key={slot.id}
                                          className={`schedule-slot calendar-event schedule-slot-cross ${userRole !== 'user' ? 'schedule-slot-clickable' : ''}`}
                                          title={getSlotTooltipText(slot, userRole !== 'user')}
                                          draggable={userRole !== 'user' && (isTemplateMode ? (typeof slot.id !== 'string' || !String(slot.id).startsWith('draft-')) : true)}
                                          onDragStart={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragStart(e, slot); else handleScheduleSlotDragStart(e, slot); } : undefined}
                                          onDragEnd={userRole !== 'user' ? () => { if (isTemplateMode) handleTemplateSlotDragEnd(); else handleScheduleSlotDragEnd(); } : undefined}
                                          onDoubleClick={userRole !== 'user' ? (e) => {
                                            e.stopPropagation();
                                            if (isTemplateMode && templateObjectId) {
                                              setEditingTemplateSlot(slot);
                                              const dow = slot.day_of_week || 1;
                                              const daysOfWeekEdit = [false, false, false, false, false, false, false];
                                              if (dow >= 1 && dow <= 7) daysOfWeekEdit[dow - 1] = true;
                                              setTemplateSlotForm({ sectionId: slot.section_id, premiseId: slot.premise_id, day_of_week: dow, time_start: (slot.time_start || '08:00').toString().slice(0, 8), time_end: (slot.time_end || '09:00').toString().slice(0, 8), organizationId: '', sportTypeId: '', coachId: '', daysOfWeek: daysOfWeekEdit });
                                              setShowTemplateSlotModal(true);
                                            } else openSlotEditModal(slot);
                                          } : undefined}
                                          role={userRole !== 'user' ? 'button' : undefined}
                                          style={{
                                            position: 'absolute',
                                            left: `${pos.leftPct}%`,
                                            width: `${pos.widthPct}%`,
                                            top: 4 + lane * LANE_HEIGHT + 4,
                                            height: LANE_HEIGHT - 8,
                                            boxSizing: 'border-box',
                                            pointerEvents: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            padding: '2px 6px',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <span className="schedule-slot-text">
                                            {[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {(!effectiveScheduleObjects || effectiveScheduleObjects.length === 0) && (
                    <tr>
                      <td colSpan={monthDates.length + 1} style={{ padding: '1rem', color: '#666' }}>
                        {scheduleScheduleMode === 'template' ? 'Выберите объект и шаблон выше.' : 'Нет объектов.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
                );
              })()}
              {scheduleViewMode === 'hours' && (
              <table className="sports-schedule-table calendar-view schedule-view schedule-hours-view">
                <thead>
                  <tr>
                    <th className="sticky-col">Объект / Помещение</th>
                    {getScheduleHours().map((h) => (
                      <th key={h} className="schedule-hour-th">
                        {String(h).padStart(2, '0')}:00
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {effectiveScheduleObjects.map((obj) => {
                    const hasPremises = obj.premises && obj.premises.length > 0;
                    const isTemplateMode = scheduleScheduleMode === 'template';
                    return (
                      <React.Fragment key={obj.id}>
                        <tr className="schedule-row schedule-row-object">
                          <td className="sticky-col schedule-cell-premise schedule-cell-object">
                            {hasPremises && (
                              <button
                                type="button"
                                className="schedule-collapse-btn"
                                title={collapsedScheduleObjects.has(obj.id) ? 'Развернуть' : 'Свернуть'}
                                onClick={() => toggleScheduleObjectCollapsed(obj.id)}
                              >
                                <svg className={`schedule-collapse-icon ${collapsedScheduleObjects.has(obj.id) ? 'collapsed' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            <span className="schedule-object-name">{obj.name}</span>
                            {userRole !== 'user' && !isTemplateMode && (
                              <span className="schedule-object-icons">
                                <button type="button" className="icon-btn add-event-btn" title="Добавить помещение" onClick={() => handleAddPremise(obj.id)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/>
                                    <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                    <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                  </svg>
                                </button>
                                {hasPremises && (
                                  <button type="button" className="icon-btn" title="Убрать помещение (последнее)" onClick={() => { const last = obj.premises[obj.premises.length - 1]; if (last) handleDeletePremise(last.id); }}>
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                      <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                          {hasPremises ? getScheduleHours().map((h) => <td key={h} className="schedule-cell calendar-cell schedule-cell-empty" />) : (
                            <td colSpan={17} className="schedule-cell-empty schedule-cell-object-as-premise">
                              <span className="schedule-no-premises">(сам объект как помещение)</span>
                            </td>
                          )}
                        </tr>
                        {hasPremises && !collapsedScheduleObjects.has(obj.id) && obj.premises.map((prem) => (
                          <tr key={`${obj.id}-${prem.id}`} className="schedule-row schedule-row-premise">
                            <td className="sticky-col schedule-cell-premise">
                              <span className="schedule-premise-name">{prem.name}</span>
                              {userRole !== 'user' && !isTemplateMode && (
                                <button type="button" className="icon-btn" title="Убрать помещение" onClick={() => handleDeletePremise(prem.id)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7.5" stroke="#d32f2f" strokeWidth="1.2"/>
                                    <path d="M7 10H13" stroke="#d32f2f" strokeWidth="1.2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              )}
                            </td>
                            {(() => {
                              const slotsWithLanes = getSlotsWithLanes(prem.id, scheduleDay, effectiveScheduleSlots);
                              const numLanes = slotsWithLanes.length ? Math.max(...slotsWithLanes.map((s) => (s._lane ?? 0))) + 1 : 1;
                              const trackMinHeight = 8 + numLanes * LANE_HEIGHT;
                              const dayNorm = new Date(scheduleDay.getFullYear(), scheduleDay.getMonth(), scheduleDay.getDate());
                              const dayOfWeekForTemplate = scheduleDay.getDay() === 0 ? 7 : scheduleDay.getDay();
                              const isDragOver = !isTemplateMode && scheduleDragOver && scheduleDragOver.premId === prem.id && scheduleDragOver.day.getTime() === dayNorm.getTime();
                              const isTemplateDragOverDay = isTemplateMode && templateSlotDragOver && templateSlotDragOver.premId === prem.id && templateSlotDragOver.dayOfWeek === dayOfWeekForTemplate;
                              return (
                            <td
                              colSpan={17}
                              className={`schedule-day-track-cell ${isDragOver || isTemplateDragOverDay ? 'schedule-cell-drag-over' : ''}`}
                              style={{
                                padding: 0,
                                overflow: 'hidden',
                                verticalAlign: 'middle',
                                minHeight: trackMinHeight,
                              }}
                              onDoubleClick={userRole !== 'user' && isTemplateMode && templateObjectId ? (e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const hour = rect.width > 0 ? Math.floor((x / rect.width) * 17) + 7 : 8;
                                const h = Math.max(7, Math.min(22, hour));
                                openTemplateSlotModalForCell(prem.id, dayOfWeekForTemplate, `${String(h).padStart(2, '0')}:00`, `${String(h + 1).padStart(2, '0')}:00`);
                              } : undefined}
                              onDragOver={userRole !== 'user' ? (e) => { if (isTemplateMode) { e.preventDefault(); handleTemplateSlotDragOver(e, prem.id, dayOfWeekForTemplate); } else handleScheduleSlotDragOver(e, prem.id, dayNorm); } : undefined}
                              onDragLeave={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragLeave(e, prem.id, dayOfWeekForTemplate); else handleScheduleSlotDragLeave(e, prem.id, dayNorm); } : undefined}
                              onDrop={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDrop(e, prem.id, dayOfWeekForTemplate); else handleScheduleSlotDrop(e, prem.id, dayNorm); } : undefined}
                            >
                              <div
                                className="schedule-day-track-wrap"
                                style={{
                                  position: 'relative',
                                  width: '100%',
                                  height: trackMinHeight,
                                  minHeight: trackMinHeight,
                                  margin: '0 auto',
                                }}
                              >
                                <div className="schedule-day-track" style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                                  {getScheduleHours().map((h) => (
                                    <div
                                      key={h}
                                    className={`schedule-day-hour-cell ${userRole !== 'user' ? 'schedule-cell-clickable' : ''}`}
                                    style={{ flex: 1, minWidth: 0, borderRight: '1px solid #e0e0e0' }}
                                    onDoubleClick={userRole !== 'user' ? (e) => {
                                      e.stopPropagation();
                                      if (isTemplateMode && templateObjectId) openTemplateSlotModalForCell(prem.id, dayOfWeekForTemplate, `${String(h).padStart(2, '0')}:00`, `${String(h + 1).padStart(2, '0')}:00`);
                                      else openSlotModalForCell(prem.id, scheduleDay, h);
                                    } : undefined}
                                    title={userRole !== 'user' ? 'Двойной клик — добавить слот' : undefined}
                                    role={userRole !== 'user' ? 'button' : undefined}
                                    />
                                  ))}
                                </div>
                                {slotsWithLanes.map((slot) => {
                                const pos = getSlotPositionInDay(slot, scheduleDay);
                                if (!pos || pos.widthPct <= 0) return null;
                                const lane = slot._lane ?? 0;
                                return (
                                  <div
                                    key={slot.id}
                                    className={`schedule-slot calendar-event schedule-slot-cross ${userRole !== 'user' ? 'schedule-slot-clickable' : ''}`}
                                    title={getSlotTooltipText(slot, userRole !== 'user')}
                                    draggable={userRole !== 'user' && (isTemplateMode ? (typeof slot.id !== 'string' || !String(slot.id).startsWith('draft-')) : true)}
                                    onDragStart={userRole !== 'user' ? (e) => { if (isTemplateMode) handleTemplateSlotDragStart(e, slot); else handleScheduleSlotDragStart(e, slot); } : undefined}
                                    onDragEnd={userRole !== 'user' ? () => { if (isTemplateMode) handleTemplateSlotDragEnd(); else handleScheduleSlotDragEnd(); } : undefined}
                                    onClick={userRole !== 'user' ? (e) => e.stopPropagation() : undefined}
                                    onDoubleClick={userRole !== 'user' ? (e) => {
                                      e.stopPropagation();
                                      if (isTemplateMode && templateObjectId) {
                                        setEditingTemplateSlot(slot);
                                        const dow = slot.day_of_week || 1;
                                        const daysOfWeekEdit = [false, false, false, false, false, false, false];
                                        if (dow >= 1 && dow <= 7) daysOfWeekEdit[dow - 1] = true;
                                        setTemplateSlotForm({ sectionId: slot.section_id, premiseId: slot.premise_id, day_of_week: dow, time_start: (slot.time_start || '08:00').toString().slice(0, 8), time_end: (slot.time_end || '09:00').toString().slice(0, 8), organizationId: '', sportTypeId: '', coachId: '', daysOfWeek: daysOfWeekEdit });
                                        setShowTemplateSlotModal(true);
                                      } else openSlotEditModal(slot);
                                    } : undefined}
                                    role={userRole !== 'user' ? 'button' : undefined}
                                    style={{
                                      position: 'absolute',
                                      left: `${pos.leftPct}%`,
                                      width: `${pos.widthPct}%`,
                                      top: 4 + lane * LANE_HEIGHT + 4,
                                      height: LANE_HEIGHT - 8,
                                      boxSizing: 'border-box',
                                      pointerEvents: 'auto',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      padding: '2px 6px',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <span className="schedule-slot-text">
                                      {[slot.section_institution, slot.section_coach, slot.section_name].filter(Boolean).join(' ')}
                                    </span>
                                  </div>
                                );
                              })}
                              </div>
                            </td>
                              );
                            })()}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {(!effectiveScheduleObjects || effectiveScheduleObjects.length === 0) && (
                    <tr>
                      <td colSpan={18} style={{ padding: '1rem', color: '#666' }}>
                        {scheduleScheduleMode === 'template' ? 'Выберите объект и шаблон выше.' : 'Нет объектов. Добавьте объекты во вкладке «Мероприятия».'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
              <div className="schedule-summary" style={{ marginTop: '1rem', fontSize: '14px', color: '#555' }}>
                Секций: {scheduleData.sections?.length || 0}. Слотов в расписании: {scheduleScheduleMode === 'template' ? templateSlots.length : (scheduleData.slots?.length || 0)}.
              </div>

              {/* Модалка: слот шаблона (добавить/редактировать) */}
              {showTemplateSlotModal && templateObjectId && (
                <div className="modal-overlay" onClick={() => { setShowTemplateSlotModal(false); setEditingTemplateSlot(null); }}>
                  <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()}>
                    <h2>{editingTemplateSlot ? 'Редактировать слот шаблона' : 'Добавить слот в шаблон'}</h2>
                    <div className="form-group">
                      <label>Организация *</label>
                      <select
                        value={templateSlotForm.organizationId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setTemplateSlotForm((f) => ({ ...f, organizationId: id, sportTypeId: '', coachId: '' }));
                          if (id) referencesAPI.getSportTypes(id).then(setSlotModalSportTypes).catch(() => setSlotModalSportTypes([]));
                          else setSlotModalSportTypes([]);
                          setSlotModalCoaches([]);
                        }}
                        disabled={!!editingTemplateSlot}
                      >
                        <option value="">— выберите —</option>
                        {slotModalOrgs.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Вид спорта *</label>
                      <select
                        value={templateSlotForm.sportTypeId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setTemplateSlotForm((f) => ({ ...f, sportTypeId: id, coachId: '' }));
                          if (id) referencesAPI.getCoaches(id).then(setSlotModalCoaches).catch(() => setSlotModalCoaches([]));
                          else setSlotModalCoaches([]);
                        }}
                        disabled={!!editingTemplateSlot || !templateSlotForm.organizationId}
                      >
                        <option value="">— выберите —</option>
                        {slotModalSportTypes.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Тренер</label>
                      <select
                        value={templateSlotForm.coachId}
                        onChange={(e) => setTemplateSlotForm((f) => ({ ...f, coachId: e.target.value }))}
                        disabled={!!editingTemplateSlot || !templateSlotForm.sportTypeId}
                      >
                        <option value="">— не выбран —</option>
                        {slotModalCoaches.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Помещение *</label>
                      <select
                        value={templateSlotForm.premiseId}
                        onChange={(e) => setTemplateSlotForm((f) => ({ ...f, premiseId: Number(e.target.value) }))}
                        disabled={!!editingTemplateSlot}
                      >
                        {(selectedScheduleObject?.premises || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Дни недели (повторяющееся событие)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' }}>
                        {(templateSlotForm.daysOfWeek || Array(7).fill(false)).map((checked, i) => (
                          <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={!!checked}
                              onChange={(e) => {
                                const prev = templateSlotForm.daysOfWeek || Array(7).fill(false);
                                const next = editingTemplateSlot
                                  ? (e.target.checked ? prev.map((_, j) => j === i) : prev)
                                  : [...prev];
                                if (!editingTemplateSlot) next[i] = e.target.checked;
                                setTemplateSlotForm((f) => ({ ...f, daysOfWeek: next, day_of_week: i + 1 }));
                              }}
                              disabled={!!editingTemplateSlot}
                            />
                            <span>{daysOfWeekShort[i]}</span>
                          </label>
                        ))}
                      </div>
                      {editingTemplateSlot && <span style={{ fontSize: '0.85em', color: '#666' }}>При редактировании меняется только один слот (выбранный день).</span>}
                    </div>
                    <div className="form-group">
                      <label>Время начала</label>
                      <input type="time" value={(templateSlotForm.time_start || '').slice(0, 5)} onChange={(e) => setTemplateSlotForm((f) => ({ ...f, time_start: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Время окончания</label>
                      <input type="time" value={(templateSlotForm.time_end || '').slice(0, 5)} onChange={(e) => setTemplateSlotForm((f) => ({ ...f, time_end: e.target.value }))} />
                    </div>
                    <div className="modal-buttons" style={{ marginTop: 16 }}>
                      <button type="button" className="add-btn" onClick={saveTemplateSlot}>Сохранить</button>
                      {editingTemplateSlot && (
                        <button type="button" className="cancel-button" onClick={() => { deleteTemplateSlot(editingTemplateSlot); setShowTemplateSlotModal(false); setEditingTemplateSlot(null); }}>Удалить</button>
                      )}
                      <button type="button" className="cancel-button" onClick={() => { setShowTemplateSlotModal(false); setEditingTemplateSlot(null); }}>Отмена</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Модалка: новая секция */}
              {showSectionModal && (
                <div className="modal-overlay" onClick={() => { logAction('section_modal_close', 'overlay'); setShowSectionModal(false); }}>
                  <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()}>
                    <h2>Добавить секцию</h2>
                    <form onSubmit={handleAddSection}>
                      <div className="form-group">
                        <label>Название секции *</label>
                        <input type="text" value={sectionForm.name} onChange={(e) => setSectionForm((f) => ({ ...f, name: e.target.value }))} placeholder="Бокс, Плавание, Карате" required />
                      </div>
                      <div className="form-group">
                        <label>Тренер (ФИО)</label>
                        <input type="text" value={sectionForm.coach} onChange={(e) => setSectionForm((f) => ({ ...f, coach: e.target.value }))} placeholder="Иванов А." />
                      </div>
                      <div className="form-group">
                        <label>Учреждение</label>
                        <input type="text" value={sectionForm.institution} onChange={(e) => setSectionForm((f) => ({ ...f, institution: e.target.value }))} placeholder="СШОР, КСШ" />
                      </div>
                      <div className="modal-buttons">
                        <button type="submit" className="save-button">Сохранить</button>
                        <button type="button" className="cancel-button" onClick={() => { logAction('section_modal_close', 'cancel'); setShowSectionModal(false); }}>Отмена</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Модалка: новый слот расписания */}
              {showSlotModal && (
                <div className="modal-overlay" onClick={() => { logAction('slot_modal_close', 'overlay'); setShowSlotModal(false); }}>
                  <div className="modal-content schedule-modal slot-modal-wide" onClick={(e) => e.stopPropagation()}>
                    <h2>Добавить слот в расписание</h2>
                    <form onSubmit={handleAddSlot}>
                      <div className="form-group">
                        <label>Организация *</label>
                        <select
                          value={slotForm.organizationId}
                          onChange={(e) => {
                            const id = e.target.value;
                            setSlotForm((f) => ({ ...f, organizationId: id, sportTypeId: '', coachId: '' }));
                            setSlotModalCoaches([]);
                            if (id) referencesAPI.getSportTypes(id).then(setSlotModalSportTypes).catch(() => setSlotModalSportTypes([]));
                            else setSlotModalSportTypes([]);
                          }}
                          required
                        >
                          <option value="">— выбрать —</option>
                          {slotModalOrgs.map((o) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Вид спорта *</label>
                        <select
                          value={slotForm.sportTypeId}
                          onChange={(e) => {
                            const id = e.target.value;
                            setSlotForm((f) => ({ ...f, sportTypeId: id, coachId: '' }));
                            if (id) referencesAPI.getCoaches(id).then(setSlotModalCoaches).catch(() => setSlotModalCoaches([]));
                            else setSlotModalCoaches([]);
                          }}
                          required
                          disabled={!slotForm.organizationId}
                        >
                          <option value="">— выбрать —</option>
                          {slotModalSportTypes.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Тренер</label>
                        <select
                          value={slotForm.coachId}
                          onChange={(e) => setSlotForm((f) => ({ ...f, coachId: e.target.value }))}
                          disabled={!slotForm.sportTypeId}
                        >
                          <option value="">— не выбран —</option>
                          {slotModalCoaches.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Помещение *</label>
                        <select value={slotForm.premiseId} onChange={(e) => setSlotForm((f) => ({ ...f, premiseId: e.target.value }))} required>
                          <option value="">— выбрать —</option>
                          {(scheduleData.objects || []).flatMap((o) => (o.premises || []).map((p) => ({ ...p, objectName: o.name }))).map((p) => (
                            <option key={p.id} value={p.id}>{p.objectName} — {p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Начало *</label>
                        <input type="datetime-local" value={slotForm.startDate} onChange={(e) => setSlotForm((f) => ({ ...f, startDate: e.target.value }))} required />
                      </div>
                      <div className="form-group">
                        <label>Окончание *</label>
                        <input type="datetime-local" value={slotForm.endDate} onChange={(e) => setSlotForm((f) => ({ ...f, endDate: e.target.value }))} required />
                      </div>
                      <div className="modal-buttons">
                        <button type="submit" className="save-button">Сохранить</button>
                        <button type="button" className="cancel-button" onClick={() => { logAction('slot_modal_close', 'cancel'); setShowSlotModal(false); }}>Отмена</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Модалка: редактирование / удаление слота */}
              {editingSlot && (
                <div className="modal-overlay" onClick={() => { logAction('slot_edit_modal_close', 'overlay'); setEditingSlot(null); }}>
                  <div className="modal-content schedule-modal slot-modal-wide" onClick={(e) => e.stopPropagation()}>
                    <h2>Редактировать слот</h2>
                    <div className="schedule-slot-info" style={{ marginBottom: '1rem', padding: '0.5rem', background: '#f0f4f8', borderRadius: 4 }}>
                      <div><strong>Секция:</strong> {editingSlot.section_name}{editingSlot.section_coach || editingSlot.section_institution ? ` / ${[editingSlot.section_coach, editingSlot.section_institution].filter(Boolean).join(' / ')}` : ''}</div>
                      <div style={{ marginTop: 4 }}>
                        <strong>Помещение:</strong>{' '}
                        {(() => {
                          const p = (scheduleData.objects || []).flatMap((o) => (o.premises || []).map((pr) => ({ ...pr, objectName: o.name }))).find((pr) => pr.id === editingSlot.premise_id);
                          return p ? `${p.objectName} — ${p.name}` : '—';
                        })()}
                      </div>
                    </div>
                    <form onSubmit={handleUpdateSlot}>
                      <div className="form-group">
                        <label>Начало *</label>
                        <input
                          type="datetime-local"
                          value={editSlotForm.startDate}
                          onChange={(e) => setEditSlotForm((f) => ({ ...f, startDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Окончание *</label>
                        <input
                          type="datetime-local"
                          value={editSlotForm.endDate}
                          onChange={(e) => setEditSlotForm((f) => ({ ...f, endDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="modal-buttons" style={{ flexWrap: 'wrap', gap: 8 }}>
                        <button type="submit" className="save-button">Сохранить</button>
                        <button type="button" className="cancel-button" style={{ background: '#c62828', color: '#fff', border: 'none' }} onClick={handleDeleteSlot}>Удалить слот</button>
                        <button type="button" className="cancel-button" style={{ background: '#1565c0', color: '#fff', border: 'none' }} onClick={() => { logAction('slot_copy', { slotId: editingSlot.id }); setCopiedSlot(editingSlot); setEditingSlot(null); }}>Копировать слот</button>
                        <button type="button" className="cancel-button" onClick={() => { logAction('slot_edit_modal_close', 'cancel'); setEditingSlot(null); }}>Отмена</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Переключатель режима календаря */}
      {activeTab === 'events' && (
        <div className="calendar-modes">
          <button className={calendarMode === 'day' ? 'mode-btn active' : 'mode-btn'} onClick={() => setCalendarMode('day')}>День</button>
          <button className={calendarMode === 'week' ? 'mode-btn active' : 'mode-btn'} onClick={() => setCalendarMode('week')}>Неделя</button>
          <button className={calendarMode === 'month' ? 'mode-btn active' : 'mode-btn'} onClick={() => setCalendarMode('month')}>Месяц</button>
          <button className={calendarMode === 'year' ? 'mode-btn active' : 'mode-btn'} onClick={() => setCalendarMode('year')}>Год</button>
        </div>
      )}

      {/* Календарная сетка мероприятий */}
      {activeTab === 'events' && calendarMode === 'week' && (
        <>
          <div className="calendar-navigation">
            <button className="nav-button" onClick={() => {
              const prevWeek = new Date(weekStartDate);
              prevWeek.setDate(prevWeek.getDate() - 7);
              setWeekStartDate(prevWeek);
            }}>← Назад</button>
            <h3 className="calendar-navigation-title">{monthsOfYear[weekStartDate.getMonth()]} {weekStartDate.getFullYear()}</h3>
            <button className="nav-button" onClick={() => {
              const nextWeek = new Date(weekStartDate);
              nextWeek.setDate(nextWeek.getDate() + 7);
              setWeekStartDate(nextWeek);
            }}>Вперед →</button>
            {(userRole === 'admin' || userRole === 'manager' || userRole === 'superuser') && (
              <button className="nav-button" onClick={handleExportToExcel} disabled={exportLoading} style={{ marginLeft: 'auto' }}>
                {exportLoading ? 'Экспорт...' : 'Экспорт'}
              </button>
            )}
          </div>
          <table className="sports-schedule-table calendar-view week-view events-week-view">
        <thead>
          <tr>
                <th className="sticky-col">Объект</th>
                {getWeekDates(weekStartDate).map((date, idx) => (
                  <th key={idx} className={getDayClasses(date)}>{daysOfWeek[idx]}<br/>{date.toLocaleDateString()}</th>
                ))}
          </tr>
          <tr className="schedule-header-time-row">
                <th className="sticky-col" style={{ borderTop: 'none', paddingTop: 0 }} />
                <th colSpan={7} style={{ borderTop: 'none', padding: '2px 0', verticalAlign: 'top' }}>
                  {/* Одна временная шкала на всю неделю (Пн 7:00 – Вс 23:00) */}
                  {(() => {
                    const weekDates = getWeekDates(weekStartDate);
                    const weekStart = new Date(weekDates[0].getFullYear(), weekDates[0].getMonth(), weekDates[0].getDate(), 7, 0, 0, 0);
                    const weekEnd = new Date(weekDates[6].getFullYear(), weekDates[6].getMonth(), weekDates[6].getDate(), 23, 0, 0, 0);
                    const totalMs = weekEnd.getTime() - weekStart.getTime();
                    const tickEveryHours = 6;
                    const ticks = [];
                    if (totalMs > 0) {
                      const tickMs = tickEveryHours * 60 * 60 * 1000;
                      for (let t = weekStart.getTime(); t <= weekEnd.getTime(); t += tickMs) {
                        ticks.push(new Date(t));
                      }
                    }
                    return (
                      <div style={{ position: 'relative', height: 14 }}>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#666' }}>
                          {ticks.map((d) => {
                            const pct = totalMs > 0 ? ((d.getTime() - weekStart.getTime()) / totalMs) * 100 : 0;
                            const label = d.getHours().toString().padStart(2, '0') + ':00';
                            return (
                              <span key={d.toISOString()} style={{ position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)' }}>
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </th>
          </tr>
        </thead>
        <tbody>
          {data.map((obj, objIdx) => {
                const weekDates = getWeekDates(weekStartDate);
                const { segments, maxLanes } = getWeekEventSegmentsUnified(obj, weekDates);
                const weekStart = new Date(weekDates[0].getFullYear(), weekDates[0].getMonth(), weekDates[0].getDate(), 7, 0, 0, 0);
                const weekEnd = new Date(weekDates[6].getFullYear(), weekDates[6].getMonth(), weekDates[6].getDate(), 23, 0, 0, 0);
                const totalMs = weekEnd.getTime() - weekStart.getTime();
                const trackMinHeight = 8 + maxLanes * LANE_HEIGHT;
                return (
                  <tr key={obj.id} data-obj-idx={objIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                    <td className="sticky-col">
                        <div className="object-cell-vertical">
                          <div className="object-name">{obj.name}</div>
                          <div className="object-btns">
                            {userRole === 'admin' && (
                              <>
                                <button className="icon-btn" title="Редактировать объект" onClick={() => handleEditObject(objIdx)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V14.75C3 14.1977 3.44772 13.75 4 13.75H16C16.5523 13.75 17 14.1977 17 14.75V17.25C17 17.8023 16.5523 18.25 16 18.25H4C3.44772 18.25 3 17.8023 3 17.25Z" stroke="#1976d2" strokeWidth="1.2"/><path d="M7.5 10.5L12.5 5.5M13.5 6.5L14.5 7.5C15.0523 8.05228 15.0523 8.94772 14.5 9.5L10.5 13.5C10.2239 13.7761 9.77614 13.7761 9.5 13.5L8.5 12.5M13.5 6.5L11.5 4.5C10.9477 3.94772 10.0523 3.94772 9.5 4.5L5.5 8.5C5.22386 8.77614 5.22386 9.22386 5.5 9.5L7.5 11.5" stroke="#1976d2" strokeWidth="1.2"/></svg>
                                </button>
                                <button className="icon-btn" title="Удалить объект" onClick={() => handleDeleteObject(objIdx)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7" stroke="#d32f2f" strokeWidth="1.2"/></svg>
                                </button>
                              </>
                            )}
                            {userRole !== 'user' && (
                            <button
                              className="icon-btn add-event-btn"
                              title="Добавить мероприятие"
                              onClick={() => handleAddEvent(objIdx)}
                            >
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/>
                                <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                              </svg>
                            </button>
                            )}
                          </div>
                    </div>
                  </td>
                    <td
                      colSpan={7}
                      data-obj-idx={objIdx}
                      className={`calendar-cell events-week-unified-cell ${dragOverCell && dragOverCell.objIdx === objIdx && dragOverCell.mode === 'week_unified' ? 'drag-over' : ''}`}
                      style={{ padding: 0, verticalAlign: 'middle' }}
                      onDragOver={userRole !== 'user' ? (e) => {
                        e.preventDefault();
                        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                        setDragOverCell({ objIdx, date: weekDates[0], mode: 'week_unified' });
                      } : undefined}
                      onDragLeave={userRole !== 'user' ? handleDragLeave : undefined}
                      onDrop={userRole !== 'user' ? (e) => {
                        if (!totalMs || totalMs <= 0) return;
                        const target = e.currentTarget.querySelector('.events-week-unified-track');
                        if (!target) return;
                        const rect = target.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const pct = rect.width > 0 ? Math.max(0, Math.min(1, x / rect.width)) : 0;
                        const d = new Date(weekStart.getTime() + pct * totalMs);
                        handleDrop(e, d, objIdx);
                      } : undefined}
                    >
                      <div
                        className="events-week-unified-track"
                        style={{ position: 'relative', width: '100%', height: trackMinHeight, minHeight: trackMinHeight }}
                        onDoubleClick={userRole !== 'user' ? (e) => {
                          if (e.target.closest('.calendar-event')) return;
                          if (!totalMs || totalMs <= 0) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const pct = rect.width > 0 ? Math.max(0, Math.min(1, x / rect.width)) : 0;
                          const start = new Date(weekStart.getTime() + pct * totalMs);
                          setDefaultEventDate(start);
                          handleAddEvent(objIdx, start);
                        } : undefined}
                      >
                        {/* фон: границы дней + редкие тики по часу */}
                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                          {(() => {
                            if (!totalMs || totalMs <= 0) return null;
                            const lines = [];
                            // границы дней: Пн 07:00 (0%), затем каждый 00:00
                            const dayStarts = weekDates.map((d, idx) => (
                              idx === 0
                                ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 7, 0, 0, 0)
                                : new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
                            ));
                            dayStarts.forEach((d, idx) => {
                              const pct = ((d.getTime() - weekStart.getTime()) / totalMs) * 100;
                              if (pct < 0 || pct > 100) return;
                              lines.push(
                                <div key={`day-${idx}`} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 0, borderLeft: '2px solid rgba(0,0,0,0.08)' }} />
                              );
                            });
                            // каждые 3 часа — тонкая линия
                            const tickHours = 3;
                            const tickMs = tickHours * 60 * 60 * 1000;
                            for (let t = weekStart.getTime(); t <= weekEnd.getTime(); t += tickMs) {
                              const pct = ((t - weekStart.getTime()) / totalMs) * 100;
                              lines.push(
                                <div key={`tick-${t}`} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 0, borderLeft: '1px solid rgba(0,0,0,0.05)' }} />
                              );
                            }
                            return lines;
                          })()}
                        </div>

                        {segments.map((seg) => {
                          const lane = seg._lane ?? 0;
                          const isDraggingThis = isDragging && draggedEvent && draggedEvent.event.id === seg.event.id;
                          return (
                            <div
                              key={seg.event.id}
className={`calendar-event ${getEventStatusClasses(seg.event)} ${isDraggingThis ? 'dragging' : ''} ${dragArmedEventId === seg.event.id ? 'drag-armed' : ''} ${seg.event.objectIds && seg.event.objectIds[0] != null && Number(seg.event.objectIds[0]) === obj.id ? 'event-main' : ''}`}
                               style={{
                                 position: 'absolute',
                                 left: `${seg.leftPct}%`,
                                 width: `${seg.widthPct}%`,
                                 top: 4 + lane * LANE_HEIGHT + 4,
                                 height: LANE_HEIGHT - 8,
                                 background: getEventColor(seg.event.id, seg.event.name, seg.event.status),
                                 boxSizing: 'border-box',
                                 overflow: 'hidden',
                                 display: 'flex',
                                 alignItems: 'center',
                                 padding: '0 6px',
                                 minWidth: 0,
                                 textDecoration: seg.event.status === 'cancelled' ? 'line-through' : 'none',
                                 color: seg.event.status === 'cancelled' ? '#666' : undefined,
                                 opacity: seg.event.status === 'cancelled' ? 0.7 : undefined
                               }}
                               draggable={userRole !== 'user' && dragArmedEventId === seg.event.id}
                               onMouseDown={userRole !== 'user' ? (e) => { if (e.button !== 0) return; armDrag(seg.event.id); } : undefined}
                               onMouseUp={userRole !== 'user' ? disarmDrag : undefined}
                               onMouseEnter={(e) => { e.stopPropagation(); if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current); const rect = e.currentTarget.getBoundingClientRect(); tooltipTimeoutRef.current = setTimeout(() => { setEventTooltip({ event: seg.event, x: rect.left + rect.width / 2, y: rect.top, objectName: obj.name || '' }); }, 200); }}
                              onMouseLeave={() => { if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current); setEventTooltip(null); }}
                              onDragStart={userRole !== 'user' ? (e) => handleDragStart(e, seg.event, objIdx) : undefined}
                              onDragEnd={userRole !== 'user' ? handleDragEnd : undefined}
                              onDoubleClick={userRole !== 'user' ? (e) => { e.preventDefault(); e.stopPropagation(); handleEditEvent(objIdx, seg.event); } : undefined}
                            >
                              <div className="event-title" style={{ flex: 1, paddingRight: seg.event.cloudUrl ? '24px' : 0 }}>{seg.event.name}</div>
                              {seg.event.cloudUrl && (
                                <a href={seg.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" onClick={(e) => e.stopPropagation()}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Режим «День»: один день, длина события по времени в ячейке */}
      {activeTab === 'events' && calendarMode === 'day' && (
        <>
          <div className="calendar-navigation">
            <button className="nav-button" onClick={() => {
              const prev = new Date(eventDayDate);
              prev.setDate(prev.getDate() - 1);
              setEventDayDate(prev);
            }}>← День</button>
            <h3 className="calendar-navigation-title">{eventDayDate.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
            <button className="nav-button" onClick={() => {
              const next = new Date(eventDayDate);
              next.setDate(next.getDate() + 1);
              setEventDayDate(next);
            }}>День →</button>
            {(userRole === 'admin' || userRole === 'manager' || userRole === 'superuser') && (
              <button className="nav-button" onClick={handleExportToExcel} disabled={exportLoading} style={{ marginLeft: 'auto' }}>
                {exportLoading ? 'Экспорт...' : 'Экспорт'}
              </button>
            )}
          </div>
          <table className="sports-schedule-table calendar-view day-view events-day-view">
            <thead>
              <tr>
                <th className="sticky-col">Объект</th>
                <th className="events-day-time-col">
                  <div className="events-day-time-track" style={{ display: 'flex' }}>
                    {getScheduleHours().map((h) => (
                      <div key={h} className="events-day-hour" style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 11 }}>{h}</div>
                    ))}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((obj, objIdx) => {
                const eventsWithLanes = getEventsWithLanesForDay(obj, eventDayDate);
                const numLanes = eventsWithLanes.length ? Math.max(...eventsWithLanes.map((e) => e._lane ?? 0)) + 1 : 1;
                const trackMinHeight = 8 + numLanes * EVENT_DAY_LANE_HEIGHT;
                return (
                  <tr key={obj.id} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                    <td className="sticky-col">
                      <div className="object-cell-vertical">
                        <div className="object-name">{obj.name}</div>
                        {userRole === 'admin' && (
                          <div className="object-btns">
                            <button className="icon-btn" title="Редактировать объект" onClick={() => handleEditObject(objIdx)}>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V14.75C3 14.1977 3.44772 13.75 4 13.75H16C16.5523 13.75 17 14.1977 17 14.75V17.25C17 17.8023 16.5523 18.25 16 18.25H4C3.44772 18.25 3 17.8023 3 17.25Z" stroke="#1976d2" strokeWidth="1.2"/><path d="M7.5 10.5L12.5 5.5M13.5 6.5L14.5 7.5C15.0523 8.05228 15.0523 8.94772 14.5 9.5L10.5 13.5C10.2239 13.7761 9.77614 13.7761 9.5 13.5L8.5 12.5M13.5 6.5L11.5 4.5C10.9477 3.94772 10.0523 3.94772 9.5 4.5L5.5 8.5C5.22386 8.77614 5.22386 9.22386 5.5 9.5L7.5 11.5" stroke="#1976d2" strokeWidth="1.2"/></svg>
                            </button>
                            <button className="icon-btn" title="Удалить объект" onClick={() => handleDeleteObject(objIdx)}>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7" stroke="#d32f2f" strokeWidth="1.2"/></svg>
                            </button>
                          </div>
                        )}
                        {userRole !== 'user' && (
                          <button className="icon-btn add-event-btn" title="Добавить мероприятие" onClick={() => handleAddEvent(objIdx, eventDayDate)}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/><path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/><path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/></svg>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="events-day-track-cell" style={{ padding: 0, verticalAlign: 'middle' }}>
                      <div
                        className="events-day-track-wrap"
                        style={{ position: 'relative', width: '100%', height: trackMinHeight, minHeight: trackMinHeight }}
                        onDoubleClick={userRole !== 'user' ? (e) => {
                          if (e.target.closest('.calendar-event')) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const hour = rect.width > 0 ? Math.floor((x / rect.width) * 17) + 7 : 8;
                          const h = Math.max(7, Math.min(22, hour));
                          const start = new Date(eventDayDate.getFullYear(), eventDayDate.getMonth(), eventDayDate.getDate(), h, 0, 0, 0);
                          setDefaultEventDate(start);
                          handleAddEvent(objIdx, start);
                        } : undefined}
                      >
                        <div className="events-day-hour-track" style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                          {getScheduleHours().map((h) => (
                            <div key={h} style={{ flex: 1, minWidth: 0, borderRight: '1px solid #e0e0e0' }} />
                          ))}
                        </div>
                        {eventsWithLanes.map((item) => {
                          const lane = item._lane ?? 0;
                          const isDraggingThis = isDragging && draggedEvent && draggedEvent.event.id === item.event.id;
                          return (
                            <div
                              key={item.event.id}
                              className={`calendar-event ${getEventStatusClasses(item.event)} ${isDraggingThis ? 'dragging' : ''} ${dragArmedEventId === item.event.id ? 'drag-armed' : ''} ${item.event.objectIds && item.event.objectIds[0] != null && Number(item.event.objectIds[0]) === obj.id ? 'event-main' : ''}`}
style={{
  position: 'absolute',
  left: `${item.leftPct}%`,
  width: `${item.widthPct}%`,
  top: 4 + lane * EVENT_DAY_LANE_HEIGHT + 4,
  height: EVENT_DAY_LANE_HEIGHT - 8,
  background: getEventColor(item.event.id, item.event.name, item.event.status),
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  padding: '0 6px',
  minWidth: 0,
  textDecoration: item.event.status === 'cancelled' ? 'line-through' : 'none',
  color: item.event.status === 'cancelled' ? '#666' : undefined,
  opacity: item.event.status === 'cancelled' ? 0.7 : undefined
}}
                              draggable={userRole !== 'user' && dragArmedEventId === item.event.id}
                              onMouseDown={userRole !== 'user' ? (e) => { if (e.button !== 0) return; armDrag(item.event.id); } : undefined}
                              onMouseUp={userRole !== 'user' ? disarmDrag : undefined}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltipTimeoutRef.current = setTimeout(() => {
                                  setEventTooltip({ event: item.event, x: rect.left + rect.width / 2, y: rect.top, objectName: obj.name || '' });
                                }, 200);
                              }}
                              onMouseLeave={() => { if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current); setEventTooltip(null); }}
                              onDragStart={userRole !== 'user' ? (e) => handleDragStart(e, item.event, objIdx) : undefined}
                              onDragEnd={userRole !== 'user' ? handleDragEnd : undefined}
                              onDoubleClick={userRole !== 'user' ? (e) => { e.preventDefault(); e.stopPropagation(); handleEditEvent(objIdx, item.event); } : undefined}
                            >
                              <div className="event-title" style={{ flex: 1, paddingRight: item.event.cloudUrl ? '24px' : 0 }}>{item.event.name}</div>
                              {item.event.cloudUrl && (
                                <a href={item.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" onClick={(e) => e.stopPropagation()}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Кнопка добавить объект под таблицей, слева (только для admin) */}
      {activeTab === 'events' && (calendarMode === 'day' || calendarMode === 'week' || calendarMode === 'month' || calendarMode === 'year') && userRole === 'admin' && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', marginLeft: '8px' }}>
          <button className="icon-btn add-object-btn" title="Добавить объект" onClick={handleAddObject}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="10" stroke="#1976d2" strokeWidth="1.5"/><path d="M11 7V15" stroke="#1976d2" strokeWidth="1.5" strokeLinecap="round"/><path d="M7 11H15" stroke="#1976d2" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
          <span style={{ fontSize: 12, color: '#1976d2', marginLeft: 6 }}>Добавить объект</span>
        </div>
      )}

      {/* Месячный режим */}
      {activeTab === 'events' && calendarMode === 'month' && (
        <>
          <div className="calendar-navigation">
            <button className="nav-button" onClick={() => {
              const prevMonth = new Date(monthStartDate);
              prevMonth.setMonth(prevMonth.getMonth() - 1);
              setMonthStartDate(prevMonth);
            }}>← Назад</button>
            <h3 className="calendar-navigation-title">{monthsOfYear[monthStartDate.getMonth()]} {monthStartDate.getFullYear()}</h3>
            <button className="nav-button" onClick={() => {
              const nextMonth = new Date(monthStartDate);
              nextMonth.setMonth(nextMonth.getMonth() + 1);
              setMonthStartDate(nextMonth);
            }}>Вперед →</button>
            {(userRole === 'admin' || userRole === 'manager' || userRole === 'superuser') && (
              <button className="nav-button" onClick={handleExportToExcel} disabled={exportLoading} style={{ marginLeft: 'auto' }}>
                {exportLoading ? 'Экспорт...' : 'Экспорт'}
              </button>
            )}
          </div>
          <table className="sports-schedule-table calendar-view month-view">
            <thead>
              <tr>
                <th className="sticky-col">Объект</th>
                {getMonthDates(monthStartDate).map((date, idx) => {
                  const dayOfWeek = date.getDay(); // 0 = воскресенье, 6 = суббота
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const weekendLabel = dayOfWeek === 6 ? 'сб' : dayOfWeek === 0 ? 'вс' : '';
                  return (
                    <th key={idx} className={getDayClasses(date)} style={{ cursor: 'pointer' }} onClick={() => handleDayClick(date)}>
                      <div className="month-day-header">
                        {date.getDate()}
                        {isWeekend && <span className="weekend-label">{weekendLabel}</span>}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.map((obj, objIdx) => {
                const monthDates = getMonthDates(monthStartDate);
                const { segments, maxLanes } = getMonthEventSegmentsUnified(obj, monthStartDate);
                const daysInMonth = monthDates.length || 1;
                const trackMinHeight = 8 + maxLanes * LANE_HEIGHT;
                const monthStart = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth(), 1, 0, 0, 0, 0);
                const monthEnd = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth(), daysInMonth, 23, 59, 59, 999);
                const totalMs = monthEnd.getTime() - monthStart.getTime();

                return (
                  <tr key={obj.id + '-month-unified'} data-obj-idx={objIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                    <td className="sticky-col">
                      <div className="object-cell-vertical">
                        <div className="object-name">{obj.name}</div>
                        <div className="object-btns">
                          {userRole === 'admin' && (
                            <>
                              <button className="icon-btn" title="Редактировать объект" onClick={() => handleEditObject(objIdx)}>
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V14.75C3 14.1977 3.44772 13.75 4 13.75H16C16.5523 13.75 17 14.1977 17 14.75V17.25C17 17.8023 16.5523 18.25 16 18.25H4C3.44772 18.25 3 17.8023 3 17.25Z" stroke="#1976d2" strokeWidth="1.2"/><path d="M7.5 10.5L12.5 5.5M13.5 6.5L14.5 7.5C15.0523 8.05228 15.0523 8.94772 14.5 9.5L10.5 13.5C10.2239 13.7761 9.77614 13.7761 9.5 13.5L8.5 12.5M13.5 6.5L11.5 4.5C10.9477 3.94772 10.0523 3.94772 9.5 4.5L5.5 8.5C5.22386 8.77614 5.22386 9.22386 5.5 9.5L7.5 11.5" stroke="#1976d2" strokeWidth="1.2"/></svg>
                              </button>
                              <button className="icon-btn" title="Удалить объект" onClick={() => handleDeleteObject(objIdx)}>
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7" stroke="#d32f2f" strokeWidth="1.2"/></svg>
                              </button>
                            </>
                          )}
                          {userRole !== 'user' && (
                            <button
                              className="icon-btn add-event-btn"
                              title="Добавить мероприятие"
                              onClick={() => handleAddEvent(objIdx)}
                            >
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6" />
                                <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round" />
                                <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td
                      colSpan={monthDates.length}
                      data-obj-idx={objIdx}
                      className={`calendar-cell events-month-unified-cell ${dragOverCell && dragOverCell.objIdx === objIdx && dragOverCell.mode === 'month_unified' ? 'drag-over' : ''}`}
                      style={{ padding: 0, verticalAlign: 'middle' }}
                      onDragOver={userRole !== 'user' ? (e) => {
                        e.preventDefault();
                        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                        setDragOverCell({ objIdx, date: monthStart, mode: 'month_unified' });
                      } : undefined}
                      onDragLeave={userRole !== 'user' ? handleDragLeave : undefined}
                      onDrop={userRole !== 'user' ? (e) => {
                        if (!totalMs || totalMs <= 0) return;
                        const target = e.currentTarget.querySelector('.events-month-unified-track');
                        if (!target) return;
                        const rect = target.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const pct = rect.width > 0 ? Math.max(0, Math.min(1, x / rect.width)) : 0;
                        const d = new Date(monthStart.getTime() + pct * totalMs);
                        handleDrop(e, d, objIdx);
                      } : undefined}
                    >
                      <div
                        className="events-month-unified-track"
                        style={{ position: 'relative', width: '100%', height: trackMinHeight, minHeight: trackMinHeight }}
                        onDoubleClick={userRole !== 'user' ? (e) => {
                          if (e.target.closest('.calendar-event')) return;
                          if (!totalMs || totalMs <= 0) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const pct = rect.width > 0 ? Math.max(0, Math.min(1, x / rect.width)) : 0;
                          const d = new Date(monthStart.getTime() + pct * totalMs);
                          const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 10, 0, 0, 0);
                          setDefaultEventDate(start);
                          handleAddEvent(objIdx, start);
                        } : undefined}
                      >
                        {/* фон: границы дней месяца */}
                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                          {monthDates.map((d, idx) => {
                            const pct = (idx / daysInMonth) * 100;
                            return (
                              <div key={`day-${idx}`} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 0, borderLeft: '1px solid rgba(0,0,0,0.06)' }} />
                            );
                          })}
                        </div>
                        {segments.map((seg) => {
                          const lane = seg._lane ?? 0;
                          const isDraggingThis = isDragging && draggedEvent && draggedEvent.event.id === seg.event.id;
                          return (
                            <div
                              key={seg.event.id}
                              data-event-id={seg.event.id}
                              className={`calendar-event ${getEventStatusClasses(seg.event)} ${isDraggingThis ? 'dragging' : ''} ${dragArmedEventId === seg.event.id ? 'drag-armed' : ''} ${seg.event.objectIds && seg.event.objectIds[0] != null && Number(seg.event.objectIds[0]) === obj.id ? 'event-main' : ''} ${seg.event.id === highlightEventId ? 'event-highlighted' : ''}`}
                              style={{
  position: 'absolute',
  left: `${seg.leftPct}%`,
  width: `${seg.widthPct}%`,
  top: 4 + lane * LANE_HEIGHT + 4,
  height: LANE_HEIGHT - 8,
  background: getEventColor(seg.event.id, seg.event.name, seg.event.status),
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  padding: '0 6px',
  minWidth: 0,
  textDecoration: seg.event.status === 'cancelled' ? 'line-through' : 'none',
  color: seg.event.status === 'cancelled' ? '#666' : undefined,
  opacity: seg.event.status === 'cancelled' ? 0.7 : undefined
}}
                              draggable={userRole !== 'user' && dragArmedEventId === seg.event.id}
                              onMouseDown={userRole !== 'user' ? (e) => { if (e.button !== 0) return; armDrag(seg.event.id); } : undefined}
                              onMouseUp={userRole !== 'user' ? disarmDrag : undefined}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltipTimeoutRef.current = setTimeout(() => {
                                  setEventTooltip({ event: seg.event, x: rect.left + rect.width / 2, y: rect.top, objectName: obj.name || '' });
                                }, 200);
                              }}
                              onMouseLeave={() => { if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current); setEventTooltip(null); }}
                              onDragStart={userRole !== 'user' ? (e) => handleDragStart(e, seg.event, objIdx) : undefined}
                              onDragEnd={userRole !== 'user' ? handleDragEnd : undefined}
                              onDoubleClick={userRole !== 'user' ? (e) => { e.preventDefault(); e.stopPropagation(); handleEditEvent(objIdx, seg.event); } : undefined}
                            >
                              <div className="event-title" style={{ flex: 1, paddingRight: seg.event.cloudUrl ? '24px' : 0 }}>{seg.event.name}</div>
                              {seg.event.cloudUrl && (
                                <a href={seg.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" onClick={(e) => e.stopPropagation()}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Годовой режим */}
      {activeTab === 'events' && calendarMode === 'year' && (
        <>
          <div className="calendar-navigation">
            <button className="nav-button" onClick={() => {
              const prevYear = new Date(yearStartDate);
              prevYear.setFullYear(prevYear.getFullYear() - 1);
              setYearStartDate(prevYear);
            }}>← Назад</button>
            <h3 className="calendar-navigation-title">{yearStartDate.getFullYear()}</h3>
            <button className="nav-button" onClick={() => {
              const nextYear = new Date(yearStartDate);
              nextYear.setFullYear(nextYear.getFullYear() + 1);
              setYearStartDate(nextYear);
            }}>Вперед →</button>
            {(userRole === 'admin' || userRole === 'manager' || userRole === 'superuser') && (
              <button className="nav-button" onClick={handleExportToExcel} disabled={exportLoading} style={{ marginLeft: 'auto' }}>
                {exportLoading ? 'Экспорт...' : 'Экспорт'}
              </button>
            )}
          </div>
          <table className="sports-schedule-table calendar-view year-view">
            <thead>
              <tr>
                <th className="sticky-col">Объект</th>
                {getYearMonths().map((month, idx) => (
                  <th key={idx} style={{cursor:'pointer'}} onClick={() => handleMonthClick(idx)}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((obj, objIdx) => {
                const months = getYearMonths();
                // Определяем границы года для фильтрации
                const yearStart = new Date(yearStartDate.getFullYear(), 0, 1);
                const yearEnd = new Date(yearStartDate.getFullYear(), 11, 31);
                yearEnd.setHours(23, 59, 59, 999);
                
                // --- СТЕКИНГ МЕРОПРИЯТИЙ ---
                // Фильтруем события по датам на этом объекте
                const filteredEvents = (obj.events || []).filter(event => {
                  const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  const eventStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                  const eventEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                  return !(eventEnd < yearStart || eventStart > yearEnd);
                });
                
                const uniqueEventsMap = new Map();
                filteredEvents.forEach(event => {
                  if (!uniqueEventsMap.has(event.id)) {
                    uniqueEventsMap.set(event.id, event);
                  }
                });
                const events = Array.from(uniqueEventsMap.values());
                // Сортируем по дате начала, затем по дате окончания (как при логике месячного режима — не пересекающиеся по датам попадают в одну строку)
                events.sort((a, b) => {
                  const { startDate: sa, endDate: ea } = getEventStartEndForObject(a, obj.id);
                  const { startDate: sb, endDate: eb } = getEventStartEndForObject(b, obj.id);
                  const tA = new Date(sa).getTime();
                  const tB = new Date(sb).getTime();
                  if (tA !== tB) return tA - tB;
                  return new Date(ea).getTime() - new Date(eb).getTime();
                });

                // Даты начала каждого месяца года (как в месячном режиме — по границам)
                const yearMonthStarts = months.map((_, idx) => new Date(yearStartDate.getFullYear(), idx, 1));
                const yearMonthStart = yearMonthStarts[0];
                const yearMonthEnd = new Date(yearStartDate.getFullYear(), 11, 31);
                yearMonthEnd.setHours(23, 59, 59, 999);
                const msPerDay = 24 * 60 * 60 * 1000;
                const lastDayOfYear = Math.floor((yearMonthEnd.getTime() - yearMonthStart.getTime()) / msPerDay);

                let tracks = [];
                events.forEach(event => {
                  const { startDate, endDate } = getEventStartEndForObject(event, obj.id);
                  const eventForCell = { ...event, startDate, endDate };
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                  const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                  // Индекс месяца для отрисовки (какой столбец занимает событие)
                  let startIdx = -1;
                  if (startDateOnly < yearMonthStart) {
                    startIdx = 0;
                  } else if (startDateOnly > yearMonthEnd) {
                    return;
                  } else {
                    startIdx = yearMonthStarts.findIndex((d, i) => {
                      const monthStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                      const nextStart = i < 11 ? new Date(yearStartDate.getFullYear(), i + 1, 1) : new Date(yearStartDate.getFullYear(), 11, 31, 23, 59, 59, 999);
                      return startDateOnly >= monthStart && startDateOnly < nextStart;
                    });
                  }
                  if (startIdx === -1) return;

                  let endIdx = -1;
                  if (endDateOnly > yearMonthEnd) {
                    endIdx = yearMonthStarts.length - 1;
                  } else if (endDateOnly < yearMonthStart) {
                    endIdx = startIdx;
                  } else {
                    endIdx = yearMonthStarts.findIndex((d, i) => {
                      const monthStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                      const nextStart = i < 11 ? new Date(yearStartDate.getFullYear(), i + 1, 1) : new Date(yearStartDate.getFullYear(), 11, 31, 23, 59, 59, 999);
                      return endDateOnly >= monthStart && endDateOnly < nextStart;
                    });
                    if (endIdx === -1) {
                      const firstAfter = yearMonthStarts.findIndex(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()) > endDateOnly);
                      endIdx = firstAfter === -1 ? yearMonthStarts.length - 1 : firstAfter - 1;
                    }
                  }
                  if (endIdx < startIdx) endIdx = startIdx;

                  // День года (0..lastDayOfYear) для проверки пересечения: одна полоса только если события не пересекаются по дням
                  const startDayOfYear = Math.max(0, Math.min(lastDayOfYear, Math.floor((startDateOnly.getTime() - yearMonthStart.getTime()) / msPerDay)));
                  const endDayOfYear = Math.max(0, Math.min(lastDayOfYear, Math.floor((endDateOnly.getTime() - yearMonthStart.getTime()) / msPerDay)));

                  let placed = false;
                  for (let t = 0; t < tracks.length; t++) {
                    let busy = false;
                    for (let e of tracks[t]) {
                      if (!(endDayOfYear < e.startDayOfYear || startDayOfYear > e.endDayOfYear)) {
                        busy = true;
                        break;
                      }
                    }
                    if (!busy) {
                      tracks[t].push({ event: eventForCell, startIdx, endIdx, startDayOfYear, endDayOfYear });
                      placed = true;
                      break;
                    }
                  }
                  if (!placed) {
                    tracks.push([{ event: eventForCell, startIdx, endIdx, startDayOfYear, endDayOfYear }]);
                  }
                });
                // Если нет мероприятий, создаем пустой track для отображения объекта
                if (tracks.length === 0) {
                  tracks = [[]];
                }
                const numTracks = Math.max(tracks.length, 1);
                return tracks.map((track, trackIdx) => {
                  const uniqueEvents = [];
                  const seenEventIds = new Set();
                  track.forEach(e => {
                    if (!seenEventIds.has(e.event.id)) {
                      seenEventIds.add(e.event.id);
                      uniqueEvents.push(e);
                    }
                  });
                  
                  return (
                  <tr key={obj.id + '-year-track-' + trackIdx} data-obj-idx={objIdx} className={objIdx % 2 === 0 ? 'object-row object-row-even' : 'object-row object-row-odd'}>
                    {trackIdx === 0 ? (
                      <td rowSpan={numTracks} className="sticky-col">
                        <div className="object-cell-vertical">
                          <div className="object-name">{obj.name}</div>
                          <div className="object-btns">
                            {userRole === 'admin' && (
                              <>
                                <button className="icon-btn" title="Редактировать объект" onClick={() => handleEditObject(objIdx)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V14.75C3 14.1977 3.44772 13.75 4 13.75H16C16.5523 13.75 17 14.1977 17 14.75V17.25C17 17.8023 16.5523 18.25 16 18.25H4C3.44772 18.25 3 17.8023 3 17.25Z" stroke="#1976d2" strokeWidth="1.2"/><path d="M7.5 10.5L12.5 5.5M13.5 6.5L14.5 7.5C15.0523 8.05228 15.0523 8.94772 14.5 9.5L10.5 13.5C10.2239 13.7761 9.77614 13.7761 9.5 13.5L8.5 12.5M13.5 6.5L11.5 4.5C10.9477 3.94772 10.0523 3.94772 9.5 4.5L5.5 8.5C5.22386 8.77614 5.22386 9.22386 5.5 9.5L7.5 11.5" stroke="#1976d2" strokeWidth="1.2"/></svg>
                                </button>
                                <button className="icon-btn" title="Удалить объект" onClick={() => handleDeleteObject(objIdx)}>
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7" stroke="#d32f2f" strokeWidth="1.2"/></svg>
                                </button>
                              </>
                            )}
                            {userRole !== 'user' && (
                            <button
                              className="icon-btn add-event-btn"
                              title="Добавить мероприятие"
                              onClick={() => handleAddEvent(objIdx)}
                            >
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="7.5" stroke="#2e7d32" strokeWidth="1.6"/>
                                <path d="M10 6.5V13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                                <path d="M6.5 10H13.5" stroke="#2e7d32" strokeWidth="1.6" strokeLinecap="round"/>
                              </svg>
                            </button>
                            )}
                          </div>
                        </div>
                                                    </td>
                    ) : null}
                    {months.map((month, monthIdx) => {
                      const targetMonthDate = new Date(yearStartDate.getFullYear(), monthIdx, 1);
                      const cellEvents = uniqueEvents.filter(e => monthIdx >= e.startIdx && monthIdx <= e.endIdx);
                      const year = yearStartDate.getFullYear();
                      if (cellEvents.length > 0) {
                        return (
                          <td
                            key={monthIdx}
                            data-date={targetMonthDate.toISOString()}
                            data-obj-idx={objIdx}
                            className={`calendar-cell year-view-month-cell ${dragOverCell && dragOverCell.objIdx === objIdx && dragOverCell.date.getTime() === targetMonthDate.getTime() ? 'drag-over' : ''}`}
                            onDragOver={userRole !== 'user' && draggedEvent ? (e) => handleDragOver(e, targetMonthDate, objIdx) : undefined}
                            onDragLeave={userRole !== 'user' ? handleDragLeave : undefined}
                            onDrop={userRole !== 'user' && draggedEvent ? (e) => handleDrop(e, targetMonthDate, objIdx) : undefined}
                          >
                            <div className="year-view-month-track" style={{ position: 'relative', minHeight: 28, height: 28 }}>
                              {cellEvents.map((cellEvent) => {
                                const seg = getEventSegmentInMonth(
                                  new Date(cellEvent.event.startDate),
                                  new Date(cellEvent.event.endDate),
                                  year,
                                  monthIdx
                                );
                                if (!seg || seg.widthPct <= 0) return null;
                                const isDraggingThis = isDragging && draggedEvent && draggedEvent.event.id === cellEvent.event.id;
                                return (
                                  <div
                                    key={cellEvent.event.id}
                                    className={`calendar-event ${getEventStatusClasses(cellEvent.event)} ${isDraggingThis ? 'dragging' : ''} ${dragArmedEventId === cellEvent.event.id ? 'drag-armed' : ''} ${cellEvent.event.objectIds && cellEvent.event.objectIds[0] != null && (Number(cellEvent.event.objectIds[0]) === obj.id) ? 'event-main' : ''}`}
                                    style={{
                                      position: 'absolute',
                                      left: `${seg.leftPct}%`,
                                      width: `calc(${seg.widthPct}% - 2px)`,
                                      minWidth: `calc(${seg.widthPct}% - 2px)`,
                                      top: 2,
                                      height: 24,
                                      background: getEventColor(cellEvent.event.id, cellEvent.event.name, cellEvent.event.status),
                                      boxSizing: 'border-box',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: seg.widthPct < 10 ? '0 2px' : '0 6px',
                                    }}
                                    draggable={userRole !== 'user' && dragArmedEventId === cellEvent.event.id}
                                    onMouseDown={userRole !== 'user' ? (e) => { if (e.button !== 0) return; armDrag(cellEvent.event.id); } : undefined}
                                    onMouseUp={userRole !== 'user' ? disarmDrag : undefined}
                                    onMouseLeave={() => { if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current); setEventTooltip(null); }}
                                    onMouseEnter={(e) => {
                                      e.stopPropagation();
                                      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      tooltipTimeoutRef.current = setTimeout(() => {
                                        setEventTooltip({ event: cellEvent.event, x: rect.left + rect.width / 2, y: rect.top, objectName: data[objIdx]?.name || '' });
                                      }, 200);
                                    }}
                                    onDragStart={userRole !== 'user' ? (e) => handleDragStart(e, cellEvent.event, objIdx) : undefined}
                                    onDragEnd={userRole !== 'user' ? handleDragEnd : undefined}
                                    onTouchStart={userRole !== 'user' ? (e) => handleTouchStart(e, cellEvent.event, objIdx) : undefined}
                                    onTouchMove={userRole !== 'user' ? handleTouchMove : undefined}
                                    onTouchEnd={userRole !== 'user' ? handleTouchEnd : undefined}
                                    onDoubleClick={userRole !== 'user' ? (e) => { e.preventDefault(); e.stopPropagation(); handleEditEvent(objIdx, cellEvent.event); } : undefined}
                                  >
                                    <div className="event-title" style={{ flex: 1, paddingRight: cellEvent.event.cloudUrl ? '24px' : 0 }}>{cellEvent.event.name}</div>
                                    {cellEvent.event.cloudUrl && (
                                      <a href={cellEvent.event.cloudUrl} target="_blank" rel="noopener noreferrer" className="event-docs-icon" title="Открыть документы в облаке" onClick={(e) => e.stopPropagation()}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      }
                      const isDragOver = dragOverCell && dragOverCell.objIdx === objIdx && dragOverCell.date.getTime() === targetMonthDate.getTime();
                      return (
                        <td
                          key={monthIdx}
                          data-date={targetMonthDate.toISOString()}
                          data-obj-idx={objIdx}
                          className={`calendar-cell year-view-month-cell ${isDragOver ? 'drag-over' : ''}`}
                          onDragOver={userRole !== 'user' ? (e) => handleDragOver(e, targetMonthDate, objIdx) : undefined}
                          onDragLeave={userRole !== 'user' ? handleDragLeave : undefined}
                          onDrop={userRole !== 'user' ? (e) => handleDrop(e, targetMonthDate, objIdx) : undefined}
                          onClick={e => { e.stopPropagation(); setSelectedCell({ objIdx, trackIdx, dayIdx: monthIdx, mode: 'year' }); }}
                        >
                          <div className="year-view-month-track" style={{ position: 'relative', minHeight: 28, height: 28 }}>
                            {selectedCell && selectedCell.objIdx === objIdx && selectedCell.trackIdx === trackIdx && selectedCell.dayIdx === monthIdx && selectedCell.mode === 'year' && (
                              <button className="add-btn small show" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} onClick={e => { e.stopPropagation(); handleAddEvent(objIdx, targetMonthDate); setSelectedCell(null); }}>+</button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
                });
          })}
        </tbody>
      </table>
        </>
      )}

      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        onSave={handleSaveEvent}
        onDelete={userRole !== 'user' && selectedEvent && currentObjectIdxForEvent !== null ? () => handleDeleteEvent(currentObjectIdxForEvent, selectedEvent.id) : null}
        event={selectedEvent}
        mode={eventModalMode}
        defaultDate={defaultEventDate}
        objects={data.map(o => ({ id: o.id, name: o.name }))}
        defaultObjectId={currentObjectIdxForEvent !== null ? data[currentObjectIdxForEvent]?.id : null}
        canEditObjects={userRole === 'superuser' || (userRole === 'admin' && userOrgId && userOrgId === Number(selectedEvent?.organizationId))}
        canEditStatus={canEditStatus}
        userRole={userRole}
        userOrganizationId={userProfile?.organizationId}
      />
      
      {/* Tooltip для события */}
      {eventTooltip && (
        <div 
          className="event-tooltip"
          style={{
            position: 'fixed',
            left: `${eventTooltip.x}px`,
            top: `${eventTooltip.y - 10}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}
          onMouseEnter={(e) => {
            e.stopPropagation();
            // Продлеваем показ tooltip при наведении на него
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setEventTooltip(null);
          }}
        >
          <div className="event-tooltip-content">
            <header className="event-tooltip-header">
              <h3 className="event-tooltip-title">{eventTooltip.event.name}</h3>
              {eventTooltip.objectName && (
                <p className="event-tooltip-object">{eventTooltip.objectName}</p>
              )}
              {(eventTooltip.event.organizationName || eventTooltip.event.sportTypeName) && (
                <div className="event-tooltip-meta">
                  {eventTooltip.event.organizationName && (
                    <span className="event-tooltip-meta-item">
                      <span className="event-tooltip-meta-label">Организатор</span>
                      <span className="event-tooltip-meta-value">{eventTooltip.event.organizationName}</span>
                    </span>
                  )}
                  {eventTooltip.event.sportTypeName && (
                    <span className="event-tooltip-meta-item">
                      <span className="event-tooltip-meta-label">Вид спорта</span>
                      <span className="event-tooltip-meta-value">{eventTooltip.event.sportTypeName}</span>
                    </span>
                  )}
                </div>
              )}
            </header>
            <div className="event-tooltip-body">
              <dl className="event-tooltip-times">
                <div className="event-tooltip-time">
                  <dt className="event-tooltip-label">Начало</dt>
                  <dd className="event-tooltip-value">{new Date(eventTooltip.event.startDate).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</dd>
                </div>
                <div className="event-tooltip-time">
                  <dt className="event-tooltip-label">Окончание</dt>
                  <dd className="event-tooltip-value">{new Date(eventTooltip.event.endDate).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</dd>
                </div>
              </dl>
              {eventTooltip.event.description && (
                <div className="event-tooltip-description">
                  <span className="event-tooltip-label">Описание</span>
                  <p className="event-tooltip-desc-text">{eventTooltip.event.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsScheduleTable;
